import type {
  InfographicContent,
  InfographicChartType,
  InfographicDataPoint,
} from '@scrolly/post-types'
import { getColorString } from '../utils/color/color'
import { getWorldFeatures, getAdmin1Features } from './worldGeo'

// (count-up value labels supported via content.countUp)

// ── Colour presets ─────────────────────────────────────────
// Applied to the bar/segment colours. Categorical presets set the palette and
// clear per-row colours; gradient presets interpolate across the actual number
// of data points so they look right for any count. `colors` drives the dropdown
// preview (2 = a left→right gradient, more = discrete swatches).
export const INFOGRAPHIC_BRAND_PALETTE = [
  '#96C81E',
  '#FFC800',
  '#FF9600',
  '#F02873',
  '#1996D0',
]
export const INFOGRAPHIC_COLOR_PRESETS: {
  key: string
  label: string
  colors: string[]
}[] = [
  { key: 'brand', label: 'scrolly', colors: INFOGRAPHIC_BRAND_PALETTE },
  { key: 'redGreen', label: 'Red → Green', colors: ['#E53935', '#43A047'] },
  { key: 'greenRed', label: 'Green → Red', colors: ['#43A047', '#E53935'] },
  { key: 'heat', label: 'Heat (Yellow → Red)', colors: ['#FFD54F', '#E53935'] },
  { key: 'ocean', label: 'Ocean (Blue → Teal)', colors: ['#1996D0', '#1DE9B6'] },
  { key: 'purplePink', label: 'Purple → Pink', colors: ['#7C4DFF', '#F02873'] },
  { key: 'grayscale', label: 'Grayscale', colors: ['#5A5A5A', '#DDDDDD'] },
]

// CSS background for a preset preview swatch.
export function presetGradientCss(colors: string[]): string {
  if (colors.length <= 1) return colors[0] ?? '#888'
  if (colors.length === 2)
    return `linear-gradient(to right, ${colors[0]}, ${colors[1]})`
  // discrete categorical swatches (e.g. brand)
  const n = colors.length
  const stops = colors
    .map((c, i) => `${c} ${((i / n) * 100).toFixed(1)}% ${(((i + 1) / n) * 100).toFixed(1)}%`)
    .join(', ')
  return `linear-gradient(to right, ${stops})`
}

// One-click "look" for line charts (the curated styling, applied on top of the
// chart's existing data/title/size). Does not touch data, title, value/unit.
export const INFOGRAPHIC_LINE_PRESET: Partial<InfographicContent> = {
  chartType: 'line',
  textColor: '#ffffff',
  backgroundColor: { r: 59, g: 51, b: 60, a: 0.81 },
  bgBlur: false,
  bgShadow: false,
  bgPaddingX: 100,
  bgPaddingY: 210,
  bgRadius: 60,
  gridlines: true,
  showValues: false,
  showLabels: true,
  gridColor: { r: 255, g: 255, b: 255, a: 0.52 },
  gridWidth: 5.5,
  axisFontSize: 71,
  titleFontSize: 127,
  valueFontSize: 118,
  labelFontSize: 96,
  fontFamily: 'BarlowSemiCondensed',
  lineColor: '#e83777',
  lineWidth: 20,
  pointSize: 21,
  showPoints: true,
  showGradient: true,
  showArea: false,
  lineReveal: 'draw',
}

// One-click "look" for bar charts (curated styling). Keeps the data + title.
export const INFOGRAPHIC_BAR_PRESET: Partial<InfographicContent> = {
  chartType: 'bar',
  textColor: '#ffffff',
  backgroundColor: { r: 59, g: 51, b: 60, a: 0.81 },
  bgBlur: false,
  bgShadow: false,
  bgPaddingX: 100,
  bgPaddingY: 210,
  bgRadius: 60,
  showLabels: true,
  showValues: true,
  gridlines: false,
  horizontal: false,
  countUp: true,
  barWidthPct: 70,
  barRadius: 20,
  gridColor: { r: 255, g: 255, b: 255, a: 0.52 },
  gridWidth: 5.5,
  axisFontSize: 69,
  titleFontSize: 127,
  valueFontSize: 118,
  labelFontSize: 96,
  fontFamily: 'BarlowSemiCondensed',
}

// One-click "look" for donut / pie charts (curated styling). Keeps data + title +
// centre value/unit.
export const INFOGRAPHIC_PIE_PRESET: Partial<InfographicContent> = {
  chartType: 'pie',
  textColor: '#ffffff',
  backgroundColor: { r: 59, g: 51, b: 60, a: 0.81 },
  bgBlur: false,
  bgShadow: false,
  bgPaddingX: 205,
  bgPaddingY: 65,
  bgRadius: 0,
  pieSpan: 'full',
  ringThickness: 63,
  segmentStroke: 7,
  titleFontSize: 122,
  valueFontSize: 84,
  labelFontSize: 73,
  fontFamily: 'BarlowSemiCondensed',
}

function hexTriple(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return m
    ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
    : [0, 0, 0]
}

function lerpHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexTriple(a)
  const [br, bg, bb] = hexTriple(b)
  const ch = (x: number, y: number) =>
    Math.round(x + (y - x) * t)
      .toString(16)
      .padStart(2, '0')
  return `#${ch(ar, br)}${ch(ag, bg)}${ch(ab, bb)}`
}

const clearColors = (data: InfographicDataPoint[]): InfographicDataPoint[] =>
  data.map(({ color, ...rest }) => rest)

// Interpolate a from→to gradient across the data rows (per-row colours).
export function applyCustomGradient(
  from: string,
  to: string,
  data: InfographicDataPoint[]
): Partial<InfographicContent> {
  const n = Math.max(data.length, 1)
  return {
    data: data.map((d, i) => ({
      ...d,
      color: lerpHex(from, to, n > 1 ? i / (n - 1) : 0),
    })),
  }
}

// Returns the content patch to apply for a preset (palette and/or per-row colours).
export function applyColorPreset(
  key: string,
  data: InfographicDataPoint[]
): Partial<InfographicContent> {
  if (key === 'brand')
    return { palette: INFOGRAPHIC_BRAND_PALETTE, data: clearColors(data) }
  const preset = INFOGRAPHIC_COLOR_PRESETS.find((p) => p.key === key)
  if (!preset || preset.colors.length < 2) return {}
  return applyCustomGradient(
    preset.colors[0],
    preset.colors[preset.colors.length - 1],
    data
  )
}

// per-type default text sizes (px). title = heading, value = the numbers
// (bar values / donut total / KPI value), label = categories / sublabels / unit.
// Shared with the panel so the sliders show the right default when unset.
export const INFOGRAPHIC_FONT_DEFAULTS: Record<
  InfographicChartType,
  { title: number; value: number; label: number }
> = {
  bar: { title: 36, value: 24, label: 22 },
  line: { title: 36, value: 24, label: 20 },
  pie: { title: 36, value: 52, label: 20 },
  kpi: { title: 64, value: 150, label: 34 },
  // timeline: value = the date/heading size, label = the description size
  timeline: { title: 36, value: 28, label: 20 },
  // circles: value/label are the scale baseline; actual size scales per circle
  circles: { title: 36, value: 30, label: 26 },
  // map: only the title/source matter (no per-feature numbers in v1)
  map: { title: 36, value: 24, label: 22 },
  mapEurope: { title: 36, value: 24, label: 22 },
  mapCountry: { title: 36, value: 24, label: 22 },
  // wordcloud: only title/source — word sizes follow the frame automatically
  wordcloud: { title: 36, value: 24, label: 22 },
  // table: value = Zellen, label = Kopfzeile
  table: { title: 36, value: 52, label: 30 },
  // Aufzählung: value = große Texte, label = Subtext
  steps: { title: 36, value: 64, label: 30 },
}

// Recommended, good-looking font sizes per chart type for the standard
// 1700×1000 content. Applied when the chart type is switched, so each type
// starts with sensible sizes instead of carrying over the previous type's.
export const INFOGRAPHIC_TYPE_FONT_DEFAULTS: Record<
  InfographicChartType,
  { title: number; value: number; label: number; axisFontSize?: number }
> = {
  bar: { title: 127, value: 118, label: 96, axisFontSize: 69 },
  line: { title: 127, value: 118, label: 96, axisFontSize: 69 },
  pie: { title: 122, value: 84, label: 73 },
  kpi: { title: 96, value: 240, label: 64 },
  timeline: { title: 110, value: 44, label: 36 },
  circles: { title: 128, value: 66, label: 29 },
  map: { title: 110, value: 60, label: 48 },
  mapEurope: { title: 110, value: 60, label: 48 },
  mapCountry: { title: 110, value: 60, label: 48 },
  wordcloud: { title: 110, value: 60, label: 48 },
  table: { title: 96, value: 52, label: 30 },
  steps: { title: 96, value: 72, label: 34 },
}

/** Deckkraft des Titels beim Aufbau: blendet über die ersten 20 % ein (Jan 18.10.2026). */
export function titelDeckkraft(progress: number): number {
  const a = Math.min(Math.max(progress / 0.2, 0), 1)
  return 1 - Math.pow(1 - a, 3)
}

/** Deckkraft der Quelle beim Aufbau: blendet über die letzten 20 % ein (Jan 18.10.2026). */
export function quelleDeckkraft(progress: number): number {
  const a = Math.min(Math.max((progress - 0.8) / 0.2, 0), 1)
  return 1 - Math.pow(1 - a, 3)
}

/**
 * Deckkraft von Beschriftungen und Werten beim Aufbau (Jan 19.10.2026:
 * „Typografie mit aufbauen — die ist am Anfang immer schon da“): blendet
 * zwischen `start` und `start + dauer` ein; bei fertigem Aufbau genau 1.
 */
export function textAufbauDeckkraft(progress: number, start = 0, dauer = 0.4): number {
  if (progress >= 1) return 1
  const a = Math.min(Math.max((progress - start) / Math.max(dauer, 0.0001), 0), 1)
  return 1 - Math.pow(1 - a, 3)
}

// opacity-Attribut nur, solange der Text noch einblendet (fertig = Ausgabe unverändert)
function deckAttr(o: number): string {
  return o >= 0.999 ? '' : ` opacity="${o.toFixed(2)}"`
}

/** Schriftgröße der Quelle, wenn keine eingetragen ist (Jan 18.10.2026; vorher 22 % der Titelgröße, mind. 18). */
export const QUELLE_SCHRIFT_STANDARD = 60

function fonts(content: InfographicContent) {
  const d = INFOGRAPHIC_FONT_DEFAULTS[content.chartType]
  return {
    title: content.titleFontSize ?? d.title,
    value: content.valueFontSize ?? d.value,
    label: content.labelFontSize ?? d.label,
  }
}

// Builds the SVG markup for an infographic layer. The string is injected as
// innerHTML into a CSS3D element (see RendererWebGL.updateHTMLInfographic), so
// it must be a fully self-contained <svg> — no external fonts or CSS classes.
//
// `progress` (0..1) drives the reveal animation: bars grow up, the line rises,
// the donut sweeps around, and the KPI value counts up. progress = 1 = full.

function esc(s: string | number): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// background fill as a CSS colour string (transparent when a = 0)
function bgFill(content: InfographicContent): string {
  return getColorString(content.backgroundColor) ?? 'rgba(20,20,28,0.92)'
}

// Total pixel size of the generated SVG = content area + background padding on
// each side. The CSS3D element is sized to this (RendererWebGL), so growing the
// padding enlarges the card on the stage without touching the chart content.
export function infographicPixelSize(content: InfographicContent): {
  width: number
  height: number
} {
  const px = Math.max(content.bgPaddingX ?? 0, 0)
  const py = Math.max(content.bgPaddingY ?? 0, 0)
  let cw = content.width
  let ch = content.height
  // a timeline stretches along its axis with the length factor (so the SVG /
  // CSS3D element grows and stations don't overlap)
  if (content.chartType === 'timeline') {
    const f = timelineLengthFactor(content)
    if (content.timelineOrientation === 'vertical') ch *= f
    else cw *= f
  }
  return { width: cw + px * 2, height: ch + py * 2 }
}

// Wraps the chart body in the <svg> + rounded card background. The card spans
// the full padded size; the chart content keeps its own size and is shifted in
// by the padding so it stays put while the card grows around it.
/**
 * Abstand des Inhalts vom Kartenrand je Diagrammtyp — dort stehen Titel und
 * Quelle, wenn sie links- oder rechtsbündig sind (Jan 18.10.2026).
 */
function titelRand(content: InfographicContent): number {
  switch (content.chartType) {
    case 'bar':
      return content.horizontal ? 40 : 56
    case 'line':
      return 56
    case 'map':
    case 'mapEurope':
    case 'mapCountry':
      return 24
    default:
      return 40
  }
}

/**
 * KOPF der Grafik (Jan 19.10.2026): Überzeile / Titel / Unterzeile, je
 * optional. Liefert die Zeilen mit ihrer Grundlinie (ab Oberkante des Kopfs)
 * und die Höhe des Blocks — bei nur einem Titel genau die Titelgröße, so
 * bleiben bestehende Grafiken pixelgleich. KPI: nur der Titel (zeichnet ihn
 * selbst an der Zahl).
 */
type KopfZeile = { art: 'kicker' | 'title' | 'subtitle'; text: string; fs: number; y: number }
function kopf(content: InfographicContent): { zeilen: KopfZeile[]; hoehe: number } {
  const titleFs = fonts(content).title
  const mitExtras = content.chartType !== 'kpi'
  const kicker = mitExtras ? (content.kicker ?? '').trim() : ''
  const sub = mitExtras ? (content.subtitle ?? '').trim() : ''
  const ks = content.kickerFontSize ?? Math.round(titleFs * 0.42)
  const ss = content.subtitleFontSize ?? Math.round(titleFs * 0.5)
  const zeilen: KopfZeile[] = []
  let y = 0
  const dazu = (art: KopfZeile['art'], text: string, fs: number, abstand: number) => {
    if (zeilen.length) y += abstand
    y += fs
    zeilen.push({ art, text, fs, y })
  }
  if (kicker) dazu('kicker', kicker, ks, 0)
  if (content.title) dazu('title', content.title, titleFs, ks * 0.7)
  if (sub) dazu('subtitle', sub, ss, ss * 0.55)
  return { zeilen, hoehe: y }
}

/** Mehrhöhe des Kopfs gegenüber „nur Titel“ (bzw. nichts) — für Typen mit
 *  festem oberen Rand, die den Titel bisher nicht einrechneten. */
function kopfExtra(content: InfographicContent): number {
  return Math.max(0, kopf(content).hoehe - (content.title ? fonts(content).title : 0))
}

/**
 * `kante` (Jan 18.10.2026): wo der INHALT tatsächlich steht (x im Inhalts-
 * bereich). Karten, Kreise, Wortwolke und Torte sind schmaler als ihre Fläche
 * und stehen mittig — links-/rechtsbündige Titel und Quellen sollen an IHNEN
 * stehen, nicht am Flächenrand (der beim Handy außerhalb des Bilds lag).
 */
function wrap(content: InfographicContent, inner: string, kante?: { links: number; rechts: number }): string {
  const ff = fontStack(content)
  const px = Math.max(content.bgPaddingX ?? 0, 0)
  const py = Math.max(content.bgPaddingY ?? 0, 0)
  const { width: W, height: H } = infographicPixelSize(content)
  const radius = content.bgRadius ?? 20
  // headline pinned to the card's top edge, horizontally centred — drawn OUTSIDE
  // the content group, so it stays put when the background padding grows.
  // kpi draws title AND source itself, right around the number (Jan,
  // 12.10.2026: pinned to the card edges they sat far away from it) — see
  // kpiSvg; an empty title simply draws nothing.
  const titleFs = fonts(content).title
  const insetY = Math.max(20, radius * 0.6)
  // Titel und Quelle (Jan 18.10.2026): links/mittig/rechts — bündig mit dem
  // Inhalt (Rand je Diagrammtyp, s. titelRand) — Titel mit eigener Schrift
  const titelBund = content.chartType === 'kpi' ? 'center' : content.titleAlign ?? 'center'
  const titelX =
    titelBund === 'left'
      ? px + (kante ? kante.links : titelRand(content))
      : titelBund === 'right'
        ? kante
          ? px + kante.rechts
          : W - px - titelRand(content)
        : W / 2
  const titelAnker = titelBund === 'left' ? 'start' : titelBund === 'right' ? 'end' : 'middle'
  const titelSchrift = infografikTitelSchrift(content)
  // Überzeile / Titel / Unterzeile (Jan 19.10.2026) — alle drei blenden
  // wie der Titel beim Aufbau ein (data-titel)
  const titleEl =
    content.chartType === 'kpi'
      ? ''
      : kopf(content)
          .zeilen.map((z) =>
            z.art === 'title'
              ? `<text x="${titelX}" y="${insetY + z.y}" fill="${content.titleColor || content.textColor}" ` +
                `font-size="${titleFs}" font-weight="${titelSchrift.weight}" ` +
                `font-family="${wortwolkenSchriftStapel(titelSchrift.family)}" ` +
                `text-anchor="${titelAnker}" data-titel="1">${esc(z.text)}</text>`
              : z.art === 'kicker'
                ? `<text x="${titelX}" y="${insetY + z.y}" fill="${content.kickerColor || content.accentColor || content.textColor}" ` +
                  `font-size="${z.fs}" font-weight="${staerke(content, 600)}" letter-spacing="${(z.fs * 0.12).toFixed(1)}" ` +
                  `font-family="${ff}" text-anchor="${titelAnker}" data-titel="1">${esc(z.text.toUpperCase())}</text>`
                : `<text x="${titelX}" y="${insetY + z.y}" fill="${content.subtitleColor || content.labelColor || content.textColor}" ` +
                  (content.subtitleColor ? '' : `fill-opacity="0.75" `) +
                  `font-size="${z.fs}" font-family="${ff}" text-anchor="${titelAnker}" data-titel="1">${esc(z.text)}</text>`,
          )
          .join('')
  // source / attribution line pinned bottom-centre at the card's bottom edge —
  // except for kpi, which draws it right under the number + unit (see kpiSvg):
  // with the kpi card's large padding the bottom edge is far from the number.
  const sourceFs = content.sourceFontSize ?? QUELLE_SCHRIFT_STANDARD
  // eigene Quellen-Farbe (Jan 22.10.2026: „Farbauswahl für Labels und
  // Quelle getrennt“); ohne sie wie bisher die Label-Farbe
  const sourceFill = content.sourceColor || content.labelColor || content.textColor
  const sourceEl = content.source && content.chartType !== 'kpi'
    ? `<text x="${titelX}" y="${H - insetY}" fill="${sourceFill}" ` +
      `fill-opacity="${content.sourceColor ? 1 : content.labelColor ? 0.85 : 0.6}" ` +
      `font-size="${sourceFs}" font-family="${ff}" text-anchor="${titelAnker}" data-quelle="1">` +
      `${esc(content.source)}</text>`
    : ''
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" ` +
    `width="100%" height="100%" preserveAspectRatio="xMidYMid meet"` +
    // gewählter Schnitt für alle Texte ohne eigene Stärke (s. staerke unten);
    // ohne Wahl bleibt das SVG Zeichen für Zeichen wie bisher
    (grundStaerke(content) ? ` font-weight="${grundStaerke(content)}"` : '') +
    `>` +
    `<rect x="0" y="0" width="${W}" height="${H}" rx="${radius}" ` +
    `fill="${bgFill(content)}"/>` +
    titleEl +
    `<g transform="translate(${px}, ${py})">${inner}</g>` +
    sourceEl +
    `</svg>`
  )
}

/**
 * Schnitt der Infografik (Jans Regel 07.10.2026, „Grundtext, Fettes bleibt“):
 * Der gewählte Schnitt gilt für alle Texte ohne eigene Stärke (Beschriftungen,
 * Achsen, Quelle) — sie erben ihn vom <svg> (s. wrap). Überschrift und Werte
 * behalten ihre Stärke (600/700), werden aber nie dünner als der Grundtext.
 * Dieselbe Regel wie packages/post-types/src/schrift-staerke.ts, hier ohne
 * Import: Jans Kopie dieser Datei (pro-editor/) kann das Paket nicht laden.
 */
function grundStaerke(content: InfographicContent): number | undefined {
  const w = content.fontWeight
  if (typeof w !== 'number' || !Number.isFinite(w)) return undefined
  const gerundet = Math.round(w / 100) * 100
  return gerundet >= 100 && gerundet <= 900 ? gerundet : undefined
}

function staerke(content: InfographicContent, bisher: number): number {
  return Math.max(bisher, grundStaerke(content) ?? 0)
}

/**
 * Stärken, die eine Infografik mit gewähltem Schnitt zeichnet — für das
 * Einbetten der Schnitt-Dateien im 3D-Modus (infographicMesh). Ohne Wahl
 * `undefined`: dann bleibt es bei der einen Datei wie bisher.
 */
export function infografikSchnitte(content: InfographicContent): number[] | undefined {
  const grund = grundStaerke(content)
  return grund === undefined ? undefined : [grund, staerke(content, 600), staerke(content, 700)]
}

// CSS font stack for all chart text (the chosen family + a sans-serif fallback)
function fontStack(content: InfographicContent): string {
  return content.fontFamily ? `'${content.fontFamily}', sans-serif` : 'sans-serif'
}

// fill + opacity attributes for category/axis labels: a dedicated labelColour at
// full strength when set, otherwise the dimmed textColor (the previous default).
/** Farbe der WERTE (Zahlen, Datum, Zellen, große Texte; Jan 19.10.2026) —
 *  leer = „Alle Texte“ (textColor) wie bisher. */
function wertFarbe(content: InfographicContent): string {
  return content.valueColor || content.textColor
}

function labelPaint(content: InfographicContent, dim = 0.6): string {
  const fill = content.labelColor || content.textColor
  return `fill="${fill}" fill-opacity="${content.labelColor ? 1 : dim}"`
}

// thousands grouping character for the chosen option ('' = no grouping)
function thousandsChar(content: InfographicContent): string {
  switch (content.thousandsSep) {
    case 'dot':
      return '.'
    case 'comma':
      return ','
    case 'space':
      return ' ' // non-breaking space, renders reliably in the SVG
    default:
      return ''
  }
}

// value label incl. optional prefix/suffix + thousands separator (e.g. "€1.340")
function fmtVal(content: InfographicContent, v: number | string): string {
  let s = String(v)
  const sep = thousandsChar(content)
  if (sep) {
    // group the integer part only, keep sign + any decimal/trailing part
    const m = s.match(/^(-?)(\d+)(.*)$/)
    if (m)
      s = m[1] + m[2].replace(/\B(?=(\d{3})+(?!\d))/g, sep) + m[3]
  }
  return `${content.valuePrefix ?? ''}${s}${content.valueSuffix ?? ''}`
}

// bar value label: counts up with progress when enabled, and shows nothing
// (not "0") when the value is zero — also keeps count-up clean at the start.
function barValueLabel(
  content: InfographicContent,
  d: InfographicDataPoint,
  progress: number
): string {
  const v = content.countUp ? Math.round(d.value * progress) : d.value
  return v === 0 ? '' : fmtVal(content, v)
}

// gridline + axis-tick styling: colour (incl. alpha), stroke width, label size
function gridStyle(content: InfographicContent): {
  stroke: string
  width: number
  axisFs: number
} {
  return {
    // one colour drives both the gridlines and the axis/scale labels
    stroke:
      getColorString(content.gridColor ?? { r: 255, g: 255, b: 255, a: 0.5 }) ??
      'rgba(255,255,255,0.5)',
    width: content.gridWidth ?? 1,
    axisFs: content.axisFontSize ?? 14,
  }
}


// RUNDE ACHSEN-SCHRITTE (Jans Fund 12.10.2026, "Gridlinien in 50er- oder
// 100er-Schritten"): Vorher lag die oberste Linie exakt auf dem Datenmaximum
// und die Zwischenlinien auf dessen Vierteln - krumme Werte wie 93/70/47/23.
// Jetzt: Schrittweite 1/2/2,5/5 x 10^k (ca. 4 Linien), Achsenmaximum =
// naechster Schritt UEBER dem Maximum. `gridStep` ueberschreibt die Automatik
// (Pro-Editor "Grid step", 0 = automatisch), gedeckelt auf 20 Linien; ein
// gesetztes `axisMax` bleibt das harte Achsenende.
function niceAxis(
  content: InfographicContent,
  dataMax: number
): { achseMax: number; ticks: number[] } {
  const fest = content.axisMax && content.axisMax > 0 ? content.axisMax : 0
  const spitze = Math.max(fest || dataMax, 1)
  const automatik = () => {
    const roh = spitze / 4
    const zehner = Math.pow(10, Math.floor(Math.log10(roh)))
    const f = roh / zehner
    return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * zehner
  }
  // festes axisMax ohne manuellen Schritt: wie bisher Viertel des gewaehlten
  // Maximums (der Nutzer hat das Achsenende bewusst gesetzt, die oberste
  // Linie soll es weiterhin treffen)
  let step =
    content.gridStep && content.gridStep > 0
      ? content.gridStep
      : fest
        ? fest / 4
        : automatik()
  if (spitze / step > 20) step = automatik()
  const achseMax = fest || Math.ceil(spitze / step - 1e-9) * step
  const ticks: number[] = []
  for (let v = step; v <= achseMax + 1e-9; v += step)
    ticks.push(Math.round(v * 1000) / 1000)
  return { achseMax, ticks }
}

function barSvg(content: InfographicContent, progress: number): string {
  return content.horizontal
    ? barSvgHorizontal(content, progress)
    : barSvgVertical(content, progress)
}

function barSvgVertical(content: InfographicContent, progress: number): string {
  const { width: w, height: h, data, palette, textColor } = content
  const { value: valueFs, label: labelFs } = fonts(content)
  const ff = fontStack(content)
  // values and the grid are mutually exclusive (a grid replaces the per-bar
  // numbers); labels are independent/optional
  const showValues = content.showValues !== false && !content.gridlines
  const showLabels = content.showLabels !== false
  const padX = 56
  // Platz für den Wert über dem HÖCHSTEN Balken (Jans Fund 15.10.2026,
  // „bei dem Chart verschwinden die Zahlen"): fest 90 schnitt große
  // Werte-Schriften oben ab. Wächst jetzt mit der Schrift — bis 75 wie bisher.
  const padTopAlt = showValues ? Math.max(90, Math.ceil(valueFs * 0.8) + 30) : 90
  // Mit Überzeile/Unterzeile (Jan 19.10.2026) Platz für den ganzen Kopf plus
  // die Werte über dem höchsten Balken — ohne bleibt alles wie bisher.
  const padTop = kopfExtra(content)
    ? Math.max(padTopAlt, kopf(content).hoehe + 36 + (showValues ? Math.ceil(valueFs * 0.8) + 20 : 0))
    : padTopAlt
  const padBottom = showLabels ? labelFs + 48 : 40
  const chartH = h - padTop - padBottom
  const chartBottom = h - padBottom
  const max =
    content.axisMax && content.axisMax > 0
      ? content.axisMax
      : Math.max(...data.map((d) => d.value), 1)
  const { achseMax, ticks } = niceAxis(content, max)
  const skalenMax = content.gridlines ? achseMax : max
  const slot = (w - padX * 2) / Math.max(data.length, 1)
  const barW = slot * Math.min(Math.max((content.barWidthPct ?? 56) / 100, 0.02), 1)
  const barRadius = content.barRadius ?? 6

  let grid = ''
  if (content.gridlines) {
    const { stroke, width: gw, axisFs } = gridStyle(content)
    const over = 15 // gridlines run a few px past the chart edges
    for (const tick of [0, ...ticks]) {
      const gy = chartBottom - (chartH * tick) / skalenMax
      grid +=
        `<line x1="${padX - over}" y1="${gy.toFixed(1)}" x2="${(w - padX + over).toFixed(1)}" y2="${gy.toFixed(1)}" ` +
        `stroke="${stroke}" stroke-width="${gw}"/>` +
        `<text${deckAttr(textAufbauDeckkraft(progress))} x="${padX - over - 8}" y="${(gy + axisFs * 0.35).toFixed(1)}" fill="${stroke}" ` +
        `font-size="${axisFs}" text-anchor="end" font-family="${ff}">` +
        `${esc(fmtVal(content, tick))}</text>`
    }
  }

  let bars = ''
  data.forEach((d, i) => {
    // clamp to the chart area so an axisMax below the data doesn't overflow
    const bh = chartH * Math.min(Math.max(d.value, 0) / skalenMax, 1) * progress
    const x = padX + slot * i + (slot - barW) / 2
    const y = chartBottom - bh
    const col = d.color ?? palette[i % palette.length]
    const cx = (x + barW / 2).toFixed(1)
    bars += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${barW.toFixed(
      1
    )}" height="${bh.toFixed(1)}" rx="${barRadius}" fill="${col}"/>`
    if (showValues)
      bars +=
        `<text${deckAttr(textAufbauDeckkraft(progress, 0.15))} x="${cx}" y="${(y - 22).toFixed(1)}" fill="${wertFarbe(content)}" ` +
        `font-size="${valueFs}" font-weight="${staerke(content, 600)}" text-anchor="middle" ` +
        `font-family="${ff}">${esc(
          barValueLabel(content, d, progress)
        )}</text>`
    if (showLabels)
      bars +=
        `<text${deckAttr(textAufbauDeckkraft(progress))} x="${cx}" y="${(chartBottom + labelFs + 8).toFixed(1)}" ${labelPaint(content)} ` +
        `font-size="${labelFs}" text-anchor="middle" ` +
        `font-family="${ff}">${esc(d.label)}</text>`
  })

  return wrap(
    content,
    grid +
      bars +
      // baseline only with the grid — off the grid means a clean, line-free chart
      (content.gridlines
        ? `<line x1="${padX}" y1="${chartBottom}" x2="${w - padX}" y2="${chartBottom}" ` +
          `stroke="${textColor}" stroke-opacity="0.15" stroke-width="2"/>`
        : '')
  )
}

function barSvgHorizontal(content: InfographicContent, progress: number): string {
  const { width: w, height: h, data, palette, textColor } = content
  const { title: titleFs, value: valueFs, label: labelFs } = fonts(content)
  // values and the grid are mutually exclusive; labels are independent/optional
  const showValues = content.showValues !== false && !content.gridlines
  const showLabels = content.showLabels !== false
  const ff = fontStack(content)
  const barRadius = content.barRadius ?? 6
  const axisFs = content.axisFontSize ?? 14
  // Mit Titel etwas mehr Luft (Jan 18.10.2026: „der Titel ist zu nah am
  // Bar Chart“) — der Titel steht fest am Kartenrand, die Balken rücken ab.
  const kopfH = kopf(content).hoehe
  const padTop = kopfH ? kopfH + 56 : titleFs + 40
  const padBottom = content.gridlines ? axisFs + 34 : 24
  // size the label gutter (left) and value gutter (right) to the longest text
  // so long labels don't overflow the card and short ones don't waste space.
  // widths are estimated from character count (no DOM measuring in an SVG string).
  const labelW = Math.max(
    0,
    ...data.map((d) => String(d.label).length * labelFs * 0.58)
  )
  const valueW = Math.max(
    0,
    ...data.map((d) => fmtVal(content, d.value).length * valueFs * 0.62)
  )
  // Beschriftung ÜBER dem Balken (Jans Standard 13.10.2026) — dann gibt es
  // keine linke Label-Spalte; barLabelLinks holt die alte Anordnung zurück.
  const labelOben = showLabels && !content.barLabelLinks
  const padLeft =
    showLabels && !labelOben ? Math.min(Math.max(labelW + 32, 64), w * 0.5) : 40
  const padRight = showValues ? Math.min(Math.max(valueW + 26, 56), w * 0.4) : 40
  const chartTop = padTop
  const chartH = h - padTop - padBottom
  const chartLeft = padLeft
  const chartW = w - padLeft - padRight
  const max =
    content.axisMax && content.axisMax > 0
      ? content.axisMax
      : Math.max(...data.map((d) => d.value), 1)
  const { achseMax, ticks } = niceAxis(content, max)
  const skalenMax = content.gridlines ? achseMax : max
  const slot = chartH / Math.max(data.length, 1)
  // Label oben: jede Zeile = Beschriftung + Balken. Die Beschriftung gehört
  // sichtbar zu IHREM Balken (Jan 14.10.2026): Abstand Label → eigener Balken
  // 0,35 × Schrift, Abstand zum Balken darüber mindestens 0,6 × Schrift.
  // Die Schriftgröße gilt wie eingestellt — der Balken bekommt den Rest und
  // wird bei großer Schrift dünner. Erst wenn Beschriftung + Mindestabstand
  // mehr als 80 % der Zeile bräuchten, wird die Schrift kleiner — sonst
  // bliebe vom Balken nur ein Strich.
  const labelFsOben = labelOben ? Math.min(labelFs, (slot * 0.8) / 1.67) : labelFs
  const labelAbstand = labelFsOben * 0.35
  // Versalhöhe (~0,72) + Abstand zum eigenen Balken
  const labelBlock = labelOben ? labelFsOben * 0.72 + labelAbstand : 0
  const abstandOben = labelOben ? labelFsOben * 0.6 : 0
  const barH = Math.min(
    (slot - labelBlock) *
      Math.min(Math.max((content.barWidthPct ?? 56) / 100, 0.02), 1),
    slot - labelBlock - abstandOben
  )

  let grid = ''
  if (content.gridlines) {
    const { stroke, width: gw } = gridStyle(content)
    for (const tick of [0, ...ticks]) {
      const gx = chartLeft + (chartW * tick) / skalenMax
      grid +=
        `<line x1="${gx.toFixed(1)}" y1="${chartTop}" x2="${gx.toFixed(1)}" ` +
        `y2="${chartTop + chartH}" stroke="${stroke}" stroke-width="${gw}"/>` +
        `<text${deckAttr(textAufbauDeckkraft(progress))} x="${gx.toFixed(1)}" y="${chartTop + chartH + axisFs + 6}" fill="${stroke}" ` +
        `font-size="${axisFs}" text-anchor="middle" font-family="${ff}">` +
        `${esc(fmtVal(content, tick))}</text>`
    }
  }

  let bars = ''
  data.forEach((d, i) => {
    // clamp to the chart area so an axisMax below the data doesn't overflow
    const bl = chartW * Math.min(Math.max(d.value, 0) / skalenMax, 1) * progress
    const gruppe = labelBlock + barH
    const y = chartTop + slot * i + (slot - gruppe) / 2 + labelBlock
    const cy = y + barH / 2
    const col = d.color ?? palette[i % palette.length]
    bars += `<rect x="${chartLeft}" y="${y.toFixed(1)}" width="${bl.toFixed(
      1
    )}" height="${barH.toFixed(1)}" rx="${barRadius}" fill="${col}"/>`
    if (showValues)
      bars +=
        // Grundlinie statt dominant-baseline="central": central mittelt die
        // ganze Zeile inkl. Unterlängen, Ziffern hingen dadurch zu tief.
        `<text${deckAttr(textAufbauDeckkraft(progress, 0.15))} x="${(chartLeft + bl + 18).toFixed(1)}" y="${(cy + valueFs * 0.36).toFixed(1)}" fill="${wertFarbe(content)}" ` +
        `font-size="${valueFs}" font-weight="${staerke(content, 600)}" text-anchor="start" ` +
        `font-family="${ff}">${esc(
          barValueLabel(content, d, progress)
        )}</text>`
    if (showLabels && labelOben)
      bars +=
        `<text${deckAttr(textAufbauDeckkraft(progress))} x="${chartLeft}" y="${(y - labelAbstand).toFixed(1)}" ${labelPaint(content)} ` +
        `font-size="${labelFsOben.toFixed(1)}" text-anchor="start" font-family="${ff}">${esc(d.label)}</text>`
    else if (showLabels)
      bars +=
        `<text${deckAttr(textAufbauDeckkraft(progress))} x="${(chartLeft - 20).toFixed(1)}" y="${(cy + labelFs * 0.36).toFixed(1)}" ${labelPaint(content)} ` +
        `font-size="${labelFs}" text-anchor="end" ` +
        `font-family="${ff}">${esc(d.label)}</text>`
  })

  return wrap(
    content,
    grid +
      bars +
      // axis line only with the grid — off the grid means a clean, line-free chart
      (content.gridlines
        ? `<line x1="${chartLeft}" y1="${chartTop}" x2="${chartLeft}" y2="${chartTop + chartH}" ` +
          `stroke="${textColor}" stroke-opacity="0.15" stroke-width="2"/>`
        : '')
  )
}

function lineSvg(content: InfographicContent, progress: number): string {
  const { width: w, height: h, data, textColor, accentColor } = content
  const { value: valueFs, label: labelFs } = fonts(content)
  const ff = fontStack(content)
  // line styling (fall back to accent colour / sensible defaults)
  const lineColor = content.lineColor || accentColor
  const lineWidth = content.lineWidth ?? 4
  const showPoints = content.showPoints !== false
  const pointSize = content.pointSize ?? 6
  // grid and per-point value labels are mutually exclusive (both show numbers)
  const showValues = content.showValues !== false && !content.gridlines
  const padX = 56
  // reserve headroom for the topmost value label so it can't hit the headline
  const padTopAlt = 90 + (showValues ? valueFs + 8 : 0)
  // Mit Überzeile/Unterzeile Platz für den ganzen Kopf (Jan 19.10.2026)
  const padTop = kopfExtra(content)
    ? Math.max(padTopAlt, kopf(content).hoehe + 36 + (showValues ? valueFs + 8 : 0))
    : padTopAlt
  // small x-axis ticks + a gap before the category labels (both grid states)
  const tickLen = Math.max(10, labelFs * 0.35)
  const labelGap = 16
  const padBottom = tickLen + labelGap + labelFs + 24
  const chartH = h - padTop - padBottom
  const chartBottom = h - padBottom
  const vals = data.map((d) => d.value)
  const max = Math.max(...vals, 1)
  const min = Math.min(...vals, 0)
  // bei aktivem Grid endet die Achse am runden achseMax statt am Datenmaximum
  // (sonst laege die oberste Linie auf einem krummen Wert, s. niceAxis)
  const { achseMax, ticks } = niceAxis(content, max)
  const spitze = content.gridlines ? achseMax : max
  const range = spitze - min || 1
  const stepX = (w - padX * 2) / Math.max(data.length - 1, 1)
  const reveal = content.lineReveal === 'draw' ? 'draw' : 'rise'
  // final (full) position of every data point
  const fullPts = data.map((d, i) => [
    padX + stepX * i,
    chartBottom - chartH * ((d.value - min) / range),
  ])

  // linePts = the polyline actually drawn at this progress; reached[i] = whether
  // data point i is revealed (its dot + value shown)
  let linePts: number[][]
  let reached: boolean[]
  if (reveal === 'draw') {
    // draw the line left→right with a short lead-in: the reveal starts slightly
    // left of the first point so the very beginning is clean/empty and the first
    // point only appears once the line actually reaches it
    const leadIn = stepX * 0.5
    const startX = padX - leadIn
    const revealX = startX + progress * (w - padX - startX)
    linePts = []
    reached = data.map(() => false)
    if (revealX > padX) {
      for (let i = 0; i < fullPts.length; i++) {
        if (fullPts[i][0] <= revealX) {
          linePts.push(fullPts[i])
          reached[i] = true
        } else {
          // interpolated tip on the segment currently being drawn
          const [x0, y0] = fullPts[i - 1] ?? fullPts[i]
          const [x1, y1] = fullPts[i]
          const t = (revealX - x0) / (x1 - x0 || 1)
          linePts.push([revealX, y0 + (y1 - y0) * t])
          break
        }
      }
    }
  } else {
    // rise from the baseline: all points lift together with progress
    linePts = fullPts.map(([x, y]) => [x, chartBottom - (chartBottom - y) * progress])
    reached = data.map(() => true)
  }

  // the upper grid + axis numbers follow the grid colour; the baseline (null
  // line) + category ticks belong to the labels, so they follow the label colour
  const { stroke: gridStroke, width: gridW, axisFs } = gridStyle(content)
  const axisStroke = content.labelColor || textColor
  const axisStrokeOp = content.labelColor ? 1 : 0.6
  const over = 15 // gridlines + baseline run a few px past the chart edges
  let grid = ''
  if (content.gridlines) {
    // upper horizontal value grid + axis numbers (the baseline is drawn below)
    for (const tickVal of ticks) {
      const gy = chartBottom - (chartH * (tickVal - min)) / range
      grid +=
        `<line x1="${padX - over}" y1="${gy.toFixed(1)}" x2="${(w - padX + over).toFixed(1)}" y2="${gy.toFixed(1)}" ` +
        `stroke="${gridStroke}" stroke-width="${gridW}"/>` +
        // suppress a "0" reading
        (tickVal === 0
          ? ''
          : `<text${deckAttr(textAufbauDeckkraft(progress))} x="${padX - over - 8}" y="${(gy + axisFs * 0.35).toFixed(1)}" fill="${gridStroke}" ` +
            `font-size="${axisFs}" text-anchor="end" font-family="${ff}">` +
            `${esc(fmtVal(content, tickVal))}</text>`)
    }
  }
  // baseline across the bottom (always) — label colour
  grid +=
    `<line x1="${padX - over}" y1="${chartBottom.toFixed(1)}" x2="${(w - padX + over).toFixed(1)}" y2="${chartBottom.toFixed(1)}" ` +
    `stroke="${axisStroke}" stroke-opacity="${axisStrokeOp}" stroke-width="${gridW}"/>`
  // small vertical ticks dropping to each category label (always, fixed axis)
  fullPts.forEach((p) => {
    grid +=
      `<line x1="${p[0].toFixed(1)}" y1="${chartBottom.toFixed(1)}" ` +
      `x2="${p[0].toFixed(1)}" y2="${(chartBottom + tickLen).toFixed(1)}" ` +
      `stroke="${axisStroke}" stroke-opacity="${axisStrokeOp}" stroke-width="${gridW}"/>`
  })

  const line = linePts.length
    ? linePts
        .map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
        .join(' ')
    : ''
  const area = linePts.length
    ? `M${linePts[0][0].toFixed(1)} ${chartBottom} ` +
      linePts.map((p) => `L${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ') +
      ` L${linePts[linePts.length - 1][0].toFixed(1)} ${chartBottom} Z`
    : ''

  // dots + value labels sit at the final point position; in 'draw' mode they
  // only appear once the line has reached them (reached[i])
  const dots = data
    .map((d, i) => {
      const p = reveal === 'draw' ? fullPts[i] : linePts[i]
      const visible = reached[i]
      return (
        (showValues && visible
          ? `<text${deckAttr(textAufbauDeckkraft(progress, 0.15))} x="${p[0].toFixed(1)}" y="${(
              p[1] -
              pointSize -
              10
            ).toFixed(1)}" fill="${wertFarbe(content)}" font-size="${valueFs}" ` +
            `font-weight="${staerke(content, 600)}" text-anchor="middle" font-family="${ff}">` +
            `${esc(fmtVal(content, d.value))}</text>`
          : '') +
        (showPoints && visible
          ? `<circle cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" r="${pointSize}" ` +
            `fill="${textColor}" stroke="${lineColor}" stroke-width="${Math.max(
              2,
              pointSize * 0.5
            ).toFixed(1)}"/>`
          : '') +
        // category (x-axis) label: fixed position, blendet beim Aufbau ein
        `<text${deckAttr(textAufbauDeckkraft(progress))} x="${fullPts[i][0].toFixed(1)}" y="${(
          chartBottom +
          tickLen +
          labelGap +
          labelFs * 0.75
        ).toFixed(1)}" ${labelPaint(content)} ` +
        `font-size="${labelFs}" text-anchor="middle" font-family="${ff}">` +
        `${esc(d.label)}</text>`
      )
    })
    .join('')

  return wrap(
    content,
    `<defs><linearGradient id="lgArea" x1="0" y1="0" x2="0" y2="1">` +
      `<stop offset="0" stop-color="${lineColor}" stop-opacity="0.35"/>` +
      `<stop offset="1" stop-color="${lineColor}" stop-opacity="0.02"/>` +
      `</linearGradient></defs>` +
      grid +
      // solid area takes precedence over the gradient (mutually exclusive in
      // the UI); neither = just the line
      (content.showArea
        ? `<path d="${area}" fill="${lineColor}" fill-opacity="0.3"/>`
        : content.showGradient !== false
        ? `<path d="${area}" fill="url(#lgArea)"/>`
        : '') +
      `<path d="${line}" fill="none" stroke="${lineColor}" stroke-width="${lineWidth}" ` +
      `stroke-linejoin="round" stroke-linecap="round"/>` +
      dots
  )
}

// annular sector (donut segment) path; falls back to a wedge when ri≈0 (pie).
// cornerRadius (px) rounds the corners (d3.arc-style): all four for a donut,
// the two outer ones for a pie wedge. It is clamped to half the ring thickness
// and to the segment's angular span, so thin/small segments round less. The
// fillet geometry (tangent points + asin offsets, all sweeps = 1) was verified
// by point-sampling — see the rounded-segments slider.
function sectorPath(
  cx: number,
  cy: number,
  R: number,
  ri: number,
  a0: number,
  a1: number,
  cornerRadius = 0
): string {
  const wedge = ri <= 0.5
  const halfSpan = (a1 - a0) / 2
  const sinH = Math.sin(Math.min(halfSpan, Math.PI / 2))
  let cr = Math.max(0, cornerRadius)
  if (cr > 0) {
    const maxThick = wedge ? R * 0.5 : (R - ri) / 2
    const maxAngO = sinH < 0.999 ? (R * sinH) / (1 + sinH) : Infinity
    const maxAngI = wedge
      ? Infinity
      : sinH < 0.999
      ? (ri * sinH) / (1 - sinH)
      : Infinity
    cr = Math.min(cr, maxThick, maxAngO, maxAngI)
  }

  // sharp (original) path when there's effectively no rounding
  if (cr <= 0.5) {
    const large = a1 - a0 > Math.PI ? 1 : 0
    const xo0 = (cx + R * Math.cos(a0)).toFixed(2)
    const yo0 = (cy + R * Math.sin(a0)).toFixed(2)
    const xo1 = (cx + R * Math.cos(a1)).toFixed(2)
    const yo1 = (cy + R * Math.sin(a1)).toFixed(2)
    if (wedge) {
      return `M${cx.toFixed(2)} ${cy.toFixed(2)} L${xo0} ${yo0} A${R} ${R} 0 ${large} 1 ${xo1} ${yo1} Z`
    }
    const xi1 = (cx + ri * Math.cos(a1)).toFixed(2)
    const yi1 = (cy + ri * Math.sin(a1)).toFixed(2)
    const xi0 = (cx + ri * Math.cos(a0)).toFixed(2)
    const yi0 = (cy + ri * Math.sin(a0)).toFixed(2)
    return (
      `M${xo0} ${yo0} A${R} ${R} 0 ${large} 1 ${xo1} ${yo1} ` +
      `L${xi1} ${yi1} A${ri} ${ri} 0 ${large} 0 ${xi0} ${yi0} Z`
    )
  }

  const P = (rad: number, ang: number) =>
    `${(cx + rad * Math.cos(ang)).toFixed(2)} ${(cy + rad * Math.sin(ang)).toFixed(2)}`
  const c = cr.toFixed(2)
  const phio = Math.asin(Math.min(0.9999, cr / (R - cr)))
  const lO = a1 - phio - (a0 + phio) > Math.PI ? 1 : 0
  const roE = (R - cr) * Math.cos(phio) // radius of the radial-edge tangent point (outer)

  if (wedge) {
    return (
      `M${cx.toFixed(2)} ${cy.toFixed(2)} L${P(roE, a0)} ` +
      `A${c} ${c} 0 0 1 ${P(R, a0 + phio)} A${R} ${R} 0 ${lO} 1 ${P(R, a1 - phio)} ` +
      `A${c} ${c} 0 0 1 ${P(roE, a1)} Z`
    )
  }

  const phii = Math.asin(Math.min(0.9999, cr / (ri + cr)))
  const lI = a1 - phii - (a0 + phii) > Math.PI ? 1 : 0
  const riE = (ri + cr) * Math.cos(phii) // radial-edge tangent point (inner)
  return (
    `M${P(roE, a0)} A${c} ${c} 0 0 1 ${P(R, a0 + phio)} ` +
    `A${R} ${R} 0 ${lO} 1 ${P(R, a1 - phio)} A${c} ${c} 0 0 1 ${P(roE, a1)} ` +
    `L${P(riE, a1)} A${c} ${c} 0 0 1 ${P(ri, a1 - phii)} ` +
    `A${ri} ${ri} 0 ${lI} 0 ${P(ri, a0 + phii)} A${c} ${c} 0 0 1 ${P(riE, a0)} Z`
  )
}

function pieSvg(content: InfographicContent, progress: number): string {
  const { width: w, height: h, data, palette } = content
  const { value: valueFs, label: labelFs } = fonts(content)
  const ff = fontStack(content)
  const total = data.reduce((s, d) => s + Math.max(d.value, 0), 0) || 1
  const semi = content.pieSpan === 'semi'
  // more than 5 parts → always a legend (per-segment labels get too crowded);
  // up to 5 parts the legend is optional (off by default)
  // legend is forced on for >8 parts (on-segment labels get too crowded); up to
  // 8 parts it's optional (content.showLegend, off by default)
  const legend = data.length > 8 ? true : !!content.showLegend
  const strokeW = content.segmentStroke ?? 0
  const ringPct = Math.min(Math.max(content.ringThickness ?? 42, 0), 100)
  const colOf = (d: InfographicDataPoint, i: number) =>
    d.color ?? palette[i % palette.length]
  // NAMEN an Torte und Legende (Jan 20.10.2026: „die Namen an der Torte bitte
  // auch schwarz“): eigene Labels-Farbe bzw. „Alle Texte“ (pieNameColor)
  // gewinnen, sonst Farbe des Stücks wie bisher.
  const nameFarbe = (d: InfographicDataPoint, i: number) =>
    content.labelColor || content.pieNameColor || colOf(d, i)

  // ── legend column width (left of the chart); drawn further below once the
  //    ring centre is known so it can be vertically centred to the ring ─────
  const topPad = 78 + kopfExtra(content)
  const legendLh = labelFs * 1.6
  const legendSw = labelFs * 0.8
  let legendW = 0
  if (legend) {
    const maxLen = Math.max(
      1,
      ...data.map((d) => `${d.label} · ${fmtVal(content, d.value)}`.length)
    )
    legendW = Math.min(
      44 + legendSw + 14 + maxLen * labelFs * 0.5 + 30,
      w * 0.52
    )
  }

  // ── ring geometry ──────────────────────────────────────────────────────
  // legend mode: chart occupies the area to the right of the legend column.
  // otherwise category labels sit OUTSIDE the ring, so reserve room for them.
  const maxLabelChars = Math.max(1, ...data.map((d) => String(d.label).length))
  const estLabelW = maxLabelChars * labelFs * 0.5
  const sideMargin = legend ? 50 : Math.min(70 + estLabelW, w * 0.3)
  const vReserve = legend ? 50 : labelFs * 1.5 + 36
  const topExtra = legend ? 0 : vReserve
  const bottomMargin = vReserve
  const chartLeft = legend ? legendW : 0
  const chartW = w - chartLeft
  const cx = chartLeft + chartW / 2
  const areaTop = topPad + topExtra
  let cy: number
  let r: number
  if (semi) {
    // half ring, flat side anchored to the bottom of the card (parliament
    // style) — leaves the whole height above it free for the headline + legend
    cy = h - bottomMargin
    r = Math.max(80, Math.min(chartW / 2 - sideMargin, cy - areaTop))
  } else {
    r = Math.max(
      80,
      Math.min(chartW / 2 - sideMargin, (h - bottomMargin - areaTop) / 2)
    )
    cy = areaTop + r
  }

  // legend rows (never above the headline):
  //  • full circle → vertically centred to the ring centre
  //  • half circle → bottom-aligned to the flat edge of the half ring (cy)
  let legendSvg = ''
  if (legend) {
    const lastBaseline = (data.length - 1) * legendLh
    const startY = Math.max(
      topPad + labelFs,
      semi ? cy - lastBaseline : cy - lastBaseline / 2
    )
    legendSvg = data
      .map((d, i) => {
        const y = startY + i * legendLh
        return (
          // Kästchen blenden mit ihrem Namen ein (Jan 20.10.2026: standen am
          // Anfang des Fortschritts schon da)
          `<rect${deckAttr(textAufbauDeckkraft(progress))} x="44" y="${(y - legendSw * 0.85).toFixed(1)}" width="${legendSw}" height="${legendSw}" rx="${(
            legendSw * 0.25
          ).toFixed(1)}" fill="${colOf(d, i)}"/>` +
          `<text${deckAttr(textAufbauDeckkraft(progress))} x="${(44 + legendSw + 14).toFixed(1)}" y="${y.toFixed(1)}" ` +
          `font-size="${labelFs}" font-family="${ff}">` +
          `<tspan fill="${nameFarbe(d, i)}">${esc(d.label)}</tspan>` +
          `<tspan fill="${wertFarbe(content)}"> · ${esc(fmtVal(content, d.value))}</tspan>` +
          `</text>`
        )
      })
      .join('')
  }
  const ri = r * (1 - ringPct / 100)
  // corner rounding: a % of half the ring thickness (0 = sharp, 100 = pill
  // ends). Per-segment angular clamping happens inside sectorPath.
  const segRadiusPct = Math.min(Math.max(content.segmentRadius ?? 0, 0), 100)
  const cornerR = (segRadiusPct / 100) * ((r - ri) / 2)
  // "Segment contour" now renders as a real angular gap (the slider px mapped to
  // an angle at the outer radius, where the gap is most visible), so the spacing
  // shows whatever is behind the chart and stays visible on a transparent
  // background — the old background-coloured stroke vanished when the bg faded.
  const gapAngle = strokeW > 0 && r > 0 ? strokeW / r : 0
  const startAng = semi ? Math.PI : -Math.PI / 2
  const spanAng = semi ? Math.PI : Math.PI * 2

  // ── segments (wind up with progress) ───────────────────────────
  let ang = startAng
  const segs = data
    .map((d, i) => {
      const frac = (Math.max(d.value, 0) / total) * progress
      const a0 = ang
      const a1 = ang + frac * spanAng
      ang = a1
      if (frac <= 0) return ''
      const col = colOf(d, i)
      // a single 100% slice is a full ring/circle (no gap). Use an even-odd
      // annular path for the donut so the hole is a real hole (transparent-safe)
      // instead of overlaying a background-coloured circle.
      if (!semi && frac >= 0.999) {
        if (ri > 0.5) {
          const ro = r.toFixed(2)
          const rin = ri.toFixed(2)
          return (
            `<path fill-rule="evenodd" fill="${col}" d="` +
            `M${(cx - r).toFixed(2)} ${cy} A${ro} ${ro} 0 1 0 ${(cx + r).toFixed(2)} ${cy} A${ro} ${ro} 0 1 0 ${(cx - r).toFixed(2)} ${cy} Z ` +
            `M${(cx - ri).toFixed(2)} ${cy} A${rin} ${rin} 0 1 0 ${(cx + ri).toFixed(2)} ${cy} A${rin} ${rin} 0 1 0 ${(cx - ri).toFixed(2)} ${cy} Z"/>`
          )
        }
        return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${col}"/>`
      }
      // real angular gap between segments — shows through on a transparent
      // background; capped so thin slices stay visible
      const half = Math.min(gapAngle / 2, (a1 - a0) * 0.4)
      return `<path d="${sectorPath(cx, cy, r, ri, a0 + half, a1 - half, cornerR)}" fill="${col}"/>`
    })
    .join('')

  // ── per-segment category labels outside the ring (NOT in legend mode);
  //    the value itself is drawn centred inside the segment below ───────
  let segLabels = ''
  if (!legend) {
    let la = startAng
    let lcum = 0
    segLabels = data
      .map((d, i) => {
        const frac = Math.max(d.value, 0) / total
        const mid = la + (frac * spanAng) / 2
        la += frac * spanAng
        // Name blendet ein, während der Aufbau über sein Stück läuft
        const deck = textAufbauDeckkraft(progress, lcum, Math.max(frac, 0.12))
        lcum += frac
        if (frac <= 0 || deck <= 0) return ''
        // gap from the ring; top/bottom labels need extra room because the text
        // height reaches back toward the ring (its centre is on the radius)
        const lr = r + 42 + (labelFs / 2) * Math.abs(Math.sin(mid))
        const lx = cx + lr * Math.cos(mid)
        const ly = cy + lr * Math.sin(mid)
        const anchor =
          Math.abs(Math.cos(mid)) < 0.3
            ? 'middle'
            : Math.cos(mid) > 0
            ? 'start'
            : 'end'
        return (
          `<text${deckAttr(deck)} x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" fill="${nameFarbe(d, i)}" ` +
          `font-size="${labelFs}" text-anchor="${anchor}" ` +
          `dominant-baseline="central" font-family="${ff}">` +
          `${esc(d.label)}</text>`
        )
      })
      .join('')
  }

  // ── value (white) centred inside each segment, at its mid radius/angle.
  //    Appears as the wind-up sweep passes the segment's middle; skipped when
  //    the segment is too small/thin to fit the number. ─────────────────
  const midR = (r + ri) / 2
  let cum = 0
  const innerValues =
    content.showValues === false
      ? ''
      : data.map((d) => {
      const frac = Math.max(d.value, 0) / total
      const mid = startAng + (cum + frac / 2) * spanAng
      // Wert blendet ein, sobald der Aufbau die Mitte seines Stücks erreicht
      const deck = textAufbauDeckkraft(progress, cum + frac / 2, 0.12)
      cum += frac
      if (frac <= 0 || deck <= 0) return ''
      const fitsRadial = r - ri >= labelFs * 0.9
      const fitsArc = frac * spanAng * midR >= labelFs * 1.2
      if (!fitsRadial || !fitsArc) return ''
      const vx = cx + midR * Math.cos(mid)
      const vy = cy + midR * Math.sin(mid)
      return (
        `<text${deckAttr(deck)} x="${vx.toFixed(1)}" y="${vy.toFixed(1)}" fill="${wertFarbe(content)}" ` +
        `font-size="${labelFs}" font-weight="${staerke(content, 600)}" text-anchor="middle" ` +
        `dominant-baseline="central" font-family="${ff}">` +
        `${esc(fmtVal(content, d.value))}</text>`
      )
    })
    .join('')

  // ── centre: a single label/value, wrapped to two lines if it's long
  //    (only meaningful when there's a hole) ──────────────────────────
  let centre = ''
  if (ri > r * 0.3) {
    const raw = content.value
      ? scaleKpiValue(content.value, progress)
      : fmtVal(content, Math.round(total * progress))
    // break into (at most) two lines at the space nearest the middle
    let lines = [raw]
    if (raw.length > 12 && raw.includes(' ')) {
      const spaces: number[] = []
      for (let k = 0; k < raw.length; k++) if (raw[k] === ' ') spaces.push(k)
      const mid = raw.length / 2
      let best = spaces[0]
      for (const idx of spaces)
        if (Math.abs(idx - mid) < Math.abs(best - mid)) best = idx
      lines = [raw.slice(0, best), raw.slice(best + 1)]
    }
    const lineH = valueFs * 1.02
    const n = lines.length
    // full ring → vertically centred on cy (dominant-baseline central);
    // half ring → last line sits just above the flat edge with a small gap
    const semiGap = valueFs * 0.35
    const y0 = semi
      ? cy - semiGap - (n - 1) * lineH
      : cy - ((n - 1) * lineH) / 2
    const baselineAttr = semi ? '' : ' dominant-baseline="central"'
    centre =
      `<text${deckAttr(textAufbauDeckkraft(progress, 0.2, 0.5))} x="${cx}" y="${y0.toFixed(1)}" fill="${wertFarbe(content)}" ` +
      `font-size="${valueFs}" font-weight="${staerke(content, 700)}" text-anchor="middle"${baselineAttr} ` +
      `font-family="${ff}">` +
      lines
        .map(
          (ln, k) =>
            `<tspan x="${cx}"${k ? ` dy="${lineH.toFixed(1)}"` : ''}>${esc(
              ln
            )}</tspan>`
        )
        .join('') +
      `</text>`
  }

  return wrap(content, legendSvg + segs + segLabels + innerValues + centre, {
    links: legend ? 44 : cx - r,
    rechts: cx + r,
  })
}

// scale the numeric part of a KPI value string by progress, preserving any
// prefix/suffix and the original decimal places ("87%" → "61%" at 0.7)
function scaleKpiValue(value: string, progress: number): string {
  const raw = String(value ?? '')
  const m = raw.match(/-?\d+(\.\d+)?/)
  if (!m) return raw
  const decimals = m[0].includes('.') ? m[0].split('.')[1].length : 0
  const shown = (parseFloat(m[0]) * progress).toFixed(decimals)
  return raw.replace(m[0], shown)
}

// greedy word-wrap into at most maxLines lines of ~maxChars; appends "…" if the
// text didn't fit. Used for timeline station descriptions (SVG has no auto-wrap).
function wrapText(s: string, maxChars: number, maxLines: number): string[] {
  const words = (s || '').trim().split(/\s+/).filter(Boolean)
  if (!words.length) return []
  const lines: string[] = []
  let cur = ''
  let i = 0
  for (; i < words.length; i += 1) {
    const test = cur ? `${cur} ${words[i]}` : words[i]
    if (test.length > maxChars && cur) {
      lines.push(cur)
      cur = words[i]
      if (lines.length >= maxLines) {
        cur = ''
        break
      }
    } else {
      cur = test
    }
  }
  if (cur && lines.length < maxLines) {
    lines.push(cur)
    i = words.length
  }
  if (i < words.length && lines.length) {
    lines[lines.length - 1] = `${lines[lines.length - 1]}…`
  }
  return lines
}

// Parse a timeline station's label into a sortable numeric time + a display
// string. Accepts a plain year ("2025") or DD.MM.YYYY (also with / or -), where
// 00 for day/month means "unknown" → shown coarser: 00.00.2025 → "2025",
// 00.03.2025 → "03.2025", 15.03.2025 → "15.03.2025". Unparseable → raw label.
function parseTimelineDate(label: string): { value: number; display: string } {
  const s = (label || '').trim()
  const pad = (n: number) => String(n).padStart(2, '0')
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/)
  if (m) {
    const d = parseInt(m[1], 10)
    const mo = parseInt(m[2], 10)
    const y = parseInt(m[3], 10)
    const value =
      y +
      (mo > 0 ? (mo - 1) / 12 : 0) +
      (d > 0 && mo > 0 ? (d - 1) / 372 : 0)
    const display =
      mo === 0 ? `${y}` : d === 0 ? `${pad(mo)}.${y}` : `${pad(d)}.${pad(mo)}.${y}`
    return { value, display }
  }
  const y = s.match(/^(\d{4})$/)
  if (y) return { value: parseInt(y[1], 10), display: s }
  return { value: NaN, display: s }
}

// Simple Timeline: a line of stations (label = date/heading, text = description)
// that build up step-by-step with `progress`. Horizontal (left→right) or
// vertical (top→bottom); evenly spaced or — with timelineProportional — spaced
// by the parsed date so real gaps show (e.g. 2001→2003 twice 2005→2006).
// length factor for the timeline axis (10–400 % → 0.1–4). Stretches/shrinks the
// SVG along the axis so stations spread out (more room → no overlapping text).
function timelineLengthFactor(content: InfographicContent): number {
  return Math.min(Math.max((content.timelineAxisLength ?? 100) / 100, 0.1), 4)
}

function timelineSvg(content: InfographicContent, progress: number): string {
  const { data, textColor, accentColor } = content
  const vertical = content.timelineOrientation === 'vertical'
  // the axis dimension grows with the length factor; cross dimension stays put
  const f = timelineLengthFactor(content)
  const w = vertical ? content.width : content.width * f
  const h = vertical ? content.height * f : content.height
  const ff = fontStack(content)
  const { value: dateFs, label: descFs } = fonts(content)
  const lineColor = content.lineColor || accentColor || '#6c8cff'
  const palette = content.palette ?? []
  const dotColor = (i: number) =>
    data[i].color || palette[i % (palette.length || 1)] || lineColor
  const baseStroke = content.labelColor || textColor
  const n = data.length
  const dotR = content.pointSize ?? 12
  const lineW = content.lineWidth ?? 5

  // date display + (optional) proportional positions along the axis
  const parsed = data.map((d) => parseTimelineDate(d.label))
  const values = parsed.map((p) => p.value)
  const vmin = Math.min(...values)
  const vmax = Math.max(...values)
  const proportional =
    !!content.timelineProportional &&
    n > 1 &&
    values.every((v) => Number.isFinite(v)) &&
    vmax > vmin
  const frac = (i: number) =>
    proportional
      ? (values[i] - vmin) / (vmax - vmin)
      : i / Math.max(n - 1, 1)

  const station = (cx: number, cy: number, i: number) => {
    const c = dotColor(i)
    return (
      `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${dotR}" ` +
      `fill="${c}" stroke="${baseStroke}" stroke-opacity="0.25" stroke-width="2"/>`
    )
  }

  // axis spans the (already length-scaled) canvas, minus the end padding
  // Überzeile/Unterzeile schieben den Zeitstrahl nach unten (Jan 19.10.2026)
  const axisStart = 70 + (vertical ? kopfExtra(content) : 0)
  const axisEnd = (vertical ? h : w) - 70
  const axisLen = axisEnd - axisStart
  const swap = !!content.timelineSwapLabels
  // GOLDENER SCHNITT (Jans Wunsch 13.10.2026): vertikal sitzt die Achse auf
  // dem goldenen Schnitt statt mittig — die Beschreibung bekommt die breite
  // Seite (ohne Swap rechts, mit Swap links). Horizontal unverändert.
  const GOLD = 0.382
  const lineX = vertical ? w * (swap ? 1 - GOLD : GOLD) : w / 2
  const baseY = h * 0.5 + kopfExtra(content) / 2 // horizontal: y of the line
  const dotR2 = dotR
  const sideGap = dotR2 + 18 // vertical: gap from the dot to the side text
  // position-based reveal: the line grows along the axis, a station appears once
  // the line reaches it; small lead-in keeps the very start empty
  const leadIn = axisLen * 0.04
  const drawn = -leadIn + progress * (axisLen + leadIn)
  const lineEnd = axisStart + Math.min(Math.max(drawn, 0), axisLen)
  const avgStep = axisLen / Math.max(n - 1, 1)
  const maxCharsH = Math.max(8, Math.round((avgStep - 16) / (descFs * 0.52)))
  // vertical: the text runs from the dot to the card's inner edge — i.e. the
  // content half PLUS the background padding — so it only wraps once the
  // background (card) is actually made smaller, not at the content midline.
  const bgPadX = Math.max(content.bgPaddingX ?? 0, 0)
  const maxCharsV = Math.max(
    8,
    Math.round(((swap ? lineX : w - lineX) + bgPadX - sideGap - 24) / (descFs * 0.52))
  )

  let body = vertical
    ? `<line x1="${lineX}" y1="${axisStart}" x2="${lineX}" y2="${axisEnd}" ` +
      `stroke="${baseStroke}" stroke-opacity="0.15" stroke-width="${lineW}"/>` +
      `<line x1="${lineX}" y1="${axisStart}" x2="${lineX}" y2="${lineEnd.toFixed(1)}" ` +
      `stroke="${lineColor}" stroke-width="${lineW}" stroke-linecap="round"/>`
    : `<line x1="${axisStart}" y1="${baseY}" x2="${axisEnd}" y2="${baseY}" ` +
      `stroke="${baseStroke}" stroke-opacity="0.15" stroke-width="${lineW}"/>` +
      `<line x1="${axisStart}" y1="${baseY}" x2="${lineEnd.toFixed(1)}" y2="${baseY}" ` +
      `stroke="${lineColor}" stroke-width="${lineW}" stroke-linecap="round"/>`

  data.forEach((d, i) => {
    const pos = axisStart + frac(i) * axisLen
    if (pos - axisStart > drawn + 0.5) return // not revealed yet
    const dateFill = content.timelineColorLabels ? dotColor(i) : wertFarbe(content)
    const descPaint = content.timelineColorLabels
      ? `fill="${dotColor(i)}"`
      : labelPaint(content)
    const date = parsed[i].display

    const dateText = (x: number, y: number, anchor: string, mid = false) =>
      `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" fill="${dateFill}" ` +
      `font-size="${dateFs}" font-weight="${staerke(content, 700)}" font-family="${ff}" ` +
      `text-anchor="${anchor}"${mid ? ' dominant-baseline="central"' : ''}>${esc(date)}</text>`
    const descText = (
      x: number,
      y: number,
      anchor: string,
      ln: string,
      mid = false
    ) =>
      `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" ${descPaint} ` +
      `font-size="${descFs}" font-family="${ff}" text-anchor="${anchor}"` +
      `${mid ? ' dominant-baseline="central"' : ''}>${esc(ln)}</text>`

    if (vertical) {
      // golden-ratio line; date on the narrow side, description on the wide
      // one, both vertically centred on the dot (swap flips the sides)
      body += station(lineX, pos, i)
      const lines = wrapText(d.text || '', maxCharsV, 4)
      const dateLeft = !swap
      const dateX = dateLeft ? lineX - sideGap : lineX + sideGap
      const descX = dateLeft ? lineX + sideGap : lineX - sideGap
      const dateAnchor = dateLeft ? 'end' : 'start'
      const descAnchor = dateLeft ? 'start' : 'end'
      body += dateText(dateX, pos, dateAnchor, true)
      const k = lines.length
      const lh = descFs * 1.25
      lines.forEach((ln, li) => {
        const ly = pos - ((k - 1) / 2) * lh + li * lh
        body += descText(descX, ly, descAnchor, ln, true)
      })
    } else {
      body += station(pos, baseY, i)
      const lines = wrapText(d.text || '', maxCharsH, 3)
      const k = lines.length
      body += dateText(
        pos,
        swap ? baseY + dotR2 + 14 + dateFs : baseY - dotR2 - 14,
        'middle'
      )
      lines.forEach((ln, li) => {
        const ly = swap
          ? baseY - dotR2 - 14 - (k - 1 - li) * descFs * 1.25
          : baseY + dotR2 + 14 + descFs + li * descFs * 1.25
        body += descText(pos, ly, 'middle', ln)
      })
    }
  })
  return wrap(content, body)
}

// relative luminance of a hex colour (0 dark … 1 light) → pick black/white text
function hexLuminance(hex: string): number {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '')
  if (!m) return 0
  const [r, g, b] = [1, 2, 3].map((i) => parseInt(m[i], 16) / 255)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

// Greedy circle packing: place each circle (largest first) tangent to an already
// placed one, at the spot closest to the centre that doesn't overlap. Not d3's
// packSiblings, but produces a very similar tight cluster. Returns centres for
// the input order; radii are taken as given.
function packCircles(
  radii: number[]
): Array<{ x: number; y: number }> {
  const placed: Array<{ x: number; y: number; r: number }> = []
  radii.forEach((r, i) => {
    if (i === 0) {
      placed.push({ x: 0, y: 0, r })
      return
    }
    if (i === 1) {
      placed.push({ x: placed[0].r + r, y: 0, r })
      return
    }
    let best: { x: number; y: number } | null = null
    let bestD = Infinity
    const steps = 30
    for (const p of placed) {
      const dist = p.r + r
      for (let a = 0; a < steps; a += 1) {
        const ang = (a / steps) * Math.PI * 2
        const x = p.x + Math.cos(ang) * dist
        const y = p.y + Math.sin(ang) * dist
        let ok = true
        for (const q of placed) {
          if (Math.hypot(x - q.x, y - q.y) < q.r + r - 0.5) {
            ok = false
            break
          }
        }
        if (ok) {
          const d = Math.hypot(x, y)
          if (d < bestD) {
            bestD = d
            best = { x, y }
          }
        }
      }
    }
    placed.push({ x: best?.x ?? 0, y: best?.y ?? 0, r })
  })
  return placed.map((p) => ({ x: p.x, y: p.y }))
}

// Simple Circles: a bubble cluster — every data point is a circle whose AREA is
// proportional to its value, packed together, labelled with name + value.
function circlesSvg(content: InfographicContent, progress: number): string {
  const { width: w, height: h, data } = content
  const ff = fontStack(content)
  const { value: valueCap, label: labelCap } = fonts(content)
  // the value/label font-size sliders scale the in-bubble text relative to the
  // radius-based default (so they actually grow/shrink, not just cap it)
  const labelScale = labelCap / INFOGRAPHIC_FONT_DEFAULTS.circles.label
  const valueScale = valueCap / INFOGRAPHIC_FONT_DEFAULTS.circles.value
  const palette = content.palette ?? []
  const colorOf = (i: number) =>
    data[i].color || palette[i % (palette.length || 1)] || '#6c8cff'

  if (!data.length) return wrap(content, '')

  // area ∝ value → radius ∝ sqrt(value); pack largest first, then map back to
  // the original order so colours/labels stay with their data point
  const order = data
    .map((d, i) => ({ i, v: Math.max(d.value, 0) }))
    .sort((a, b) => b.v - a.v)
  const radii = order.map((o) => Math.sqrt(o.v) || 0.0001)
  const packed = packCircles(radii)
  // positions back in original index order
  const pos: Array<{ x: number; y: number; r: number }> = []
  order.forEach((o, k) => {
    pos[o.i] = { x: packed[k].x, y: packed[k].y, r: radii[k] }
  })

  // fit the packing into the content box (leave room for the title at the top)
  const padX = 40
  const padTop = kopf(content).hoehe ? kopf(content).hoehe + 56 : 40
  const padBottom = 40
  const minX = Math.min(...pos.map((p) => p.x - p.r))
  const maxX = Math.max(...pos.map((p) => p.x + p.r))
  const minY = Math.min(...pos.map((p) => p.y - p.r))
  const maxY = Math.max(...pos.map((p) => p.y + p.r))
  const packW = maxX - minX || 1
  const packH = maxY - minY || 1
  const scale = Math.min((w - 2 * padX) / packW, (h - padTop - padBottom) / packH)
  const offX = padX + ((w - 2 * padX) - packW * scale) / 2 - minX * scale
  const offY = padTop + ((h - padTop - padBottom) - packH * scale) / 2 - minY * scale
  const ease = progress * progress * (3 - 2 * progress) // smoothstep
  // shrink each drawn circle by gap/2 → a uniform px gap between neighbours
  // (they're packed tangent, so two shrunk circles leave `gap` between edges)
  const gap = Math.max(4, Math.min(w, h) * 0.006)

  let body = ''
  data.forEach((d, i) => {
    const p = pos[i]
    if (!p || p.r <= 0) return
    const cx = offX + p.x * scale
    const cy = offY + p.y * scale
    const R = Math.max(0, p.r * scale * ease - gap / 2)
    if (R < 0.5) return
    const fill = colorOf(i)
    body += `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${R.toFixed(1)}" fill="${fill}"/>`
    // text inside, only if the circle is big enough to read
    if (R < 22) return
    const ink = hexLuminance(fill) > 0.6 ? '#1a1a1a' : '#ffffff'
    const labelFs = Math.max(6, R * 0.32 * labelScale)
    const valFs = Math.max(6, R * 0.26 * valueScale)
    const maxChars = Math.max(4, Math.round((R * 1.7) / (labelFs * 0.55)))
    const nameLines = wrapText(d.label, maxChars, 2)
    const valStr = fmtVal(content, d.value)
    const lh = labelFs * 1.1
    // the value line must clear the label's descender plus its own cap height:
    // the old fixed advance (lh + 0.2*valFs) was sized by the LABEL, so a big
    // value slider with a small label overlapped (Jan, 12.10.2026). In the
    // default ratio the max() picks the old advance — existing charts are
    // rendered pixel-identically.
    const vorsprung = Math.max(lh + valFs * 0.2, labelFs * 0.3 + valFs * 0.85)
    const totalH = nameLines.length * lh + valFs * 1.2 + (vorsprung - lh - valFs * 0.2)
    let ty = cy - totalH / 2 + labelFs * 0.85
    nameLines.forEach((ln) => {
      body +=
        `<text x="${cx.toFixed(1)}" y="${ty.toFixed(1)}" fill="${ink}" ` +
        `font-size="${labelFs.toFixed(1)}" font-family="${ff}" text-anchor="middle" ` +
        `opacity="${progress.toFixed(2)}">${esc(ln)}</text>`
      ty += lh
    })
    body +=
      `<text x="${cx.toFixed(1)}" y="${(ty - lh + vorsprung).toFixed(1)}" fill="${ink}" ` +
      `font-size="${valFs.toFixed(1)}" font-weight="${staerke(content, 700)}" font-family="${ff}" ` +
      `text-anchor="middle" opacity="${progress.toFixed(2)}">${esc(valStr)}</text>`
  })
  return wrap(content, body, { links: offX + minX * scale, rechts: offX + maxX * scale })
}

// ── Wortwolke ───────────────────────────────────────────────────────────────
// Jans Wunsch 17.10.2026: Wörter (im Menü durch Komma getrennt, gespeichert als
// data[].label) werden automatisch zu einer dichten Wolke gelegt, die den
// Rahmen gleichmäßig füllt. Größe nach REIHENFOLGE (erstes Wort am größten,
// sanft abgestuft); ein Wort, das mehrfach vorkommt, wird größer. 1–3
// Schriften im Wechsel. Aufbau Wort für Wort, das größte zuerst.
//
// GEMESSEN statt geschätzt: Die Wortmaße kommen aus canvas.measureText mit der
// echten Schrift (geschätzte Breiten wichen bis 25 % ab — Wörter überlappten
// oder klafften). Der Renderer baut eine Wolke deshalb erst, wenn ihre
// Schriften geladen sind (s. wortwolkenSchriften). Ohne Browser (Tests) gilt
// eine grobe Schätzung.

export const INFOGRAPHIC_WORDCLOUD_SAMPLE: InfographicDataPoint[] = [
  'Storytelling', 'Data', 'Design', 'Motion', 'Insight', 'Emotion', 'Scroll',
  'Narrative', 'Clarity', 'Impact', 'Context', 'Colour', 'Rhythm', 'Focus',
  'Curiosity', 'Detail', 'Journey', 'Surprise', 'Audience', 'Voice', 'Space',
  'Light', 'Trust', 'Ideas',
].map((label) => ({ label, value: 1 }))

export interface WortwolkenSchrift {
  family: string
  weight: number
}

/** Schriften der Wolke in Wechsel-Reihenfolge — Schrift 1 = fontFamily/fontWeight. */
export function wortwolkenSchriften(content: InfographicContent): WortwolkenSchrift[] {
  const anzahl = Math.min(Math.max(Math.round(content.wordcloudFontCount ?? 1), 1), 3)
  const erste = { family: content.fontFamily || '', weight: grundStaerke(content) ?? 700 }
  const liste = [erste]
  for (let i = 0; i < anzahl - 1; i += 1) {
    const f = content.wordcloudFonts?.[i]
    // Anzahl hochgestellt, aber noch keine Schrift gewählt: wie Schrift 1
    liste.push({
      family: f?.family || erste.family,
      weight: typeof f?.weight === 'number' ? f.weight : 700,
    })
  }
  return liste
}

/** CSS-Schriftangabe einer Wolken-Schrift (für canvas, SVG und fonts.load). */
export function wortwolkenSchriftStapel(family: string): string {
  return family ? `'${family}', sans-serif` : 'sans-serif'
}

interface WolkenWort {
  text: string
  /** erste Zeile in data — Farbe und Reihenfolge */
  index: number
  anzahl: number
  gewicht: number
}

/** Einzigartige Wörter, schwerstes zuerst (Reihenfolge, dann Häufigkeit). */
export function wortwolkenWoerter(data: InfographicDataPoint[]): WolkenWort[] {
  const nachSchluessel = new Map<string, WolkenWort>()
  data.forEach((d, i) => {
    const text = String(d?.label ?? '').trim()
    if (!text) return
    const schluessel = text.toLocaleLowerCase()
    const da = nachSchluessel.get(schluessel)
    if (da) da.anzahl += 1
    else nachSchluessel.set(schluessel, { text, index: i, anzahl: 1, gewicht: 0 })
  })
  const liste = [...nachSchluessel.values()]
  const n = liste.length
  liste.forEach((w, rang) => {
    // 1 … 0,32: die ersten Wörter groß, dann sanft abgestuft
    const basis = n > 1 ? 1 - 0.68 * Math.pow(rang / (n - 1), 0.75) : 1
    w.gewicht = basis * (1 + 0.3 * (w.anzahl - 1))
  })
  return liste.sort((a, b) => b.gewicht - a.gewicht || a.index - b.index)
}

/** Maße eines Worts in em, relativ zum Textursprung (Grundlinie, Anfang). */
interface WortMasse {
  x0: number
  x1: number
  y0: number
  y1: number
}

let wolkenMessKontext: CanvasRenderingContext2D | null | undefined

function wortMasse(text: string, s: WortwolkenSchrift): WortMasse {
  if (wolkenMessKontext === undefined) {
    try {
      wolkenMessKontext =
        typeof document !== 'undefined'
          ? document.createElement('canvas').getContext('2d')
          : null
    } catch {
      wolkenMessKontext = null
    }
  }
  const ctx = wolkenMessKontext
  if (ctx) {
    const groesse = 200
    ctx.font = `${s.weight} ${groesse}px ${wortwolkenSchriftStapel(s.family)}`
    const m = ctx.measureText(text)
    const l = m.actualBoundingBoxLeft
    const r = m.actualBoundingBoxRight
    const a = m.actualBoundingBoxAscent
    const d = m.actualBoundingBoxDescent
    if ([l, r, a, d].every(Number.isFinite) && r + l > 0 && a + d > 0) {
      return { x0: -l / groesse, x1: r / groesse, y0: -a / groesse, y1: d / groesse }
    }
  }
  // Schätzung (ohne Browser): schmale, breite, große Zeichen
  let breite = 0
  for (const c of text) {
    breite += /[il.,:;'|!\s]/.test(c)
      ? 0.28
      : /[mwMW@]/.test(c)
        ? 0.86
        : /[A-ZÄÖÜ0-9]/.test(c)
          ? 0.66
          : 0.54
  }
  const oben = /[ÄÖÜ]/.test(text) ? 0.9 : /[A-Z0-9bdfhklt]/.test(text) ? 0.72 : 0.52
  const unten = /[gjpqy]/.test(text) ? 0.21 : 0.01
  return { x0: 0, x1: breite, y0: -oben, y1: unten }
}

/**
 * Wörter dicht um die Mitte legen: jedes Wort läuft eine Spirale ab (je Wort
 * um den goldenen Winkel gedreht, in die Breite gestreckt nach dem Format des
 * Rahmens) und nimmt den ersten Platz, an dem es kein anderes berührt.
 * Rückgabe: Mittelpunkte in denselben Einheiten wie die Kästen.
 */
function legeWortwolke(
  kaesten: Array<{ w: number; h: number }>,
  aspekt: number
): Array<{ x: number; y: number }> {
  const gelegt: Array<{ l: number; r: number; o: number; u: number }> = []
  const mitten: Array<{ x: number; y: number }> = []
  const streckX = Math.min(Math.max(aspekt, 0.3), 4)
  let zuletzt = 0
  kaesten.forEach(({ w, h }, k) => {
    // Luft um jedes Wort: gut ein Zehntel seiner Höhe
    const luft = Math.max(2, h * 0.11)
    const hw = w / 2 + luft
    const hh = h / 2 + luft
    const phase = k * 2.399963
    const stoesst = (x: number, y: number) => {
      // zuerst den Kasten prüfen, der zuletzt im Weg war (meist derselbe)
      const z = gelegt[zuletzt]
      if (z && x - hw < z.r && x + hw > z.l && y - hh < z.u && y + hh > z.o) return true
      for (let i = 0; i < gelegt.length; i += 1) {
        const g = gelegt[i]
        if (x - hw < g.r && x + hw > g.l && y - hh < g.u && y + hh > g.o) {
          zuletzt = i
          return true
        }
      }
      return false
    }
    let x = 0
    let y = 0
    for (let t = 0, schritte = 0; schritte < 400000; schritte += 1) {
      const radius = 0.9 * t
      x = radius * Math.cos(t + phase) * streckX
      y = radius * Math.sin(t + phase)
      if (!stoesst(x, y)) break
      // Bogenlänge je Schritt ~3 Einheiten, innen gröber begrenzt
      t += Math.min(0.35, 3 / Math.max(radius * streckX, 1))
    }
    gelegt.push({ l: x - hw, r: x + hw, o: y - hh, u: y + hh })
    mitten.push({ x, y })
  })
  return mitten
}

// Gelegte Wolken merken: der Aufbau baut das SVG bis zu 100× neu — die Spirale
// muss dafür nicht jedes Mal laufen. Schlüssel = alles, was die Lage bestimmt.
const wolkenLagen = new Map<string, Array<{ x: number; y: number }>>()

export interface WortwolkenTeil {
  text: string
  /** erste Zeile in data (Farbe) */
  index: number
  schrift: WortwolkenSchrift
  /** Mittelpunkt, Schriftgröße und Kasten im Inhaltsbereich (px) */
  cx: number
  cy: number
  fs: number
  w: number
  h: number
  /** Textursprung relativ zum Mittelpunkt */
  tx: number
  ty: number
}

/** Fertig gelegte Wolke im Inhaltsbereich — schwerstes Wort zuerst. */
export function wortwolkenTeile(content: InfographicContent): WortwolkenTeil[] {
  const { width: w, height: h } = content
  const woerter = wortwolkenWoerter(content.data ?? [])
  if (!woerter.length) return []
  const schriften = wortwolkenSchriften(content)
  const sourceFs = content.sourceFontSize ?? QUELLE_SCHRIFT_STANDARD

  // Grundgröße 100 je Gewicht 1; gemessen in em, Schrift im Wechsel
  const B = 100
  const maxGewicht = woerter[0].gewicht || 1
  const teile = woerter.map((wort, k) => {
    const schrift = schriften[k % schriften.length]
    const fs = (wort.gewicht / maxGewicht) * B
    const m = wortMasse(wort.text, schrift)
    return { wort, schrift, fs, m, w: (m.x1 - m.x0) * fs, h: (m.y1 - m.y0) * fs }
  })

  const padX = 40
  const padTop = kopf(content).hoehe ? kopf(content).hoehe + 56 : 40
  const padBottom = content.source ? sourceFs + 50 : 40
  const flaecheW = Math.max(w - 2 * padX, 10)
  const flaecheH = Math.max(h - padTop - padBottom, 10)

  const schluessel = JSON.stringify([
    teile.map((t) => [Math.round(t.w * 10), Math.round(t.h * 10)]),
    Math.round((flaecheW / flaecheH) * 100),
  ])
  let mitten = wolkenLagen.get(schluessel)
  if (!mitten) {
    mitten = legeWortwolke(teile, flaecheW / flaecheH)
    if (wolkenLagen.size > 40) wolkenLagen.clear()
    wolkenLagen.set(schluessel, mitten)
  }

  // auf den Rahmen einpassen (Seitenverhältnis bleibt), mittig
  let l = Infinity
  let r = -Infinity
  let o = Infinity
  let u = -Infinity
  teile.forEach((t, k) => {
    l = Math.min(l, mitten[k].x - t.w / 2)
    r = Math.max(r, mitten[k].x + t.w / 2)
    o = Math.min(o, mitten[k].y - t.h / 2)
    u = Math.max(u, mitten[k].y + t.h / 2)
  })
  const massstab = Math.min(flaecheW / Math.max(r - l, 1), flaecheH / Math.max(u - o, 1))
  const offX = padX + flaecheW / 2 - ((l + r) / 2) * massstab
  const offY = padTop + flaecheH / 2 - ((o + u) / 2) * massstab
  return teile.map((t, k) => {
    const fs = t.fs * massstab
    return {
      text: t.wort.text,
      index: t.wort.index,
      schrift: t.schrift,
      cx: offX + mitten[k].x * massstab,
      cy: offY + mitten[k].y * massstab,
      fs,
      w: t.w * massstab,
      h: t.h * massstab,
      // Textursprung so, dass der Kasten des Worts mittig auf (cx, cy) sitzt
      tx: -((t.m.x0 + t.m.x1) / 2) * fs,
      ty: -((t.m.y0 + t.m.y1) / 2) * fs,
    }
  })
}

function wordcloudSvg(content: InfographicContent, progress: number): string {
  const teile = wortwolkenTeile(content)
  if (!teile.length) return wrap(content, '')
  const palette = content.palette ?? []
  const farbe = (i: number) =>
    content.data[i]?.color || palette[i % (palette.length || 1)] || '#6c8cff'

  // Aufbau: Wort für Wort, das größte zuerst — jedes wächst kurz auf
  const n = teile.length
  const dauer = Math.min(0.35, Math.max(0.12, 3 / n))
  const zurueck = (x: number) => {
    const c = 1.5
    return 1 + (c + 1) * Math.pow(x - 1, 3) + c * Math.pow(x - 1, 2)
  }

  let body = ''
  teile.forEach((t, k) => {
    const start = n > 1 ? (k / (n - 1)) * (1 - dauer) : 0
    const lp = Math.min(Math.max((progress - start) / dauer, 0), 1)
    if (lp <= 0) return
    const { cx, cy, fs, tx, ty } = t
    const voll = lp >= 1
    const s = voll ? 1 : 0.3 + 0.7 * zurueck(lp)
    body +=
      `<g transform="translate(${cx.toFixed(1)} ${cy.toFixed(1)})` +
      (voll ? '' : ` scale(${s.toFixed(3)})`) +
      `"${voll ? '' : ` opacity="${Math.min(1, lp * 1.6).toFixed(2)}"`}>` +
      `<text x="${tx.toFixed(1)}" y="${ty.toFixed(1)}" fill="${farbe(t.index)}" ` +
      `font-size="${fs.toFixed(1)}" font-weight="${t.schrift.weight}" ` +
      `font-family="${wortwolkenSchriftStapel(t.schrift.family)}">${esc(t.text)}</text></g>`
  })
  return wrap(content, body, {
    links: Math.min(...teile.map((t) => t.cx - t.w / 2)),
    rechts: Math.max(...teile.map((t) => t.cx + t.w / 2)),
  })
}

// ── Tabelle ─────────────────────────────────────────────────────────────────
// Jans Wunsch 17.10.2026: statt einer Scene aus vielen Textebenen eine echte
// Tabelle als Daten-Typ — bis 5 Spalten und 20 Zeilen, Kopfzeile klein in
// Versalien, Zeilen mit Trennlinien. Erste Spalte links, Zahlen-Spalten
// rechtsbündig (automatisch). Zeilen lassen sich hervorheben. Drei
// Schrift-Bereiche: Kopfzeile, erste Spalte, übrige Zellen. Aufbau Zeile für
// Zeile. Passt die Tabelle nicht in den Rahmen, werden alle Schriften
// gemeinsam kleiner — sie bleibt immer ganz sichtbar.

export const TABELLE_MAX_SPALTEN = 5
export const TABELLE_MAX_ZEILEN = 20

/** Beispiel: Kopfzeile + Zeilen (wie Jans Preis-Scene). */
export const INFOGRAPHIC_TABLE_SAMPLE: string[][] = [
  ['Plan', 'Price', 'Seats'],
  ['Start', '9 €', '1'],
  ['Team', '29 €', '5'],
  ['Business', '99 €', '25'],
  ['Enterprise', 'on request', 'unlimited'],
]

/** Zeilen auf die Grenzen bringen: höchstens 5 Spalten, Kopf + 20 Zeilen, alle gleich breit. */
export function tabellenZeilen(content: InfographicContent): string[][] {
  const roh = Array.isArray(content.tableRows) ? content.tableRows : []
  const zeilen = roh.slice(0, TABELLE_MAX_ZEILEN + 1).map((z) =>
    (Array.isArray(z) ? z : []).slice(0, TABELLE_MAX_SPALTEN).map((c) => String(c ?? '')),
  )
  const spalten = Math.max(1, ...zeilen.map((z) => z.length))
  return zeilen.map((z) => [...z, ...Array(Math.max(0, spalten - z.length)).fill('')])
}

/** Schriften: Zellen (= fontFamily/fontWeight), Kopfzeile, erste Spalte — leer = wie Zellen. */
export function tabellenSchriften(content: InfographicContent): {
  zellen: WortwolkenSchrift
  kopf: WortwolkenSchrift
  erste: WortwolkenSchrift
} {
  const zellen = { family: content.fontFamily || '', weight: grundStaerke(content) ?? 400 }
  const aus = (f?: { family?: string; weight?: number }, gewicht = zellen.weight) => ({
    family: f?.family || zellen.family,
    weight: typeof f?.weight === 'number' ? f.weight : gewicht,
  })
  return { zellen, kopf: aus(content.tableHeaderFont, 500), erste: aus(content.tableFirstColFont) }
}

/** Schrift der Überschrift (Jan 18.10.2026) — leer = Schrift des Diagramms, fett. */
export function infografikTitelSchrift(content: InfographicContent): WortwolkenSchrift {
  const f = content.titleFont
  return {
    family: f?.family || content.fontFamily || '',
    weight: typeof f?.weight === 'number' ? f.weight : staerke(content, 700),
  }
}

/** Spalte mit Zahlen (mindestens die Hälfte der Zellen enthält eine Ziffer) → rechtsbündig. */
function spalteRechts(zeilen: string[][], s: number): boolean {
  if (s === 0) return false
  const werte = zeilen.slice(1).map((z) => z[s].trim()).filter(Boolean)
  if (!werte.length) return false
  return werte.filter((w) => /\d/.test(w)).length * 2 >= werte.length
}

/** Breite eines Texts in px (gemessen mit der echten Schrift; ohne Browser geschätzt). */
function textBreite(text: string, s: WortwolkenSchrift, fs: number, abstandEm = 0): number {
  if (!text) return 0
  if (wolkenMessKontext === undefined) wortMasse('', s)
  const ctx = wolkenMessKontext
  let b: number
  if (ctx) {
    ctx.font = `${s.weight} 100px ${wortwolkenSchriftStapel(s.family)}`
    b = (ctx.measureText(text).width / 100) * fs
  } else {
    b = (wortMasse(text, s).x1 - wortMasse(text, s).x0) * fs
  }
  return b + abstandEm * fs * text.length
}

function tableSvg(content: InfographicContent, progress: number): string {
  const { width: w, height: h } = content
  const zeilen = tabellenZeilen(content)
  if (!zeilen.length || zeilen.every((z) => z.every((c) => !c.trim()))) return wrap(content, '')
  const schriften = tabellenSchriften(content)
  const { value: zellFs0, label: kopfFs0 } = fonts(content)
  const sourceFs = content.sourceFontSize ?? QUELLE_SCHRIFT_STANDARD
  const spalten = zeilen[0].length
  const rechts = Array.from({ length: spalten }, (_, s) => spalteRechts(zeilen, s))
  const markiert = new Set((content.tableHighlight ?? []).filter((i) => i >= 1))

  const padX = 40
  const padTop = kopf(content).hoehe ? kopf(content).hoehe + 56 : 40
  const padBottom = content.source ? sourceFs + 50 : 40
  const flaecheW = Math.max(w - 2 * padX, 10)
  const flaecheH = Math.max(h - padTop - padBottom, 10)

  // Maße bei Schriftgröße 1 — dann gemeinsam so skalieren, dass alles passt
  const kopfAbstand = 0.12 // Sperrung der Versalien (em)
  const luft = 1.6 // Zwischenraum zwischen Spalten (in Zellen-em)
  const roh = (k: number) => {
    const zellFs = zellFs0 * k
    const kopfFs = kopfFs0 * k
    const breiten = Array.from({ length: spalten }, (_, s) =>
      Math.max(
        textBreite(zeilen[0][s].toUpperCase(), schriften.kopf, kopfFs, kopfAbstand),
        ...zeilen.slice(1).map((z) => textBreite(z[s], s === 0 ? schriften.erste : schriften.zellen, zellFs)),
      ),
    )
    const breite = breiten.reduce((a, b) => a + b, 0) + luft * zellFs * (spalten - 1)
    const kopfH = kopfFs * 2.4
    const zeileH = zellFs * 2.1
    const hoehe = kopfH + zeileH * (zeilen.length - 1)
    return { zellFs, kopfFs, breiten, breite, kopfH, zeileH, hoehe }
  }
  const eins = roh(1)
  const k = Math.min(1, flaecheW / Math.max(eins.breite, 1), flaecheH / Math.max(eins.hoehe, 1))
  const m = k < 1 ? roh(k) : eins
  // Platz, der übrig bleibt, bekommt die erste Spalte (Namen links, Zahlen rechts)
  const breiten = [...m.breiten]
  breiten[0] += Math.max(0, flaecheW - m.breite)
  const lueckeX = luft * m.zellFs
  const xLinks: number[] = []
  let x = padX
  for (let s = 0; s < spalten; s += 1) {
    xLinks.push(x)
    x += breiten[s] + lueckeX
  }
  const y0 = padTop + Math.max(0, (flaecheH - m.hoehe) / 2)

  const textFarbe = content.textColor || '#000000'
  const kopfFarbe = content.tableHeaderColor || content.labelColor || textFarbe
  const linienFarbe = content.tableLineColor || content.labelColor || textFarbe
  const markFarbe = content.tableHighlightColor || '#e9d5e6'
  const markText = content.tableHighlightTextColor || textFarbe
  // Zellen = Werte (Jan 19.10.2026); Kopf/Linien folgen weiter „Alle Texte“
  const zellFarbe = content.valueColor || textFarbe
  const linie = Math.max(1, m.zellFs * 0.04)

  // Aufbau: Kopfzeile, dann Zeile für Zeile — jede blendet ein und gleitet hoch
  const n = zeilen.length
  const dauer = Math.min(0.4, Math.max(0.12, 2.5 / n))
  const anteil = (i: number) => {
    const start = n > 1 ? (i / (n - 1)) * (1 - dauer) : 0
    const p = Math.min(Math.max((progress - start) / dauer, 0), 1)
    return 1 - Math.pow(1 - p, 3)
  }

  const zelle = (txt: string, s: number, y: number, fs: number, schrift: WortwolkenSchrift, farbe: string, extra = '') =>
    txt
      ? `<text x="${(rechts[s] ? xLinks[s] + breiten[s] : xLinks[s]).toFixed(1)}" y="${y.toFixed(1)}" ` +
        `fill="${farbe}" font-size="${fs.toFixed(1)}" font-weight="${schrift.weight}" ` +
        `font-family="${wortwolkenSchriftStapel(schrift.family)}"` +
        `${rechts[s] ? ' text-anchor="end"' : ''}${extra}>${esc(txt)}</text>`
      : ''

  let body = ''
  // Kopfzeile
  const aKopf = anteil(0)
  if (aKopf > 0) {
    const yKopf = y0 + m.kopfH * 0.55
    body += `<g opacity="${aKopf.toFixed(2)}">`
    for (let s = 0; s < spalten; s += 1) {
      body += zelle(zeilen[0][s].toUpperCase(), s, yKopf, m.kopfFs, schriften.kopf, kopfFarbe, ` letter-spacing="${(kopfAbstand * m.kopfFs).toFixed(1)}"`)
    }
    body += '</g>'
  }
  // Zeilen
  const linksAussen = padX
  const rechtsAussen = xLinks[spalten - 1] + breiten[spalten - 1]
  for (let i = 1; i < n; i += 1) {
    const a = anteil(i)
    if (a <= 0) continue
    const oben = y0 + m.kopfH + (i - 1) * m.zeileH
    const versatz = (1 - a) * m.zeileH * 0.35
    const grundlinie = oben + m.zeileH * 0.62
    const hervor = markiert.has(i)
    body += `<g opacity="${a.toFixed(2)}"${versatz > 0.05 ? ` transform="translate(0 ${versatz.toFixed(1)})"` : ''}>`
    if (hervor) {
      const rand = m.zellFs * 0.45
      body +=
        `<rect x="${(linksAussen - rand).toFixed(1)}" y="${(oben + linie).toFixed(1)}" ` +
        `width="${(rechtsAussen - linksAussen + 2 * rand).toFixed(1)}" height="${(m.zeileH - 2 * linie).toFixed(1)}" fill="${markFarbe}"/>`
    }
    for (let s = 0; s < spalten; s += 1) {
      body += zelle(zeilen[i][s], s, grundlinie, m.zellFs, s === 0 ? schriften.erste : schriften.zellen, hervor ? markText : zellFarbe)
    }
    // Trennlinie unter der Zeile — zieht sich mit dem Auftritt von links auf
    const yLinie = oben + m.zeileH
    const laenge = (rechtsAussen - linksAussen) * a
    if (!hervor) {
      body +=
        `<line x1="${linksAussen.toFixed(1)}" y1="${yLinie.toFixed(1)}" x2="${(linksAussen + laenge).toFixed(1)}" ` +
        `y2="${yLinie.toFixed(1)}" stroke="${linienFarbe}" stroke-opacity="0.45" stroke-width="${linie.toFixed(1)}"/>`
    }
    body += '</g>'
  }
  return wrap(content, body)
}

// ── Aufzählung ─────────────────────────────────────────────────────────────
// Jans Wunsch 18.10.2026 (aus Benedikts Scene „Ablauf Schritt für Schritt“):
// bis zu 10 Punkte von oben nach unten, je Nummer (Kreis, Quadrat oder ohne
// Form), großer Text und Subtext darunter; eine dünne Linie verbindet die
// Markierungen. Aufbau Punkt für Punkt. Passt die Liste nicht in den Rahmen,
// wird alles gemeinsam kleiner.

export const AUFZAEHLUNG_MAX = 10

/** Beispiel (wie Benedikts Scene). */
export const INFOGRAPHIC_STEPS_SAMPLE: InfographicDataPoint[] = [
  { label: 'Sign up', value: 0, text: 'Create an account in two minutes' },
  { label: 'Choose', value: 0, text: 'Find the plan that fits' },
  { label: 'Get started', value: 0, text: 'Start right away, nothing to install' },
  { label: 'Grow', value: 0, text: 'Scale up whenever you like' },
]

/** Schriften: Text (= fontFamily/fontWeight, Subtext + Nummern) und Punkte (große Texte; leer = wie Text). */
export function aufzaehlungSchriften(content: InfographicContent): {
  text: WortwolkenSchrift
  punkt: WortwolkenSchrift
} {
  const text = { family: content.fontFamily || '', weight: grundStaerke(content) ?? 400 }
  const f = content.stepsHeadFont
  return {
    text,
    punkt: { family: f?.family || text.family, weight: typeof f?.weight === 'number' ? f.weight : text.weight },
  }
}

/**
 * Nummernfarbe einer Markierung: gewählt, sonst automatisch kontrastreich
 * (Jans Fund 18.10.2026: weiße Zahl auf weißem Kreis bei dunklem Grund).
 */
export function aufzaehlungZahlFarbe(content: InfographicContent, markierung: string): string {
  if (content.stepsNumberColor) return content.stepsNumberColor
  const f = String(markierung).trim()
  let r = 0, g = 0, b = 0
  const hex = f.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
  const rgb = f.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i)
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1]
    r = parseInt(h.slice(0, 2), 16)
    g = parseInt(h.slice(2, 4), 16)
    b = parseInt(h.slice(4, 6), 16)
  } else if (rgb) {
    r = +rgb[1]
    g = +rgb[2]
    b = +rgb[3]
  } else return '#ffffff'
  // wahrgenommene Helligkeit (0–255): hell → dunkle Zahl, dunkel → weiße
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? '#1b1a17' : '#ffffff'
}

function stepsSvg(content: InfographicContent, progress: number): string {
  const { width: w, height: h } = content
  const punkte = (content.data ?? [])
    .slice(0, AUFZAEHLUNG_MAX)
    .filter((d) => String(d.label ?? '').trim() || String(d.text ?? '').trim())
  if (!punkte.length) return wrap(content, '')
  const schriften = aufzaehlungSchriften(content)
  const { value: grossFs0, label: kleinFs0 } = fonts(content)
  const sourceFs = content.sourceFontSize ?? QUELLE_SCHRIFT_STANDARD
  const form = content.stepsMarker ?? 'circle'
  const n = punkte.length
  const mitText = punkte.some((p) => String(p.text ?? '').trim())

  const padX = 40
  const padTop = kopf(content).hoehe ? kopf(content).hoehe + 56 : 40
  const padBottom = content.source ? sourceFs + 50 : 40
  const flaecheW = Math.max(w - 2 * padX, 10)
  const flaecheH = Math.max(h - padTop - padBottom, 10)

  // Maße bei Faktor k — dann gemeinsam so skalieren, dass alles passt
  const masse = (k: number) => {
    const gross = grossFs0 * k
    const klein = kleinFs0 * k
    const d = gross * 1.3 // Durchmesser der Markierung
    const luftX = d * 0.55
    const blockH = gross * 1.05 + (mitText ? klein * 1.55 : 0)
    const zeileH = Math.max(gross * 0.55 + d / 2, blockH)
    const abstand = gross * 1.0
    const hoehe = n * zeileH + (n - 1) * abstand
    const textB = Math.max(
      ...punkte.map((p) =>
        Math.max(textBreite(String(p.label ?? ''), schriften.punkt, gross), textBreite(String(p.text ?? ''), schriften.text, klein)),
      ),
    )
    return { gross, klein, d, luftX, zeileH, abstand, hoehe, breite: d + luftX + textB }
  }
  const eins = masse(1)
  const k = Math.min(1, flaecheW / Math.max(eins.breite, 1), flaecheH / Math.max(eins.hoehe, 1))
  const m = k < 1 ? masse(k) : eins
  const x0 = padX
  // oben angedockt (Jan 18.10.2026): die Liste beginnt unter der Überschrift
  // und wächst nach unten; passt sie nicht, wird oben alles kleiner (k)
  const y0 = padTop
  const cx = x0 + m.d / 2
  const textX = x0 + m.d + m.luftX

  const textFarbe = content.textColor || '#000000'
  const markFarbe = content.stepsMarkerColor || textFarbe
  const linienFarbe = content.lineColor || content.labelColor || textFarbe
  const linie = Math.max(2, m.d * 0.05)

  // Aufbau: Punkt für Punkt — jeder blendet ein, die Linie wächst zum nächsten
  const dauer = Math.min(0.4, Math.max(0.12, 2.5 / n))
  const anteil = (i: number) => {
    const start = n > 1 ? (i / (n - 1)) * (1 - dauer) : 0
    const p = Math.min(Math.max((progress - start) / dauer, 0), 1)
    return 1 - Math.pow(1 - p, 3)
  }
  const mitteY = (i: number) => y0 + i * (m.zeileH + m.abstand) + m.gross * 0.55

  let linien = ''
  if (content.stepsLine !== false) {
    const luft = form === 'none' ? m.d * 0.45 : m.d / 2 + m.d * 0.12
    for (let i = 0; i < n - 1; i += 1) {
      const a = anteil(i + 1)
      if (a <= 0) continue
      const von = mitteY(i) + luft
      const bis = mitteY(i + 1) - luft
      if (bis <= von) continue
      linien +=
        `<line x1="${cx.toFixed(1)}" y1="${von.toFixed(1)}" x2="${cx.toFixed(1)}" y2="${(von + (bis - von) * a).toFixed(1)}" ` +
        `stroke="${linienFarbe}" stroke-opacity="0.35" stroke-width="${linie.toFixed(1)}" stroke-linecap="round"/>`
    }
  }

  let body = linien
  punkte.forEach((p, i) => {
    const a = anteil(i)
    if (a <= 0) return
    const cy = mitteY(i)
    const farbe = p.color || markFarbe
    const s = 0.6 + 0.4 * a
    // Markierung mit Nummer — springt leicht auf
    let marke = ''
    if (form === 'circle') marke = `<circle cx="0" cy="0" r="${(m.d / 2).toFixed(1)}" fill="${farbe}"/>`
    else if (form === 'square') {
      const r = m.d / 2
      marke = `<rect x="${(-r).toFixed(1)}" y="${(-r).toFixed(1)}" width="${m.d.toFixed(1)}" height="${m.d.toFixed(1)}" rx="${(m.d * 0.14).toFixed(1)}" fill="${farbe}"/>`
    }
    const zahlFs = form === 'none' ? m.d * 0.82 : m.d * 0.42
    marke +=
      `<text x="0" y="0" fill="${form === 'none' ? farbe : aufzaehlungZahlFarbe(content, farbe)}" font-size="${zahlFs.toFixed(1)}" ` +
      `font-weight="${staerke(content, 600)}" font-family="${wortwolkenSchriftStapel(schriften.text.family)}" ` +
      `text-anchor="middle" dominant-baseline="central">${i + 1}</text>`
    body += `<g opacity="${a.toFixed(2)}" transform="translate(${cx.toFixed(1)} ${cy.toFixed(1)}) scale(${s.toFixed(3)})">${marke}</g>`
    // Texte — blenden ein und gleiten leicht hoch
    const oben = cy - m.gross * 0.55
    const versatz = (1 - a) * m.gross * 0.3
    let texte = ''
    if (String(p.label ?? '').trim()) {
      texte +=
        `<text x="${textX.toFixed(1)}" y="${(oben + m.gross * 0.82).toFixed(1)}" fill="${content.valueColor || textFarbe}" ` +
        `font-size="${m.gross.toFixed(1)}" font-weight="${schriften.punkt.weight}" ` +
        `font-family="${wortwolkenSchriftStapel(schriften.punkt.family)}">${esc(String(p.label))}</text>`
    }
    if (String(p.text ?? '').trim()) {
      texte +=
        `<text x="${textX.toFixed(1)}" y="${(oben + m.gross * 1.05 + m.klein * 1.15).toFixed(1)}" ${labelPaint(content, 0.7)} ` +
        `font-size="${m.klein.toFixed(1)}" font-weight="${schriften.text.weight}" ` +
        `font-family="${wortwolkenSchriftStapel(schriften.text.family)}">${esc(String(p.text))}</text>`
    }
    body += `<g opacity="${a.toFixed(2)}"${versatz > 0.05 ? ` transform="translate(0 ${versatz.toFixed(1)})"` : ''}>${texte}</g>`
  })
  return wrap(content, body)
}

/**
 * Schriften, mit denen die Engine MISST (Wortwolke: Wortmaße, Tabelle:
 * Spaltenbreiten) — der Renderer baut solche Grafiken erst, wenn sie geladen
 * sind (s. renderer/infographic/wortwolkenSchriften).
 */
export function infografikMessSchriften(content: InfographicContent): WortwolkenSchrift[] {
  if (content.chartType === 'wordcloud') return wortwolkenSchriften(content)
  if (content.chartType === 'table') {
    const s = tabellenSchriften(content)
    return [s.zellen, s.kopf, s.erste]
  }
  if (content.chartType === 'steps') {
    const s = aufzaehlungSchriften(content)
    return [s.text, s.punkt]
  }
  return []
}

// ── World map ───────────────────────────────────────────────────────────────
// Robinson coefficients per 5° of |latitude|: X = relative parallel length,
// Y = relative distance from the equator.
const ROBINSON: Array<[number, number]> = [
  [1.0, 0.0], [0.9986, 0.062], [0.9954, 0.124], [0.99, 0.186],
  [0.9822, 0.248], [0.973, 0.31], [0.96, 0.372], [0.9427, 0.434],
  [0.9216, 0.4958], [0.8962, 0.5571], [0.8679, 0.6176], [0.835, 0.6769],
  [0.7986, 0.7346], [0.7597, 0.7903], [0.7186, 0.8435], [0.6732, 0.8936],
  [0.6213, 0.9394], [0.5722, 0.9761], [0.5322, 1.0],
]

const D2R = Math.PI / 180

// A projection spec: the family plus any parameters it needs.
export interface ProjSpec {
  kind:
    | 'equirectangular'
    | 'mercator'
    | 'robinson'
    | 'equalEarth'
    | 'naturalEarth'
    | 'tmerc' // transverse Mercator (Gauß-Krüger / UTM family), needs lon0
    | 'lcc' // Lambert conformal conic, needs lat1/lat2/lat0/lon0
    | 'aea' // Albers equal-area conic, needs lat1/lat2/lat0/lon0
    | 'albersUsa' // CONUS + Alaska/Hawaii insets (composite)
    | 'espCanaries' // mainland Spain (TM) + Canary Islands inset (composite)
  lon0?: number
  lat0?: number
  lat1?: number
  lat2?: number
}

// Albers equal-area conic projector (degrees → planar, y down). Reused by the
// 'aea' kind and the albersUsa composite.
function aeaProjector(
  lat1: number,
  lat2: number,
  lat0: number,
  lon0: number
): (lon: number, lat: number) => [number, number] {
  const p1 = lat1 * D2R
  const p2 = lat2 * D2R
  const p0 = lat0 * D2R
  const l0 = lon0 * D2R
  const n = (Math.sin(p1) + Math.sin(p2)) / 2
  const C = Math.cos(p1) * Math.cos(p1) + 2 * n * Math.sin(p1)
  const rho0 = Math.sqrt(C - 2 * n * Math.sin(p0)) / n
  return (lon, lat) => {
    const phi = lat * D2R
    const rho = Math.sqrt(Math.max(0, C - 2 * n * Math.sin(phi))) / n
    const th = n * (lon * D2R - l0)
    return [rho * Math.sin(th), -(rho0 - rho * Math.cos(th))]
  }
}

// Spherical transverse Mercator about central meridian lon0 (degrees → planar,
// y down). Reused by the 'tmerc' kind and the espCanaries composite.
function tmercProjector(
  lon0deg: number
): (lon: number, lat: number) => [number, number] {
  const lon0 = lon0deg * D2R
  return (lon, lat) => {
    const lam = lon * D2R - lon0
    const phi = lat * D2R
    let B = Math.cos(phi) * Math.sin(lam)
    B = Math.max(-0.9999, Math.min(0.9999, B))
    const x = 0.5 * Math.log((1 + B) / (1 - B))
    const y = Math.atan2(Math.tan(phi), Math.cos(lam))
    return [x, -y]
  }
}

// lon/lat (degrees) → planar x/y, y pointing DOWN (north at the top) to match
// the SVG coordinate system. Absolute scale is irrelevant; mapSvg fits the
// result to the box by its bounds afterwards.
export function makeProjector(
  spec: ProjSpec
): (lon: number, lat: number) => [number, number] {
  const { kind } = spec
  if (kind === 'mercator') {
    return (lon, lat) => {
      const phi = Math.max(-85, Math.min(85, lat)) * D2R
      return [lon, -(180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + phi / 2))]
    }
  }
  if (kind === 'robinson') {
    return (lon, lat) => {
      const aLat = Math.min(90, Math.abs(lat) || 0)
      const i = Math.max(0, Math.min(17, Math.floor(aLat / 5)))
      const t = (aLat - i * 5) / 5
      const X = ROBINSON[i][0] + (ROBINSON[i + 1][0] - ROBINSON[i][0]) * t
      const Y = ROBINSON[i][1] + (ROBINSON[i + 1][1] - ROBINSON[i][1]) * t
      return [0.8487 * X * lon, -1.3523 * Y * Math.sign(lat) * (180 / Math.PI)]
    }
  }
  if (kind === 'equalEarth') {
    // Šavrič, Patterson & Jenny (2018), fl*=area-preserving
    const A1 = 1.340264
    const A2 = -0.081106
    const A3 = 0.000893
    const A4 = 0.003796
    const M = Math.sqrt(3) / 2
    return (lon, lat) => {
      const th = Math.asin(M * Math.sin(lat * D2R))
      const th2 = th * th
      const x =
        (2 * Math.sqrt(3) * (lon * D2R) * Math.cos(th)) /
        (3 * (9 * A4 * th2 * th2 * th2 * th2 + 7 * A3 * th2 * th2 * th2 + 3 * A2 * th2 + A1))
      const y =
        A4 * th2 * th2 * th2 * th2 * th +
        A3 * th2 * th2 * th2 * th +
        A2 * th2 * th +
        A1 * th
      return [x, -y]
    }
  }
  if (kind === 'naturalEarth') {
    // Natural Earth I polynomial (Šavrič 2011)
    return (lon, lat) => {
      const l = lon * D2R
      const p = lat * D2R
      const p2 = p * p
      const p4 = p2 * p2
      const x =
        l * (0.8707 - 0.131979 * p2 - 0.013791 * p4 + 0.003971 * p4 * p4 * p2 - 0.001529 * p4 * p4 * p4)
      const y =
        p * (1.007226 + 0.015085 * p2 - 0.044475 * p2 * p4 + 0.028874 * p4 * p4 - 0.005916 * p4 * p4 * p2)
      return [x, -y]
    }
  }
  if (kind === 'tmerc') {
    return tmercProjector(spec.lon0 ?? 0)
  }
  if (kind === 'lcc' || kind === 'aea') {
    // conic projections about two standard parallels
    const p1 = (spec.lat1 ?? 30) * D2R
    const p2 = (spec.lat2 ?? 60) * D2R
    const p0 = (spec.lat0 ?? 0) * D2R
    const l0 = (spec.lon0 ?? 0) * D2R
    if (kind === 'lcc') {
      const n =
        Math.abs(p1 - p2) < 1e-9
          ? Math.sin(p1)
          : Math.log(Math.cos(p1) / Math.cos(p2)) /
            Math.log(
              Math.tan(Math.PI / 4 + p2 / 2) / Math.tan(Math.PI / 4 + p1 / 2)
            )
      const F = (Math.cos(p1) * Math.pow(Math.tan(Math.PI / 4 + p1 / 2), n)) / n
      const rho0 = F / Math.pow(Math.tan(Math.PI / 4 + p0 / 2), n)
      return (lon, lat) => {
        const phi = Math.max(-89.9, Math.min(89.9, lat)) * D2R
        const rho = F / Math.pow(Math.tan(Math.PI / 4 + phi / 2), n)
        const th = n * (lon * D2R - l0)
        return [rho * Math.sin(th), -(rho0 - rho * Math.cos(th))]
      }
    }
    // Albers equal-area conic
    return aeaProjector(
      spec.lat1 ?? 30,
      spec.lat2 ?? 60,
      spec.lat0 ?? 0,
      spec.lon0 ?? 0
    )
  }
  if (kind === 'albersUsa') {
    // d3-style composite: CONUS in Albers, Alaska + Hawaii as scaled insets in
    // the lower-left. Insets are placed in CONUS planar units (constants tuned to
    // the bundled geometry). Region routing is by lon/lat.
    const conus = aeaProjector(29.5, 45.5, 23, -96)
    const alaska = aeaProjector(55, 65, 50, -154)
    const hawaii = aeaProjector(8, 18, 13, -157)
    const AKS = 0.42
    const AKX = -0.2176
    const AKY = 0.1673
    const HIS = 1.4
    const HIX = 0.025
    const HIY = 0.2648
    return (lon, lat) => {
      if (lat >= 50) {
        // Alaska — the only US land above 50°N (normalise the antimeridian)
        const l = lon > 0 ? lon - 360 : lon
        const [x, y] = alaska(l, lat)
        return [x * AKS + AKX, y * AKS + AKY]
      }
      if (lat >= 17 && lat <= 24 && lon >= -161 && lon <= -152) {
        const [x, y] = hawaii(lon, lat)
        return [x * HIS + HIX, y * HIS + HIY]
      }
      return conus(lon, lat)
    }
  }
  if (kind === 'espCanaries') {
    // mainland Spain (+ Balearics, Ceuta/Melilla) in TM; the Canary Islands as a
    // scaled inset pulled into the lower-left (constants tuned to the geometry).
    const main = tmercProjector(-3)
    const canary = tmercProjector(-3)
    // same scale as the mainland (1:1); tucked compactly just below it, within
    // the mainland's horizontal span so there's no wasted width.
    const CS = 1.0
    const CX = 0.157
    const CY = -0.076
    return (lon, lat) => {
      if (lat < 31 && lon < -11) {
        const [x, y] = canary(lon, lat)
        return [x * CS + CX, y * CS + CY]
      }
      return main(lon, lat)
    }
  }
  return (lon, lat) => [lon, -lat] // equirectangular
}

// ── Country maps: ordered list + each country's real national projection ──────
// Single source of truth for the Country-Map dropdown (Menu) AND the projector.
export const MAP_COUNTRY_OPTIONS: Array<{
  a3: string
  name: string
  projLabel: string
  proj: ProjSpec
}> = [
  { a3: 'AUT', name: 'Austria', projLabel: 'Austria Lambert', proj: { kind: 'lcc', lat1: 46, lat2: 49, lat0: 47.5, lon0: 13.33 } },
  { a3: 'BEL', name: 'Belgium', projLabel: 'UTM 31N', proj: { kind: 'tmerc', lon0: 4.5 } },
  { a3: 'BGR', name: 'Bulgaria', projLabel: 'UTM 35N', proj: { kind: 'tmerc', lon0: 25 } },
  { a3: 'HRV', name: 'Croatia', projLabel: 'UTM 33N', proj: { kind: 'tmerc', lon0: 16.5 } },
  { a3: 'CYP', name: 'Cyprus', projLabel: 'UTM 36N', proj: { kind: 'tmerc', lon0: 33 } },
  { a3: 'CZE', name: 'Czechia', projLabel: 'UTM 33N', proj: { kind: 'tmerc', lon0: 15.5 } },
  { a3: 'DNK', name: 'Denmark', projLabel: 'UTM 32N', proj: { kind: 'tmerc', lon0: 10 } },
  { a3: 'EST', name: 'Estonia', projLabel: 'UTM 35N', proj: { kind: 'tmerc', lon0: 25.5 } },
  { a3: 'FIN', name: 'Finland', projLabel: 'UTM 35N', proj: { kind: 'tmerc', lon0: 26 } },
  { a3: 'FRA', name: 'France', projLabel: 'Lambert-93', proj: { kind: 'lcc', lat1: 44, lat2: 49, lat0: 46.5, lon0: 3 } },
  { a3: 'DEU', name: 'Germany', projLabel: 'Gauß-Krüger', proj: { kind: 'tmerc', lon0: 10.5 } },
  { a3: 'GRC', name: 'Greece', projLabel: 'UTM 34N', proj: { kind: 'tmerc', lon0: 23 } },
  { a3: 'HUN', name: 'Hungary', projLabel: 'UTM 34N', proj: { kind: 'tmerc', lon0: 19 } },
  { a3: 'IRL', name: 'Ireland', projLabel: 'UTM 29N', proj: { kind: 'tmerc', lon0: -8 } },
  { a3: 'ITA', name: 'Italy', projLabel: 'Gauss-Boaga (TM)', proj: { kind: 'tmerc', lon0: 12.5 } },
  { a3: 'LVA', name: 'Latvia', projLabel: 'UTM 35N', proj: { kind: 'tmerc', lon0: 24.5 } },
  { a3: 'LTU', name: 'Lithuania', projLabel: 'UTM 34N', proj: { kind: 'tmerc', lon0: 23.5 } },
  { a3: 'LUX', name: 'Luxembourg', projLabel: 'UTM 32N', proj: { kind: 'tmerc', lon0: 6.1 } },
  { a3: 'MLT', name: 'Malta', projLabel: 'UTM 33N', proj: { kind: 'tmerc', lon0: 14.4 } },
  { a3: 'NLD', name: 'Netherlands', projLabel: 'RD / Transverse Mercator', proj: { kind: 'tmerc', lon0: 5.4 } },
  { a3: 'NOR', name: 'Norway', projLabel: 'UTM 32N', proj: { kind: 'tmerc', lon0: 10 } },
  { a3: 'POL', name: 'Poland', projLabel: 'PUWG 1992 (TM)', proj: { kind: 'tmerc', lon0: 19 } },
  { a3: 'PRT', name: 'Portugal', projLabel: 'UTM 29N', proj: { kind: 'tmerc', lon0: -8 } },
  { a3: 'ROU', name: 'Romania', projLabel: 'UTM 35N', proj: { kind: 'tmerc', lon0: 25 } },
  { a3: 'SVK', name: 'Slovakia', projLabel: 'UTM 34N', proj: { kind: 'tmerc', lon0: 19.5 } },
  { a3: 'SVN', name: 'Slovenia', projLabel: 'UTM 33N', proj: { kind: 'tmerc', lon0: 14.8 } },
  { a3: 'ESP', name: 'Spain', projLabel: 'UTM 30N', proj: { kind: 'tmerc', lon0: -3 } },
  { a3: 'SWE', name: 'Sweden', projLabel: 'UTM 33N', proj: { kind: 'tmerc', lon0: 15 } },
  { a3: 'CHE', name: 'Switzerland', projLabel: 'UTM 32N', proj: { kind: 'tmerc', lon0: 8.2 } },
  { a3: 'GBR', name: 'United Kingdom', projLabel: 'OSGB / National Grid', proj: { kind: 'tmerc', lon0: -2 } },
  { a3: 'USA', name: 'USA', projLabel: 'Albers (CONUS)', proj: { kind: 'aea', lat1: 29.5, lat2: 45.5, lat0: 23, lon0: -96 } },
]
const countryOption = (a3?: string) =>
  MAP_COUNTRY_OPTIONS.find((c) => c.a3 === a3) || MAP_COUNTRY_OPTIONS[0]

// Europe map frame. Every ring is CLIPPED to this box, which (a) trims overseas
// parts of included countries (French Guiana, the Canaries…) and (b) turns
// transcontinental neighbours into a European strip — Russia is cut at the
// eastern edge (≈ Urals) so only European Russia shows. lonMax 60 ≈ the
// Europe/Asia boundary; base countries all sit west of ~40°E so it never affects
// them when Russia/Turkey are off.
const EUROPE_BBOX = { lonMin: -25, lonMax: 60, latMin: 33, latMax: 72 }

// Clip a lon/lat ring to the (convex) bbox via Sutherland–Hodgman. Returns the
// clipped point list (possibly empty if the ring is fully outside).
function clipRingToBox(
  ring: number[][],
  box: { lonMin: number; lonMax: number; latMin: number; latMax: number }
): number[][] {
  const lerpX = (a: number[], b: number[], x: number): number[] => {
    const t = (x - a[0]) / (b[0] - a[0])
    return [x, a[1] + (b[1] - a[1]) * t]
  }
  const lerpY = (a: number[], b: number[], y: number): number[] => {
    const t = (y - a[1]) / (b[1] - a[1])
    return [a[0] + (b[0] - a[0]) * t, y]
  }
  const edge = (
    pts: number[][],
    inside: (p: number[]) => boolean,
    cut: (a: number[], b: number[]) => number[]
  ): number[][] => {
    const out: number[][] = []
    const n = pts.length
    for (let i = 0; i < n; i++) {
      const a = pts[i]
      const b = pts[(i + 1) % n]
      const ina = inside(a)
      const inb = inside(b)
      if (ina) {
        out.push(a)
        if (!inb) out.push(cut(a, b))
      } else if (inb) {
        out.push(cut(a, b))
      }
    }
    return out
  }
  let pts = ring
  pts = edge(pts, (p) => p[0] >= box.lonMin, (a, b) => lerpX(a, b, box.lonMin))
  if (!pts.length) return pts
  pts = edge(pts, (p) => p[0] <= box.lonMax, (a, b) => lerpX(a, b, box.lonMax))
  if (!pts.length) return pts
  pts = edge(pts, (p) => p[1] >= box.latMin, (a, b) => lerpY(a, b, box.latMin))
  if (!pts.length) return pts
  pts = edge(pts, (p) => p[1] <= box.latMax, (a, b) => lerpY(a, b, box.latMax))
  return pts
}

// Which countries the Europe map shows — geographic + political Europe, by the
// Natural Earth (world-atlas) names, lowercased to match normCountry(). EU-27 +
// UK/Norway/Switzerland/Iceland + Western Balkans + Ukraine/Belarus/Moldova +
// Cyprus. Deliberately EXCLUDES transcontinental Russia & Turkey (they'd
// dominate / show mostly Asia) and the Caucasus / Middle East / North Africa.
const EUROPE_COUNTRIES = new Set<string>([
  // EU-27
  'austria', 'belgium', 'bulgaria', 'croatia', 'cyprus', 'czechia', 'denmark',
  'estonia', 'finland', 'france', 'germany', 'greece', 'hungary', 'ireland',
  'italy', 'latvia', 'lithuania', 'luxembourg', 'malta', 'netherlands',
  'poland', 'portugal', 'romania', 'slovakia', 'slovenia', 'spain', 'sweden',
  // non-EU Europe
  'united kingdom', 'norway', 'switzerland', 'iceland', 'liechtenstein',
  'andorra', 'monaco', 'san marino', 'kosovo', 'serbia', 'bosnia and herz.',
  'montenegro', 'macedonia', 'albania', 'ukraine', 'belarus', 'moldova',
  'n. cyprus',
])

// Split a closed lon/lat ring at the antimeridian (±180°). Where an edge jumps
// more than 180° in longitude, we insert a boundary point at ±180° (latitude
// interpolated) so each piece closes ALONG the seam instead of cutting straight
// back across the globe (which produced wedge-shaped holes). The two wrap halves
// (separated only by the ring's start vertex) are merged back together.
function splitRingAtSeam(ring: number[][]): number[][][] {
  if (ring.length < 3) return [ring]
  // drop a duplicated closing vertex if present so edges aren't doubled
  const pts =
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring
  const n = pts.length
  const pieces: number[][][] = []
  let cur: number[][] = []
  for (let i = 0; i < n; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % n]
    cur.push(a)
    if (Math.abs(a[0] - b[0]) > 180) {
      const sideA = a[0] > 0 ? 180 : -180
      const bUn = b[0] + (a[0] - b[0] > 0 ? 360 : -360)
      const denom = bUn - a[0]
      // guard the degenerate case where a vertex sits exactly on the seam
      // (e.g. Fiji is pre-split at ±180° in the source data) → 0/0
      const t =
        Math.abs(denom) < 1e-9
          ? 0
          : Math.max(0, Math.min(1, (sideA - a[0]) / denom))
      const latC = a[1] + (b[1] - a[1]) * t
      cur.push([sideA, latC])
      pieces.push(cur)
      cur = [[-sideA, latC]]
    }
  }
  if (cur.length) pieces.push(cur)
  // merge the trailing wrap-half back onto the leading one (they meet at the
  // ring start, which is not a seam point)
  if (pieces.length > 1) {
    pieces[0] = pieces.pop()!.concat(pieces[0])
  }
  return pieces
}

// common user spellings → Natural Earth country names (world-atlas)
const COUNTRY_ALIASES: Record<string, string> = {
  'united states': 'united states of america',
  usa: 'united states of america',
  us: 'united states of america',
  'russian federation': 'russia',
  uk: 'united kingdom',
  'great britain': 'united kingdom',
  britain: 'united kingdom',
  'czech republic': 'czechia',
  'democratic republic of the congo': 'dem. rep. congo',
  'dr congo': 'dem. rep. congo',
  'republic of the congo': 'congo',
  'bosnia and herzegovina': 'bosnia and herz.',
  bosnia: 'bosnia and herz.',
  'north macedonia': 'macedonia',
  'ivory coast': "côte d'ivoire",
  burma: 'myanmar',
  'south korea': 'south korea',
  'north korea': 'north korea',
  // Our World in Data spellings → Natural Earth (abbreviated) names
  "cote d'ivoire": "côte d'ivoire",
  'democratic republic of congo': 'dem. rep. congo',
  'central african republic': 'central african rep.',
  'dominican republic': 'dominican rep.',
  'equatorial guinea': 'eq. guinea',
  'east timor': 'timor-leste',
  'cape verde': 'cabo verde',
  'south sudan': 's. sudan',
  'western sahara': 'w. sahara',
  swaziland: 'eswatini',
  'solomon islands': 'solomon is.',
  'falkland islands': 'falkland is.',
  'faroe islands': 'faeroe is.',
  'french polynesia': 'fr. polynesia',
  'marshall islands': 'marshall is.',
  'northern mariana islands': 'n. mariana is.',
}

// ── Ländernamen DE↔EN (Jans Zweisprachigkeit 13.10.2026) ────────────────────
// Schlüssel = die Natural-Earth-Namen, wie sie Geometrie und Default-Daten
// tragen; Wert = deutscher Anzeigename. Die Tabelle speist (1) die Anzeige-
// Übersetzung beim Platzieren (uebersetzeLandName) und (2) deutsche ALIASSE
// fürs Matching: normCountry() findet "Deutschland" genauso wie "Germany" —
// damit dürfen Labels editiert und deutsche Excel-Spalten eingefügt werden.
export const LAENDER_DE: Record<string, string> = {
  Afghanistan: 'Afghanistan', Albania: 'Albanien', Algeria: 'Algerien',
  Andorra: 'Andorra', Angola: 'Angola', Argentina: 'Argentinien',
  Armenia: 'Armenien', Australia: 'Australien', Austria: 'Österreich',
  Azerbaijan: 'Aserbaidschan', Bahamas: 'Bahamas', Bahrain: 'Bahrain',
  Bangladesh: 'Bangladesch', Barbados: 'Barbados', Belarus: 'Belarus',
  Belgium: 'Belgien', Belize: 'Belize', Benin: 'Benin', Bhutan: 'Bhutan',
  Bolivia: 'Bolivien', 'Bosnia and Herz.': 'Bosnien und Herz.',
  Botswana: 'Botsuana', Brazil: 'Brasilien', Brunei: 'Brunei',
  Bulgaria: 'Bulgarien', 'Burkina Faso': 'Burkina Faso', Burundi: 'Burundi',
  'Cabo Verde': 'Kap Verde', Cambodia: 'Kambodscha', Cameroon: 'Kamerun',
  Canada: 'Kanada', 'Central African Rep.': 'Zentralafrikanische Rep.',
  Chad: 'Tschad', Chile: 'Chile', China: 'China', Colombia: 'Kolumbien',
  Comoros: 'Komoren', Congo: 'Kongo', 'Costa Rica': 'Costa Rica',
  "Côte d'Ivoire": 'Elfenbeinküste', Croatia: 'Kroatien', Cyprus: 'Zypern',
  Czechia: 'Tschechien', 'Dem. Rep. Congo': 'Dem. Rep. Kongo',
  Denmark: 'Dänemark', Djibouti: 'Dschibuti', Dominica: 'Dominica',
  'Dominican Rep.': 'Dominikanische Rep.', Ecuador: 'Ecuador',
  Egypt: 'Ägypten', 'El Salvador': 'El Salvador',
  'Eq. Guinea': 'Äquatorialguinea', Eritrea: 'Eritrea', Estonia: 'Estland',
  eSwatini: 'Eswatini', Ethiopia: 'Äthiopien', Fiji: 'Fidschi',
  Finland: 'Finnland', France: 'Frankreich', Gabon: 'Gabun',
  Gambia: 'Gambia', Georgia: 'Georgien', Germany: 'Deutschland',
  Ghana: 'Ghana', Greece: 'Griechenland', Greenland: 'Grönland',
  Grenada: 'Grenada', Guatemala: 'Guatemala', Guinea: 'Guinea',
  'Guinea-Bissau': 'Guinea-Bissau', Guyana: 'Guyana', Haiti: 'Haiti',
  Honduras: 'Honduras', Hungary: 'Ungarn', Iceland: 'Island',
  India: 'Indien', Indonesia: 'Indonesien', Iran: 'Iran', Iraq: 'Irak',
  Ireland: 'Irland', Israel: 'Israel', Italy: 'Italien',
  Jamaica: 'Jamaika', Japan: 'Japan', Jordan: 'Jordanien',
  Kazakhstan: 'Kasachstan', Kenya: 'Kenia', Kiribati: 'Kiribati',
  Kosovo: 'Kosovo', Kuwait: 'Kuwait', Kyrgyzstan: 'Kirgisistan',
  Laos: 'Laos', Latvia: 'Lettland', Lebanon: 'Libanon',
  Lesotho: 'Lesotho', Liberia: 'Liberia', Libya: 'Libyen',
  Liechtenstein: 'Liechtenstein', Lithuania: 'Litauen',
  Luxembourg: 'Luxemburg', Macedonia: 'Nordmazedonien',
  Madagascar: 'Madagaskar', Malawi: 'Malawi', Malaysia: 'Malaysia',
  Maldives: 'Malediven', Mali: 'Mali', Malta: 'Malta',
  Mauritania: 'Mauretanien', Mauritius: 'Mauritius', Mexico: 'Mexiko',
  Moldova: 'Moldau', Monaco: 'Monaco', Mongolia: 'Mongolei',
  Montenegro: 'Montenegro', Morocco: 'Marokko', Mozambique: 'Mosambik',
  Myanmar: 'Myanmar', 'N. Cyprus': 'Nordzypern', Namibia: 'Namibia',
  Nauru: 'Nauru', Nepal: 'Nepal', Netherlands: 'Niederlande',
  'New Zealand': 'Neuseeland', Nicaragua: 'Nicaragua', Niger: 'Niger',
  Nigeria: 'Nigeria', 'North Korea': 'Nordkorea', Norway: 'Norwegen',
  Oman: 'Oman', Pakistan: 'Pakistan', Palestine: 'Palästina',
  Panama: 'Panama', 'Papua New Guinea': 'Papua-Neuguinea',
  Paraguay: 'Paraguay', Peru: 'Peru', Philippines: 'Philippinen',
  Poland: 'Polen', Portugal: 'Portugal', 'Puerto Rico': 'Puerto Rico',
  Qatar: 'Katar', Romania: 'Rumänien', Russia: 'Russland',
  Rwanda: 'Ruanda', 'S. Sudan': 'Südsudan', 'Saint Lucia': 'St. Lucia',
  Samoa: 'Samoa', 'San Marino': 'San Marino',
  'São Tomé and Principe': 'São Tomé und Príncipe',
  'Saudi Arabia': 'Saudi-Arabien', Senegal: 'Senegal', Serbia: 'Serbien',
  Seychelles: 'Seychellen', 'Sierra Leone': 'Sierra Leone',
  Singapore: 'Singapur', Slovakia: 'Slowakei', Slovenia: 'Slowenien',
  'Solomon Is.': 'Salomonen', Somalia: 'Somalia',
  'South Africa': 'Südafrika', 'South Korea': 'Südkorea', Spain: 'Spanien',
  'Sri Lanka': 'Sri Lanka', 'St. Kitts and Nevis': 'St. Kitts und Nevis',
  Sudan: 'Sudan', Suriname: 'Suriname', Sweden: 'Schweden',
  Switzerland: 'Schweiz', Syria: 'Syrien', Taiwan: 'Taiwan',
  Tajikistan: 'Tadschikistan', Tanzania: 'Tansania', Thailand: 'Thailand',
  'Timor-Leste': 'Osttimor', Togo: 'Togo', Tonga: 'Tonga',
  'Trinidad and Tobago': 'Trinidad und Tobago', Tunisia: 'Tunesien',
  Turkey: 'Türkei', Turkmenistan: 'Turkmenistan', Uganda: 'Uganda',
  Ukraine: 'Ukraine', 'United Arab Emirates': 'Vereinigte Arabische Emirate',
  'United Kingdom': 'Vereinigtes Königreich',
  'United States of America': 'USA', Uruguay: 'Uruguay',
  Uzbekistan: 'Usbekistan', Vanuatu: 'Vanuatu', Venezuela: 'Venezuela',
  Vietnam: 'Vietnam', 'W. Sahara': 'Westsahara', Yemen: 'Jemen',
  Zambia: 'Sambia', Zimbabwe: 'Simbabwe',
}

// Geläufige deutsche Zweitformen (klein geschrieben) → NE-Name klein.
const DE_SYNONYME: Record<string, string> = {
  'bosnien und herzegowina': 'bosnia and herz.',
  'tschechische republik': 'czechia',
  'grossbritannien': 'united kingdom',
  'großbritannien': 'united kingdom',
  'vereinigte staaten': 'united states of america',
  'vereinigte staaten von amerika': 'united states of america',
  'republik moldau': 'moldova',
  'moldawien': 'moldova',
  'weissrussland': 'belarus',
  'weißrussland': 'belarus',
  'sudkorea': 'south korea',
  'nordmakedonien': 'macedonia',
  'demokratische republik kongo': 'dem. rep. congo',
  'zentralafrikanische republik': 'central african rep.',
  'dominikanische republik': 'dominican rep.',
  'salomoninseln': 'solomon is.',
  'burma': 'myanmar',
}

// Nachschlagetabellen in beide Richtungen (klein geschrieben).
const EN_ZU_DE: Record<string, string> = {}
const DE_ZU_EN: Record<string, string> = {}
for (const [en, de] of Object.entries(LAENDER_DE)) {
  EN_ZU_DE[en.toLowerCase()] = de
  DE_ZU_EN[de.toLowerCase()] = en
}

// ── Regionsnamen DE↔EN (Jan 22.10.2026: „wenn ich mich als EN angemeldet
// habe — könnten die Orte auf Englisch sein?“) ─────────────────────────────
// Schlüssel = Natural-Earth-Name der Region (wie Geometrie und Default-Daten
// ihn tragen), Wert = [deutsch, englisch]. Nur Regionen, deren Namen sich
// unterscheiden; beide Formen gelten beim Zuordnen (normCountry), angezeigt
// wird die Form der Editor-Sprache (uebersetzeLandName).
export const REGIONEN_NAMEN: Record<string, [string, string]> = {
  // Deutschland
  Bayern: ['Bayern', 'Bavaria'],
  Niedersachsen: ['Niedersachsen', 'Lower Saxony'],
  'Nordrhein-Westfalen': ['Nordrhein-Westfalen', 'North Rhine-Westphalia'],
  Hessen: ['Hessen', 'Hesse'],
  'Rheinland-Pfalz': ['Rheinland-Pfalz', 'Rhineland-Palatinate'],
  Sachsen: ['Sachsen', 'Saxony'],
  'Sachsen-Anhalt': ['Sachsen-Anhalt', 'Saxony-Anhalt'],
  Thüringen: ['Thüringen', 'Thuringia'],
  'Mecklenburg-Vorpommern': ['Mecklenburg-Vorpommern', 'Mecklenburg-Western Pomerania'],
  // Österreich
  Niederösterreich: ['Niederösterreich', 'Lower Austria'],
  Oberösterreich: ['Oberösterreich', 'Upper Austria'],
  Tirol: ['Tirol', 'Tyrol'],
  Kärnten: ['Kärnten', 'Carinthia'],
  Steiermark: ['Steiermark', 'Styria'],
  Wien: ['Wien', 'Vienna'],
  // Schweiz (die NE-Namen sind gemischt deutsch/französisch/englisch)
  Genève: ['Genf', 'Geneva'],
  Lucerne: ['Luzern', 'Lucerne'],
  Valais: ['Wallis', 'Valais'],
  Ticino: ['Tessin', 'Ticino'],
  Vaud: ['Waadt', 'Vaud'],
  Neuchâtel: ['Neuenburg', 'Neuchâtel'],
  Fribourg: ['Freiburg', 'Fribourg'],
  Graubünden: ['Graubünden', 'Grisons'],
  Zürich: ['Zürich', 'Zurich'],
  'Sankt Gallen': ['St. Gallen', 'St. Gallen'],
  'Basel-Stadt': ['Basel-Stadt', 'Basel-City'],
  'Basel-Landschaft': ['Basel-Landschaft', 'Basel-Country'],
}
// jede Form (NE, deutsch, englisch; klein geschrieben) → Eintrag
const REGION_ZU_NE: Record<string, string> = {}
for (const [ne, formen] of Object.entries(REGIONEN_NAMEN))
  for (const f of [ne, ...formen]) REGION_ZU_NE[f.toLowerCase()] = ne

/** Anzeigename eines Landes in der gewünschten Sprache; unbekannte Namen
 *  bleiben unverändert (auch Nicht-Länder wie Timeline-Daten).
 *  `kartenLand`: A3-Code, wenn das Label eine Region einer Länderkarte ist. */
export function uebersetzeLandName(label: string, sprache?: string, kartenLand?: string): string {
  const n = (label || '').trim().toLowerCase().replace(/\s+/g, ' ')
  // Regionen einer Länderkarte (Bundesländer, Kantone …)
  const region = REGION_ZU_NE[n]
  if (region) return REGIONEN_NAMEN[region][sprache?.toLowerCase().startsWith('de') ? 0 : 1]
  // US-Bundesstaaten heißen auch auf Deutsch englisch — der Staat „Georgia“
  // ist nicht das Land „Georgien“ (Jan 22.10.2026); ein schon übersetztes
  // „Georgien“ wird wieder zu „Georgia“.
  if (kartenLand === 'USA') return DE_ZU_EN[n] ?? label
  if (sprache?.toLowerCase().startsWith('de')) return EN_ZU_DE[n] ?? label
  // nach Englisch: deutscher Name oder deutsche Zweitform → NE-Name
  const ne = DE_ZU_EN[n]
  if (ne) return ne
  const syn = DE_SYNONYME[n]
  if (syn) {
    const exakt = Object.keys(LAENDER_DE).find((k) => k.toLowerCase() === syn)
    if (exakt) return exakt
  }
  return label
}

/** Kanonischer (kleingeschriebener) Geometrie-Schlüssel eines Landesnamens —
 *  versteht Englisch, die Alias-Liste und die deutschen Namen/Zweitformen. */
export function normalisiereLand(s: string): string {
  return normCountry(s)
}

// Breite eines Namens auf der Karte (Kollisionsprüfung, Jan 22.10.2026):
// gemessen mit der echten Schrift, ohne Browser grob geschätzt.
let namenMessKontext: CanvasRenderingContext2D | null | undefined
function namenBreite(text: string, fs: number, schrift: string, gewicht: number | string): number {
  if (namenMessKontext === undefined) {
    try {
      namenMessKontext =
        typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null
    } catch {
      namenMessKontext = null
    }
  }
  if (namenMessKontext) {
    namenMessKontext.font = `${gewicht} ${fs}px ${schrift}`
    const b = namenMessKontext.measureText(text).width
    if (b > 0) return b
  }
  return text.length * fs * 0.56
}

function normCountry(s: string): string {
  const n = (s || '').trim().toLowerCase().replace(/\s+/g, ' ')
  // Regionsnamen in jeder Sprache → NE-Name der Region
  const region = REGION_ZU_NE[n]
  if (region) return region.toLowerCase()
  // Deutsch zuerst in den NE-Namen übersetzen, dann die alte Alias-Liste
  const de = DE_ZU_EN[n] ?? (DE_SYNONYME[n] ? DE_SYNONYME[n] : undefined)
  const k = de ? de.toLowerCase() : n
  return COUNTRY_ALIASES[k] || k
}

// Default dataset for a new Country Map (Germany): population by Bundesland in
// millions. Labels use the Natural Earth admin-1 names so they match directly.
export const INFOGRAPHIC_COUNTRY_DEFAULT_DATA: InfographicDataPoint[] = [
  { label: 'Nordrhein-Westfalen', value: 17.9 },
  { label: 'Bayern', value: 13.1 },
  { label: 'Baden-Württemberg', value: 11.1 },
  { label: 'Niedersachsen', value: 8.0 },
  { label: 'Hessen', value: 6.3 },
  { label: 'Rheinland-Pfalz', value: 4.1 },
  { label: 'Sachsen', value: 4.0 },
  { label: 'Berlin', value: 3.7 },
  { label: 'Schleswig-Holstein', value: 2.9 },
  { label: 'Brandenburg', value: 2.5 },
  { label: 'Sachsen-Anhalt', value: 2.2 },
  { label: 'Thüringen', value: 2.1 },
  { label: 'Hamburg', value: 1.9 },
  { label: 'Mecklenburg-Vorpommern', value: 1.6 },
  { label: 'Saarland', value: 1.0 },
  { label: 'Bremen', value: 0.68 },
]

// Default dataset for a new World Map: fossil-fuel subsidies per capita 2021
// (USD), Our World in Data (IEA/OECD/IMF via UN SDG). Labels use the Natural
// Earth names so they match the geometry directly. Sorted alphabetically.
export const INFOGRAPHIC_MAP_DEFAULT_DATA: InfographicDataPoint[] = [
  { label: 'Afghanistan', value: 1.55 },
  { label: 'Algeria', value: 533.69 },
  { label: 'Angola', value: 94.68 },
  { label: 'Argentina', value: 225.23 },
  { label: 'Armenia', value: 0.27 },
  { label: 'Australia', value: 359.5 },
  { label: 'Austria', value: 108.81 },
  { label: 'Azerbaijan', value: 15.43 },
  { label: 'Bahamas', value: 155.37 },
  { label: 'Bahrain', value: 1609.91 },
  { label: 'Bangladesh', value: 44.36 },
  { label: 'Barbados', value: 0 },
  { label: 'Belarus', value: 12.19 },
  { label: 'Belgium', value: 323.03 },
  { label: 'Belize', value: 0 },
  { label: 'Benin', value: 10.46 },
  { label: 'Bhutan', value: 1 },
  { label: 'Bolivia', value: 103.4 },
  { label: 'Bosnia and Herz.', value: 4.64 },
  { label: 'Botswana', value: 0 },
  { label: 'Brazil', value: 33.54 },
  { label: 'Brunei', value: 655.96 },
  { label: 'Bulgaria', value: 353.33 },
  { label: 'Burkina Faso', value: 0.69 },
  { label: 'Burundi', value: 0 },
  { label: 'Cabo Verde', value: 0 },
  { label: 'Cambodia', value: 1.58 },
  { label: 'Cameroon', value: 2.39 },
  { label: 'Canada', value: 83.6 },
  { label: 'Central African Rep.', value: 0.94 },
  { label: 'Chad', value: 2.24 },
  { label: 'Chile', value: 99.33 },
  { label: 'China', value: 17.37 },
  { label: 'Colombia', value: 79.28 },
  { label: 'Comoros', value: 0 },
  { label: 'Congo', value: 0 },
  { label: 'Costa Rica', value: 16.71 },
  { label: "Côte d'Ivoire", value: 0 },
  { label: 'Croatia', value: 84.41 },
  { label: 'Cyprus', value: 52.23 },
  { label: 'Czechia', value: 38.76 },
  { label: 'Dem. Rep. Congo', value: 0 },
  { label: 'Denmark', value: 259 },
  { label: 'Djibouti', value: 52.03 },
  { label: 'Dominica', value: 0 },
  { label: 'Dominican Rep.', value: 0.06 },
  { label: 'Ecuador', value: 85.08 },
  { label: 'Egypt', value: 247.5 },
  { label: 'El Salvador', value: 0 },
  { label: 'Eq. Guinea', value: 2.18 },
  { label: 'Eritrea', value: 0 },
  { label: 'Estonia', value: 31.61 },
  { label: 'eSwatini', value: 0 },
  { label: 'Ethiopia', value: 4.83 },
  { label: 'Fiji', value: 3.46 },
  { label: 'Finland', value: 272.82 },
  { label: 'France', value: 154.14 },
  { label: 'Gabon', value: 20.87 },
  { label: 'Gambia', value: 0 },
  { label: 'Georgia', value: 5.17 },
  { label: 'Germany', value: 94.27 },
  { label: 'Ghana', value: 16.59 },
  { label: 'Greece', value: 203.41 },
  { label: 'Grenada', value: 0 },
  { label: 'Guatemala', value: 0.03 },
  { label: 'Guinea', value: 0.03 },
  { label: 'Guinea-Bissau', value: 0.21 },
  { label: 'Guyana', value: 0.06 },
  { label: 'Haiti', value: 2.77 },
  { label: 'Honduras', value: 5.07 },
  { label: 'Hungary', value: 24.58 },
  { label: 'Iceland', value: 0 },
  { label: 'India', value: 3.01 },
  { label: 'Indonesia', value: 54.53 },
  { label: 'Iran', value: 670.2 },
  { label: 'Iraq', value: 248.66 },
  { label: 'Ireland', value: 497.42 },
  { label: 'Israel', value: 134.86 },
  { label: 'Italy', value: 209.6 },
  { label: 'Jamaica', value: 0.13 },
  { label: 'Japan', value: 26.1 },
  { label: 'Jordan', value: 11.71 },
  { label: 'Kazakhstan', value: 619.7 },
  { label: 'Kenya', value: 0 },
  { label: 'Kiribati', value: 0 },
  { label: 'Kuwait', value: 1949.82 },
  { label: 'Kyrgyzstan', value: 53.31 },
  { label: 'Latvia', value: 100.65 },
  { label: 'Lebanon', value: 614.76 },
  { label: 'Lesotho', value: 0.9 },
  { label: 'Liberia', value: 0 },
  { label: 'Libya', value: 849.45 },
  { label: 'Lithuania', value: 194.76 },
  { label: 'Luxembourg', value: 0.23 },
  { label: 'Macedonia', value: 37.78 },
  { label: 'Madagascar', value: 4.1 },
  { label: 'Malawi', value: 0 },
  { label: 'Malaysia', value: 90.37 },
  { label: 'Maldives', value: 107.64 },
  { label: 'Mali', value: 0.91 },
  { label: 'Malta', value: 0 },
  { label: 'Mauritania', value: 47.39 },
  { label: 'Mauritius', value: 35.36 },
  { label: 'Mexico', value: 185 },
  { label: 'Moldova', value: 24.19 },
  { label: 'Mongolia', value: 5.7 },
  { label: 'Montenegro', value: 0 },
  { label: 'Morocco', value: 2.4 },
  { label: 'Mozambique', value: 0 },
  { label: 'Myanmar', value: 11.28 },
  { label: 'Namibia', value: 0 },
  { label: 'Nauru', value: 0.82 },
  { label: 'Nepal', value: 0.29 },
  { label: 'Netherlands', value: 80.77 },
  { label: 'New Zealand', value: 3.7 },
  { label: 'Nicaragua', value: 3.28 },
  { label: 'Niger', value: 4.84 },
  { label: 'Nigeria', value: 24.73 },
  { label: 'Oman', value: 35.92 },
  { label: 'Pakistan', value: 35.69 },
  { label: 'Palestine', value: 141.53 },
  { label: 'Panama', value: 0 },
  { label: 'Papua New Guinea', value: 35.27 },
  { label: 'Paraguay', value: 11.27 },
  { label: 'Peru', value: 4.65 },
  { label: 'Philippines', value: 0.31 },
  { label: 'Poland', value: 30.22 },
  { label: 'Portugal', value: 62.17 },
  { label: 'Puerto Rico', value: 0 },
  { label: 'Qatar', value: 2535.61 },
  { label: 'Romania', value: 39.29 },
  { label: 'Russia', value: 199.65 },
  { label: 'Rwanda', value: 2.96 },
  { label: 'S. Sudan', value: 0 },
  { label: 'Saint Lucia', value: 0 },
  { label: 'Samoa', value: 0 },
  { label: 'São Tomé and Principe', value: 12.22 },
  { label: 'Saudi Arabia', value: 1138.75 },
  { label: 'Senegal', value: 0.08 },
  { label: 'Serbia', value: 42.29 },
  { label: 'Seychelles', value: 0.21 },
  { label: 'Sierra Leone', value: 0.61 },
  { label: 'Singapore', value: 0 },
  { label: 'Slovakia', value: 170.68 },
  { label: 'Slovenia', value: 61.34 },
  { label: 'Solomon Is.', value: 0 },
  { label: 'Somalia', value: 0.01 },
  { label: 'South Africa', value: 60.8 },
  { label: 'South Korea', value: 28.77 },
  { label: 'Spain', value: 35.46 },
  { label: 'Sri Lanka', value: 49.38 },
  { label: 'St. Kitts and Nevis', value: 0 },
  { label: 'Sudan', value: 47.64 },
  { label: 'Suriname', value: 323.83 },
  { label: 'Sweden', value: 255.82 },
  { label: 'Switzerland', value: 190.64 },
  { label: 'Syria', value: 0 },
  { label: 'Tajikistan', value: 69.23 },
  { label: 'Tanzania', value: 0 },
  { label: 'Thailand', value: 33.26 },
  { label: 'Timor-Leste', value: 45.28 },
  { label: 'Togo', value: 7.75 },
  { label: 'Tonga', value: 0 },
  { label: 'Trinidad and Tobago', value: 602.53 },
  { label: 'Tunisia', value: 28.62 },
  { label: 'Turkey', value: 47.97 },
  { label: 'Turkmenistan', value: 1067.28 },
  { label: 'Uganda', value: 0.01 },
  { label: 'Ukraine', value: 37.52 },
  { label: 'United Arab Emirates', value: 1865.32 },
  { label: 'United Kingdom', value: 38.04 },
  { label: 'United States of America', value: 28.16 },
  { label: 'Uruguay', value: 0 },
  { label: 'Uzbekistan', value: 381.93 },
  { label: 'Vanuatu', value: 0 },
  { label: 'Venezuela', value: 744.74 },
  { label: 'Vietnam', value: 46.61 },
  { label: 'Yemen', value: 0.01 },
  { label: 'Zambia', value: 9.99 },
  { label: 'Zimbabwe', value: 0 },
]

// The European subset of the world default data (keeping the real values), used
// to seed the Europe map so its data list only lists countries the map actually
// shows — not all ~180 world countries. Honours the Turkey / Russia opt-ins.
export function europeMapDefaultData(
  showTurkey = false,
  showRussia = false
): InfographicDataPoint[] {
  const set = europaLaender(showTurkey, showRussia)
  const mitWerten = INFOGRAPHIC_MAP_DEFAULT_DATA.filter((d) =>
    set.has(normCountry((d.label as string) || ''))
  ).map((d) => ({ ...d }))
  // ALLE gezeichneten Länder (Jans Fund 17.10.2026: Norwegen, Albanien,
  // Kosovo fehlten — die Beispielwerte kennen sie nicht), alphabetisch
  return europaZeilenErgaenzt(mitWerten, showTurkey, showRussia).sort((a, b) =>
    String(a.label).localeCompare(String(b.label))
  )
}

function europaLaender(showTurkey = false, showRussia = false): Set<string> {
  const set = new Set(EUROPE_COUNTRIES)
  if (showTurkey) set.add('turkey')
  if (showRussia) set.add('russia')
  return set
}

/**
 * Europa-Karte: fehlende gezeichnete Länder als Zeilen anhängen (Wert 0 =
 * „keine Daten"). Bestehende Zeilen bleiben unverändert an ihrem Platz —
 * die Reihenfolge trägt in manchen Modi die Farben.
 */
export function europaZeilenErgaenzt(
  data: InfographicDataPoint[],
  showTurkey = false,
  showRussia = false
): InfographicDataPoint[] {
  const set = europaLaender(showTurkey, showRussia)
  const da = new Set(data.map((d) => normCountry((d.label as string) || '')))
  const fehlend = getWorldFeatures('110m')
    .filter((f) => f.name && set.has(normCountry(f.name)) && !da.has(normCountry(f.name)))
    .map((f) => ({ label: f.name, value: 0 }))
  return fehlend.length ? [...data, ...fehlend] : data
}

/** Zeilen einer Karte für die Länder-Auswahl — bei Europa vollständig. */
export function kartenZeilenVollstaendig(content: InfographicContent): InfographicDataPoint[] {
  const data = content.data ?? []
  return content.chartType === 'mapEurope'
    ? europaZeilenErgaenzt(data, !!content.mapEuropeShowTurkey, !!content.mapEuropeShowRussia)
    : data
}

// normalised set of the default map countries — used to decide whether the
// current data is already country-like (so switching to map won't clobber it)
const MAP_DEFAULT_NAMESET = new Set(
  INFOGRAPHIC_MAP_DEFAULT_DATA.map((d) => normCountry(d.label as string))
)
export function dataLooksLikeCountries(
  data: ReadonlyArray<{ label?: string }>
): boolean {
  return data.some((d) => MAP_DEFAULT_NAMESET.has(normCountry(d.label || '')))
}

// The generic label/value sample that a freshly added infographic ships with
// (kept in sync with infographicContent() in LayerContent.ts). Used both as the
// "Use sample data" preset for the series-style charts and as the baseline for
// deciding whether the user has actually entered their own data.
export const INFOGRAPHIC_SAMPLE_SERIES: InfographicDataPoint[] = [
  { label: '2022', value: 42 },
  { label: '2023', value: 68 },
  { label: '2024', value: 55 },
  { label: '2025', value: 100 },
  { label: '2026', value: 34 },
]

// Two data "shapes": a map needs country names, everything else is a generic
// label/value series. Switching *within* a shape keeps the data sensible;
// crossing shapes (series <-> map) is what turns data into a messy chart.
// Wortwolke (17.10.2026): eigene Form „words“ — Wörter sind keine Reihe;
// Wechsel mit eigenen Wörtern fragt nach, unberührt kommt das Wort-Beispiel.
export type InfographicDataShape = 'map' | 'series' | 'words' | 'table' | 'steps'
// world / Europe / country maps all share the "map" data shape + render path
export const isMapChart = (t: InfographicChartType): boolean =>
  t === 'map' || t === 'mapEurope' || t === 'mapCountry'
export const infographicDataShape = (
  t: InfographicChartType
): InfographicDataShape =>
  isMapChart(t) ? 'map' : t === 'wordcloud' ? 'words' : t === 'table' ? 'table' : t === 'steps' ? 'steps' : 'series'

// "custom" = the user has edited the data away from the preset its current shape
// ships with. A fresh layer or seeded demo data is NOT custom, so switching
// types then never nags. Compares only label/value (ignores styling fields).
export function infographicDataIsCustom(content: InfographicContent): boolean {
  const data = content.data ?? []
  const form = infographicDataShape(content.chartType)
  // Tabelle: eigene Zeilen statt data — „eigen", sobald sie vom Beispiel abweichen
  if (form === 'table') {
    return JSON.stringify(content.tableRows ?? INFOGRAPHIC_TABLE_SAMPLE) !== JSON.stringify(INFOGRAPHIC_TABLE_SAMPLE)
  }
  const preset =
    form === 'map'
      ? INFOGRAPHIC_MAP_DEFAULT_DATA
      : form === 'words'
        ? INFOGRAPHIC_WORDCLOUD_SAMPLE
        : form === 'steps'
          ? INFOGRAPHIC_STEPS_SAMPLE
          : INFOGRAPHIC_SAMPLE_SERIES
  if (data.length !== preset.length) return true
  return data.some(
    (d, i) => d.label !== preset[i].label || d.value !== preset[i].value
  )
}

// Sequential colour ramps for value-based choropleths (light → dark).
export const INFOGRAPHIC_MAP_RAMPS: { key: string; label: string; stops: string[] }[] = [
  { key: 'orRd', label: 'Orange → Red', stops: ['#fff7ec', '#fdd49e', '#fdae6b', '#fb8d3d', '#e6550d', '#a63603'] },
  { key: 'ylOrRd', label: 'Yellow → Red', stops: ['#ffffb2', '#fed976', '#feb24c', '#fd8d3c', '#f03b20', '#bd0026'] },
  { key: 'blues', label: 'Blues', stops: ['#eff3ff', '#c6dbef', '#9ecae1', '#6baed6', '#3182bd', '#08519c'] },
  { key: 'greens', label: 'Greens', stops: ['#edf8e9', '#c7e9c0', '#a1d99b', '#74c476', '#31a354', '#006d2c'] },
  { key: 'purples', label: 'Purples', stops: ['#f2f0f7', '#dadaeb', '#bcbddc', '#9e9ac8', '#756bb1', '#54278f'] },
  { key: 'viridis', label: 'Viridis', stops: ['#fde725', '#7ad151', '#22a884', '#2a788e', '#414487', '#440154'] },
  { key: 'brand', label: 'Scrolly Brand', stops: ['#FFC800', '#FF9600', '#F02873', '#1996D0'] },
]
function rampStops(content: InfographicContent): string[] {
  if (content.mapColorRamp === 'custom') {
    const c = (content.mapColorRampCustom || []).filter(Boolean)
    if (c.length >= 2) return c
    if (c.length === 1) return [c[0], c[0]]
  }
  return (
    INFOGRAPHIC_MAP_RAMPS.find((r) => r.key === content.mapColorRamp) ||
    INFOGRAPHIC_MAP_RAMPS[0]
  ).stops
}
// discrete colour for bin `i` of `n` bins, sampled across the ramp
function rampBinColor(stops: string[], i: number, n: number): string {
  if (n <= 1) return stops[stops.length - 1]
  const t = i / (n - 1)
  const x = t * (stops.length - 1)
  const lo = Math.floor(x)
  const hi = Math.min(stops.length - 1, lo + 1)
  return lerpHex(stops[lo], stops[hi], x - lo)
}
// auto "nice" 1-2-5 thresholds strictly below the data max (≤7 edges)
function niceThresholds(values: number[]): number[] {
  const max = Math.max(0, ...values.filter((v) => Number.isFinite(v)))
  if (max <= 0) return [1]
  const out: number[] = []
  for (let mag = -3; mag <= 12; mag++) {
    for (const m of [1, 2, 5]) {
      const v = m * Math.pow(10, mag)
      if (v > 0 && v < max) out.push(Number(v.toPrecision(3)))
    }
  }
  // keep the largest ≤7 thresholds (coarser low end if there are too many)
  return out.slice(Math.max(0, out.length - 7))
}
// bin index for a value given sorted thresholds (0..thresholds.length)
function valueBin(v: number, thresholds: number[]): number {
  let i = 0
  while (i < thresholds.length && v >= thresholds[i]) i++
  return i
}

// Colour legend drawn below the world map. Returns the SVG (in local coords,
// 0..availW wide, starting at y=0) plus its total height so the caller can
// reserve room. Three modes: a stepped value scale (OWID-style, when colouring
// by value), a continuous gradient bar with min/max labels, or one swatch +
// label per data row (wrapped + centred).
/**
 * Kategorien einer manuell gefärbten Karte (Jan 17.10.2026): je Farbe ein
 * Eintrag, in der Reihenfolge des ersten Auftretens. Name aus
 * mapCategoryLabels (Schlüssel = Farbe klein), sonst die Regionen dieser
 * Farbe („Bayern, Sachsen +3").
 */
export function kartenKategorien(
  content: InfographicContent
): Array<{ schluessel: string; farbe: string; regionen: string[]; name: string }> {
  const palette = content.palette ?? []
  const gruppen = new Map<string, { farbe: string; regionen: string[] }>()
  ;(content.data ?? []).forEach((d, i) => {
    if (!d.label) return
    const farbe = d.color || palette[i % (palette.length || 1)] || '#6c8cff'
    const schluessel = farbe.trim().toLowerCase()
    const g = gruppen.get(schluessel) ?? { farbe, regionen: [] }
    g.regionen.push(d.label)
    gruppen.set(schluessel, g)
  })
  return [...gruppen.entries()].map(([schluessel, g]) => {
    const eigen = content.mapCategoryLabels?.[schluessel]?.trim()
    const standard =
      g.regionen.length <= 2
        ? g.regionen.join(', ')
        : `${g.regionen.slice(0, 2).join(', ')} +${g.regionen.length - 2}`
    return { schluessel, farbe: g.farbe, regionen: g.regionen, name: eigen || standard }
  })
}

function mapLegendBlock(
  content: InfographicContent,
  availW: number,
  ff: string,
  hasNoData: boolean,
  /** kleinere Schrift, wenn die Legende sonst zu viel Platz nähme (s. mapSvg) */
  fsUeberschrieben?: number
): { svg: string; height: number } {
  const data = content.data ?? []
  const labelFill = content.labelColor || content.textColor || '#ffffff'
  const fs = fsUeberschrieben ?? content.mapLegendFontSize ?? Math.max(14, content.width * 0.013)
  const palette = content.palette ?? []
  const colourOf = (d: InfographicDataPoint, i: number) =>
    d.color || palette[i % (palette.length || 1)] || '#6c8cff'

  // value mode → OWID-style stepped scale (segments + threshold labels).
  // 0 (and empty, which the data table stores as 0) counts as "no data".
  const values = data
    .map((d) => Number(d.value))
    .filter((v) => Number.isFinite(v) && v !== 0)
  if (content.mapColorMode !== 'manual' && values.length) {
    const thresholds =
      content.mapThresholds && content.mapThresholds.length
        ? [...content.mapThresholds].filter((t) => Number.isFinite(t)).sort((a, b) => a - b)
        : niceThresholds(values)
    const nBins = thresholds.length + 1
    const stops = rampStops(content)
    // only reserve room for the "No data" swatch when the map actually has
    // regions without a value; otherwise the scale uses the full width
    // nullish (not ||) so an explicitly empty label renders no text, while an
    // unset label still shows the "No data" default
    const ndLabel = content.mapNoDataLabel ?? 'No data'
    const ndW = hasNoData ? fs * 1.1 + 6 + ndLabel.length * fs * 0.5 : 0
    // generous gap so the "No data" swatch reads as separate from the scale
    const gap = hasNoData ? Math.round(fs * 1.8) : 0
    const barW = Math.min(availW - ndW - gap, 720)
    const segW = barW / nBins
    const barH = Math.max(12, fs * 0.8)
    const groupW = barW + gap + ndW
    const startX = Math.max(0, (availW - groupW) / 2)
    // value scale sits on the left; "No data" moves to the right of it
    const barX = startX
    const ndX = startX + barW + gap
    const labelY = barH + fs + 4
    let svg = ''
    // stepped segments
    for (let i = 0; i < nBins; i++) {
      svg += `<rect x="${(barX + i * segW).toFixed(1)}" y="0" width="${(segW + 0.5).toFixed(
        1
      )}" height="${barH}" fill="${rampBinColor(stops, i, nBins)}"/>`
    }
    // no-data swatch + label, set apart on the right — only when at least one
    // region has no value (all regions coloured → no swatch)
    if (hasNoData) {
      svg +=
        `<rect x="${ndX.toFixed(1)}" y="0" width="${(fs * 1.1).toFixed(
          1
        )}" height="${barH}" rx="2" fill="${content.mapNoDataColor || '#cfd2d8'}"/>` +
        `<text x="${(ndX + fs * 1.1 + 6).toFixed(1)}" y="${(barH * 0.5 + fs * 0.35).toFixed(
          1
        )}" fill="${labelFill}" font-size="${fs}" font-family="${ff}">${esc(ndLabel)}</text>`
    }
    // threshold labels at the internal segment boundaries
    thresholds.forEach((t, i) => {
      const x = barX + (i + 1) * segW
      svg += `<text x="${x.toFixed(1)}" y="${labelY}" fill="${labelFill}" font-size="${(
        fs * 0.92
      ).toFixed(1)}" font-family="${ff}" text-anchor="middle">${esc(
        fmtVal(content, t)
      )}</text>`
    })
    return { svg, height: labelY + 4 }
  }

  // manual mode is about per-region colours, so its natural legend is the
  // categorical swatch+label list; only fall back to a value gradient when the
  // user explicitly picks it. (value mode never reaches here — it returns above.)
  const legendType =
    content.mapLegendType ||
    (content.mapColorMode === 'manual' ? 'steps' : 'gradient')
  if (legendType === 'gradient') {
    const items = data
      .map((d, i) => ({ c: colourOf(d, i), v: Number(d.value) }))
      .filter((it) => Number.isFinite(it.v))
      .sort((a, b) => a.v - b.v)
    let colours = items.map((it) => it.c)
    if (colours.length === 0) colours = ['#6c8cff', '#ff4d9d']
    if (colours.length === 1) colours = [colours[0], colours[0]]
    const stops = colours
      .map(
        (c, i) =>
          `<stop offset="${((i / (colours.length - 1)) * 100).toFixed(
            1
          )}%" stop-color="${c}"/>`
      )
      .join('')
    const barW = Math.min(availW * 0.6, 640)
    const barH = Math.max(12, fs * 0.7)
    const barX = (availW - barW) / 2
    const minLabel =
      content.mapLegendMinLabel ||
      (items.length ? fmtVal(content, items[0].v) : '0')
    const maxLabel =
      content.mapLegendMaxLabel ||
      (items.length ? fmtVal(content, items[items.length - 1].v) : '100%')
    const labelY = barH + fs + 4
    const svg =
      `<defs><linearGradient id="mapLegGrad" x1="0" y1="0" x2="1" y2="0">${stops}</linearGradient></defs>` +
      `<rect x="${barX.toFixed(1)}" y="0" width="${barW.toFixed(
        1
      )}" height="${barH}" rx="${(barH / 2).toFixed(
        1
      )}" fill="url(#mapLegGrad)"/>` +
      `<text x="${barX.toFixed(1)}" y="${labelY}" fill="${labelFill}" ` +
      `font-size="${fs}" font-family="${ff}" text-anchor="start">${esc(
        minLabel
      )}</text>` +
      `<text x="${(barX + barW).toFixed(
        1
      )}" y="${labelY}" fill="${labelFill}" font-size="${fs}" ` +
      `font-family="${ff}" text-anchor="end">${esc(maxLabel)}</text>`
    return { svg, height: labelY + 4 }
  }

  // steps: swatch + label per row, centred, wrapping across rows.
  // MANUELL je FARBE statt je Region (Jan 17.10.2026): gleiche Farben sind
  // EIN Eintrag (eine Kategorie) mit eigenem Namen (mapCategoryLabels, Schlüssel
  // = Farbe klein geschrieben); ohne Namen die Regionen dieser Farbe. Vorher
  // listete die Legende jede Region einzeln — bei 16 Bundesländern sechs
  // Zeilen, die nur die Namen der Karte wiederholten.
  const items = content.mapColorMode === 'manual'
    ? kartenKategorien(content).map((k) => ({ c: k.farbe, label: k.name }))
    : data
      .map((d, i) => ({ c: colourOf(d, i), label: d.label || '' }))
      .filter((it) => it.label)
  if (!items.length) return { svg: '', height: 0 }
  const sw = fs * 1.1
  const gap = fs * 1.4
  const rowH = fs * 1.8
  const itemW = (label: string) => sw + 8 + label.length * fs * 0.55 + gap
  const rows: { c: string; label: string }[][] = []
  let cur: { c: string; label: string }[] = []
  let curW = 0
  for (const it of items) {
    const wdt = itemW(it.label)
    if (curW + wdt > availW && cur.length) {
      rows.push(cur)
      cur = []
      curW = 0
    }
    cur.push(it)
    curW += wdt
  }
  if (cur.length) rows.push(cur)
  let svg = ''
  rows.forEach((row, ri) => {
    const totalW = row.reduce((s, it) => s + itemW(it.label), 0) - gap
    let x = (availW - totalW) / 2
    const y = ri * rowH
    row.forEach((it) => {
      svg +=
        `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${sw.toFixed(
          1
        )}" height="${sw.toFixed(1)}" rx="${(sw * 0.25).toFixed(
          1
        )}" fill="${it.c}"/>` +
        `<text x="${(x + sw + 8).toFixed(1)}" y="${(y + sw * 0.85).toFixed(
          1
        )}" fill="${labelFill}" font-size="${fs}" font-family="${ff}">${esc(
          it.label
        )}</text>`
      x += itemW(it.label)
    })
  })
  return { svg, height: rows.length * rowH }
}

// Füllfarbe je Land (normCountry-Name → Farbe), genau wie mapSvg sie malt.
// Eigene Funktion, damit der Scene-Editor dieselbe Farbe als Punkt neben
// jede Tabellenzeile setzen kann (Jan 19.10.2026). Kein Eintrag = „No data“.
export function kartenFarbenJeLand(content: InfographicContent): Record<string, string> {
  const { data } = content
  const palette = content.palette ?? []
  // country name → fill colour. 'value' mode (default) bins each country's value
  // on a sequential ramp (OWID-style); 'manual' mode uses each row's own colour.
  const colorByName: Record<string, string> = {}
  // 'highlight' mode (Jan, 13.10.2026): a two-colour map — marked regions get
  // the highlight colour, the rest the muted one; their names are drawn ON the
  // active shapes (below) and the categorical legend is suppressed entirely.
  const istHervorheben = content.mapColorMode === 'highlight'
  const hervorFarbe = content.mapHighlightColor || '#B26C15'
  const ruheFarbe = content.mapMutedColor || '#BBB2A0'
  if (istHervorheben) {
    data.forEach((d) => {
      if (d.label) colorByName[normCountry(d.label)] = d.marked ? hervorFarbe : ruheFarbe
    })
  } else if (content.mapColorMode === 'manual') {
    data.forEach((d, i) => {
      const c = d.color || palette[i % (palette.length || 1)]
      if (c && d.label) colorByName[normCountry(d.label)] = c
    })
  } else {
    const values = data
      .map((d) => Number(d.value))
      // 0 (and empty, stored as 0 by the table) is treated as "no data"
      .filter((v) => Number.isFinite(v) && v !== 0)
    const thresholds =
      content.mapThresholds && content.mapThresholds.length
        ? [...content.mapThresholds]
            .filter((t) => Number.isFinite(t))
            .sort((a, b) => a - b)
        : niceThresholds(values)
    const nBins = thresholds.length + 1
    const stops = rampStops(content)
    data.forEach((d) => {
      const v = Number(d.value)
      // 0 / empty → leave uncoloured so the region renders as no-data (grey)
      if (d.label && Number.isFinite(v) && v !== 0) {
        colorByName[normCountry(d.label)] = rampBinColor(
          stops,
          valueBin(v, thresholds),
          nBins
        )
      }
    })
  }
  return colorByName
}

function mapSvg(content: InfographicContent, _progress: number): string {
  const { width: w, height: h, data, textColor } = content
  const ff = fontStack(content)
  // 50m was dropped — its ~1 MB / 93k-point SVG was slow for the browser to
  // rasterise. The map always uses the light 110m geometry now. The reveal
  // (left→right wipe) is applied as a CSS clip-path on the element per frame,
  // so the SVG itself is built once (progress-independent) — see RendererWebGL.
  // scope: world / Europe / single country — pick the geometry + projection
  const scope = content.chartType
  let baseFeatures: Array<{ name: string; polygons: number[][][][] }>
  let project: (lon: number, lat: number) => [number, number]
  if (scope === 'mapCountry') {
    const opt = countryOption(content.mapCountry)
    if (opt.a3 === 'USA') {
      const all = getAdmin1Features('USA')
      if (content.mapUsaOutlying) {
        // all 50 states + DC, with Alaska & Hawaii as insets (albersUsa). Trim
        // Hawaii to the main islands (drop the NW atoll chain) so the inset is tight.
        const HI_BOX = { lonMin: -161, lonMax: -152, latMin: 17, latMax: 24 }
        baseFeatures = all
          .map((f) =>
            f.name === 'Hawaii'
              ? {
                  name: f.name,
                  polygons: f.polygons
                    .map((poly) =>
                      poly
                        .map((ring) => clipRingToBox(ring, HI_BOX))
                        .filter((r) => r.length >= 3)
                    )
                    .filter((poly) => poly.length > 0),
                }
              : f
          )
          .filter((f) => f.polygons.length > 0)
        project = makeProjector({ kind: 'albersUsa' })
      } else {
        // default: contiguous US only (Albers CONUS)
        baseFeatures = all.filter(
          (f) => f.name !== 'Alaska' && f.name !== 'Hawaii'
        )
        project = makeProjector(opt.proj)
      }
    } else if (opt.a3 === 'ESP') {
      const all = getAdmin1Features('ESP')
      const isCanary = (n: string) =>
        n === 'Santa Cruz de Tenerife' || n === 'Las Palmas'
      if (content.mapEspCanaries) {
        // mainland + Balearics + Ceuta/Melilla + Canaries (placed as an inset)
        baseFeatures = all
        project = makeProjector({ kind: 'espCanaries' })
      } else {
        // default: mainland + Balearics only (drop the Canary provinces)
        baseFeatures = all.filter((f) => !isCanary(f.name))
        project = makeProjector(opt.proj)
      }
    } else {
      baseFeatures = getAdmin1Features(opt.a3)
      project = makeProjector(opt.proj)
    }
  } else if (scope === 'mapEurope') {
    // explicit European country set, optionally + transcontinental neighbours
    const europeSet = new Set(EUROPE_COUNTRIES)
    if (content.mapEuropeShowTurkey) europeSet.add('turkey')
    if (content.mapEuropeShowRussia) europeSet.add('russia')
    // select the countries, then CLIP every ring to the Europe frame — trims
    // overseas parts and turns Russia into its European strip
    baseFeatures = getWorldFeatures('110m')
      .filter((f) => europeSet.has(normCountry(f.name)))
      .map((f) => ({
        name: f.name,
        polygons: f.polygons
          .map((poly) =>
            poly.flatMap((ring) =>
              // split at the antimeridian FIRST — Russia's ring wraps ±180°, and
              // clipping that huge seam-spanning edge would streak across the box
              splitRingAtSeam(ring)
                .map((piece) => clipRingToBox(piece, EUROPE_BBOX))
                .filter((r) => r.length >= 3)
            )
          )
          .filter((poly) => poly.length > 0),
      }))
      .filter((f) => f.polygons.length > 0)
    // default = Lambert conformal conic (the EU's ETRS89-LCC), Mercator optional
    project =
      content.mapProjectionEurope === 'mercator'
        ? makeProjector({ kind: 'mercator' })
        : makeProjector({ kind: 'lcc', lat1: 35, lat2: 65, lat0: 52, lon0: 10 })
  } else {
    baseFeatures = getWorldFeatures('110m')
    project = makeProjector({ kind: content.mapProjection || 'equalEarth' })
  }
  if (!baseFeatures || !baseFeatures.length) {
    // geometry still loading — show a placeholder (rebuilds once it arrives)
    return wrap(
      content,
      `<text x="${w / 2}" y="${h / 2}" fill="${textColor}" fill-opacity="0.5" ` +
        `font-size="${Math.max(24, w * 0.018)}" font-family="${ff}" ` +
        `text-anchor="middle" dominant-baseline="central">Loading map…</text>`
    )
  }
  const noData = content.mapNoDataColor || '#cfd2d8'
  // Farbe je Land kommt aus kartenFarbenJeLand (auch der Scene-Editor
  // nutzt sie für die Farbpunkte neben jeder Tabellenzeile)
  const colorByName = kartenFarbenJeLand(content)
  const istHervorheben = content.mapColorMode === 'highlight'

  // Antarctica is hidden by default on the WORLD map — it stretches the bottom
  // of every projection and is rarely part of the story. Europe/Country scopes
  // are already restricted, so nothing extra to drop there.
  const drawFeatures =
    scope === 'map' && !content.mapShowAntarctica
      ? baseFeatures.filter((f) => normCountry(f.name) !== 'antarctica')
      : baseFeatures

  // project every ring once; track global + per-feature x bounds
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  const projected = drawFeatures.map((f) => {
    // each ring is split at the antimeridian into seam-bounded pieces, then
    // projected — so countries crossing ±180° (Russia, Fiji, …) close along the
    // edge instead of streaking/wedging across the map.
    const subpaths: Array<Array<[number, number]>> = []
    f.polygons.forEach((poly) =>
      poly.forEach((ring) => {
        splitRingAtSeam(ring).forEach((piece) => {
          if (piece.length < 2) return
          const sp = piece.map(([lon, lat]) => {
            const xy = project(lon, lat)
            if (xy[0] < minX) minX = xy[0]
            if (xy[0] > maxX) maxX = xy[0]
            if (xy[1] < minY) minY = xy[1]
            if (xy[1] > maxY) maxY = xy[1]
            return xy
          })
          subpaths.push(sp)
        })
      })
    )
    return { subpaths }
  })

  const padX = 24
  const titleSpace = kopf(content).hoehe ? kopf(content).hoehe + 50 : 24
  // "No data" only matters if at least one drawn region has no matching value
  const hasNoData = drawFeatures.some(
    (f) => !colorByName[normCountry(f.name)]
  )
  // build the legend first so we can reserve its height under the headline
  // (highlight mode has no legend — the region names sit on the map itself)
  // DIE KARTE HAT VORRANG (Jan 17.10.2026): Die Legende darf höchstens ein
  // Drittel der Höhe nehmen — sonst wird ihre Schrift schrittweise kleiner
  // (vorher schrumpfte die Karte auf ein Symbol, wenn die Legende groß war).
  let legendFs = content.mapLegendFontSize ?? Math.max(14, content.width * 0.013)
  const legendeMaxH = Math.max(40, (h - titleSpace - (content.source ? 56 : 24)) / 3)
  let legend = content.mapLegend && !istHervorheben
    ? mapLegendBlock(content, w - 2 * padX, ff, hasNoData, legendFs)
    : { svg: '', height: 0 }
  while (legend.height > legendeMaxH && legendFs > 10) {
    legendFs = Math.max(10, legendFs * 0.85)
    legend = mapLegendBlock(content, w - 2 * padX, ff, hasNoData, legendFs)
  }
  // gap between the legend and the map — scales with the legend font size so
  // larger legends keep proportional breathing room (was a tight fixed 18)
  const legendGap = legend.height ? Math.round(legendFs * 1.6) : 0
  const padTop = titleSpace + legend.height + legendGap
  const padBottom = content.source ? 56 : 24
  const spanX = maxX - minX || 1
  const spanY = maxY - minY || 1
  const scale = Math.min(
    (w - 2 * padX) / spanX,
    (h - padTop - padBottom) / spanY
  )
  const offX = padX + (w - 2 * padX - spanX * scale) / 2 - minX * scale
  const offY = padTop + (h - padTop - padBottom - spanY * scale) / 2 - minY * scale
  // border thickness in content px (the SVG is fit into the element afterwards)
  const strokeW = Math.max(0, content.mapStrokeWidth ?? 1.2)
  const strokeCol = content.mapStrokeColor || '#ffffff'
  // keep the original subtle look by default; full opacity once the user picks
  const strokeOp = content.mapStrokeColor ? 1 : 0.5

  // reveal order: rank regions left→right by their projected centre-x, tagged as
  // data-ri on each path so the HTML renderer can fade them in one after another
  // (a per-region build-up rather than a single wipe).
  const centreX = drawFeatures.map((_f, i) => {
    let mn = Infinity
    let mx = -Infinity
    projected[i].subpaths.forEach((sp) =>
      sp.forEach(([x]) => {
        if (x < mn) mn = x
        if (x > mx) mx = x
      })
    )
    return (mn + mx) / 2
  })
  const rankOf: number[] = []
  drawFeatures
    .map((_f, i) => i)
    .sort((a, b) => centreX[a] - centreX[b])
    .forEach((featIdx, rank) => {
      rankOf[featIdx] = rank
    })

  let body = ''
  drawFeatures.forEach((f, i) => {
    const fill = colorByName[normCountry(f.name)] || noData
    let d = ''
    projected[i].subpaths.forEach((sp) => {
      sp.forEach(([x, y], k) => {
        // integer coords: the map is fit to a ~900–1700px box, so sub-pixel
        // precision is invisible but ~20% of the SVG string / raster cost
        const X = Math.round(offX + x * scale)
        const Y = Math.round(offY + y * scale)
        d += `${k === 0 ? 'M' : 'L'}${X} ${Y}`
      })
      d += 'Z'
    })
    body +=
      `<path d="${d}" fill="${fill}" stroke="${strokeCol}" ` +
      `stroke-opacity="${strokeOp}" stroke-width="${strokeW.toFixed(2)}" ` +
      `stroke-linejoin="round" data-ri="${rankOf[i]}"/>`
  })
  // ── NAMEN OHNE ÜBERLAPPUNG (Jan 22.10.2026: „Die Schriften von Berlin und
  // Brandenburg liegen übereinander … dass sich Schriften nicht überlagern,
  // sondern automatisch einen kleinen Abstand von 5px haben … oder wenn es
  // zu eng wird, die Namen lieber verschwinden“) — für jede Karte. Erst die
  // Orte (Punkt fest, Name rechts, sonst links, sonst ohne Namen), dann die
  // Flächennamen von der KLEINSTEN Fläche aufwärts: kleine Regionen (Berlin,
  // Bremen) behalten ihre Mitte, größere weichen innerhalb ihrer Fläche aus;
  // ohne freien Platz entfällt der Name.
  type Kasten = { x0: number; y0: number; x1: number; y1: number }
  const belegt: Kasten[] = []
  const luft = (fs: number) => Math.max(5, fs * 0.25)
  // `ausser`: der eigene Punkt eines Ortes — sein Name darf daran stehen
  const frei = (k: Kasten, abstand: number, ausser?: Kasten) =>
    belegt.every(
      (b) =>
        b === ausser ||
        k.x1 + abstand <= b.x0 ||
        b.x1 + abstand <= k.x0 ||
        k.y1 + abstand <= b.y0 ||
        b.y1 + abstand <= k.y0
    )
  const textKasten = (
    x: number,
    y: number,
    breite: number,
    fs: number,
    anker: 'start' | 'middle' | 'end'
  ): Kasten => {
    const x0 = anker === 'start' ? x : anker === 'end' ? x - breite : x - breite / 2
    return { x0, y0: y - fs * 0.55, x1: x0 + breite, y1: y + fs * 0.55 }
  }
  const namenGewicht = staerke(content, 600)

  // ORTE als Punkte (Jan 22.10.2026, „Deutschlandkarte mit 5 oder 10 Orten,
  // die man im Datenfeld eingeben kann“): dieselbe Projektion wie die
  // Flächen. Mit Werten wachsen die Punkte (Fläche ∝ Wert). data-ort =
  // Reihenfolge; der Renderer blendet sie nach den Flächen nacheinander ein.
  const orte = (content.places ?? []).filter(
    (o) => !!o && Number.isFinite(o.lon) && Number.isFinite(o.lat)
  )
  const ortFs = content.placeLabelSize ?? fonts(content).label
  const ortZeichen: Array<{
    k: number
    x: number
    y: number
    r: number
    name?: { x: number; y: number; anker: 'start' | 'middle' | 'end' }
    punkt: Kasten
  }> = []
  if (orte.length) {
    const werte = orte.map((o) => (typeof o.value === 'number' && o.value > 0 ? o.value : 0))
    const maxWert = Math.max(0, ...werte)
    // ein Viertel kleiner als zuerst (Jan 22.10.2026)
    const rBasis = content.placeSize ?? Math.max(4.5, w * 0.00675)
    orte.forEach((o, k) => {
      const [px, py] = project(o.lon, o.lat)
      const x = offX + px * scale
      const y = offY + py * scale
      if (!Number.isFinite(x) || !Number.isFinite(y)) return
      const r =
        maxWert > 0 && werte[k] > 0 ? rBasis * (0.6 + 1.9 * Math.sqrt(werte[k] / maxWert)) : rBasis
      // die Punkte selbst sind feste Hindernisse
      const punkt = { x0: x - r, y0: y - r, x1: x + r, y1: y + r }
      ortZeichen.push({ k, x, y, r, punkt })
      belegt.push(punkt)
    })
    if (content.placeLabels !== false) {
      ortZeichen.forEach((z) => {
        const name = orte[z.k].name
        if (!name) return
        const breite = namenBreite(name, ortFs, ff, namenGewicht)
        // Name rechts vom Punkt (am rechten Rand zuerst links), sonst die
        // andere Seite, darunter, darüber, schräg (Jan 22.10.2026: „Siek wird
        // nicht angezeigt, obwohl dort Platz wäre“ — zwischen Hamburg und
        // Oldesloe waren beide Seiten durch Punkte belegt)
        const seitlich = z.r + ortFs * 0.35
        const senkrecht = z.r + ortFs * 0.75
        const rechts = { x: z.x + seitlich, y: z.y, anker: 'start' as const }
        const links = { x: z.x - seitlich, y: z.y, anker: 'end' as const }
        const stellen: Array<{ x: number; y: number; anker: 'start' | 'middle' | 'end' }> = [
          ...(z.x < w * 0.8 ? [rechts, links] : [links, rechts]),
          { x: z.x, y: z.y + senkrecht, anker: 'middle' },
          { x: z.x, y: z.y - senkrecht, anker: 'middle' },
          { x: z.x + z.r * 0.7, y: z.y - senkrecht, anker: 'start' },
          { x: z.x + z.r * 0.7, y: z.y + senkrecht, anker: 'start' },
          { x: z.x - z.r * 0.7, y: z.y - senkrecht, anker: 'end' },
          { x: z.x - z.r * 0.7, y: z.y + senkrecht, anker: 'end' },
        ]
        for (const s of stellen) {
          const kasten = textKasten(s.x, s.y, breite, ortFs, s.anker)
          if (frei(kasten, luft(ortFs), z.punkt)) {
            belegt.push(kasten)
            z.name = s
            break
          }
        }
      })
    }
  }

  // highlight mode: region names on the ACTIVE shapes (bbox centre), in the
  // label size, with a soft dark halo for readability on any fill. Same
  // data-ri as the shape so the per-region reveal fades name and shape as one.
  // NAMEN AUCH OHNE HERVORHEBEN (Jan 22.10.2026: „bei den Daten vor dem
  // Ländernamen markieren, welches Land in der Karte dargestellt ist“):
  // Zeilen mit showName — ohne Halo wie die Ortsnamen, mittig auf dem
  // größten Teilstück (Frankreich nicht zwischen Festland und Korsika).
  const namenZeilen = istHervorheben
    ? content.mapRegionLabels !== false
      ? data.filter((d) => d.marked)
      : []
    : data.filter((d) => d.showName)
  if (namenZeilen.length) {
    const labelVon: Record<string, string> = {}
    namenZeilen.forEach((d) => {
      if (d.label) labelVon[normCountry(d.label)] = d.label
    })
    const nameFs = content.mapRegionLabelSize ?? fonts(content).label
    const nameFill = content.labelColor || content.textColor || '#ffffff'
    const rahmen = (teile: Array<Array<[number, number]>>) => {
      let mn = Infinity
      let mx = -Infinity
      let mnY = Infinity
      let mxY = -Infinity
      teile.forEach((sp) =>
        sp.forEach(([x, y]) => {
          if (x < mn) mn = x
          if (x > mx) mx = x
          if (y < mnY) mnY = y
          if (y > mxY) mxY = y
        })
      )
      return { mn, mx, mnY, mxY, flaeche: (mx - mn) * (mxY - mnY) }
    }
    const kandidaten: Array<{ i: number; name: string; b: ReturnType<typeof rahmen> }> = []
    drawFeatures.forEach((f, i) => {
      const name = labelVon[normCountry(f.name)]
      if (!name) return
      const teile = projected[i].subpaths as Array<Array<[number, number]>>
      if (!teile.length) return
      const b = istHervorheben
        ? rahmen(teile)
        : teile
            .map((sp) => rahmen([sp]))
            .reduce((a, c) => (c.flaeche > a.flaeche ? c : a))
      kandidaten.push({ i, name, b })
    })
    // kleinste Fläche zuerst — sie hat keinen Platz zum Ausweichen
    kandidaten.sort((a, c) => a.b.flaeche - c.b.flaeche)
    const halo = istHervorheben
      ? `paint-order="stroke" stroke="#000" stroke-opacity="0.35" stroke-width="${(nameFs * 0.18).toFixed(1)}" `
      : ''
    // Ausweichstellen: Mitte, dann zeilenweise darüber/darunter, seitlich, schräg
    const schritte: Array<[number, number]> = [[0, 0]]
    for (let n = 1; n <= 4; n++)
      for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0], [-1, -1], [1, -1], [-1, 1], [1, 1]])
        schritte.push([dx * n, dy * n])
    kandidaten.forEach(({ i, name, b }) => {
      const mx0 = offX + b.mn * scale
      const mx1 = offX + b.mx * scale
      const my0 = offY + b.mnY * scale
      const my1 = offY + b.mxY * scale
      const cx0 = (mx0 + mx1) / 2
      const cy0 = (my0 + my1) / 2
      const breite = namenBreite(name, nameFs, ff, namenGewicht)
      const sx = Math.max(nameFs, breite * 0.35)
      const sy = nameFs * 0.75
      for (const [dx, dy] of schritte) {
        const cx = cx0 + dx * sx
        const cy = cy0 + dy * sy
        // nur innerhalb der eigenen Fläche ausweichen
        if (cx < mx0 || cx > mx1 || cy < my0 || cy > my1) continue
        const kasten = textKasten(cx, cy, breite, nameFs, 'middle')
        if (!frei(kasten, luft(nameFs))) continue
        belegt.push(kasten)
        body +=
          `<text x="${cx.toFixed(1)}" y="${cy.toFixed(1)}" fill="${nameFill}" ` +
          `font-size="${nameFs}" font-weight="${namenGewicht}" font-family="${ff}" ` +
          `text-anchor="middle" dominant-baseline="central" ${halo}` +
          `data-ri="${rankOf[i]}">${esc(name)}</text>`
        break
      }
    })
  }

  if (ortZeichen.length) {
    const punktFarbe = content.placeColor || content.accentColor || '#F02873'
    const ortFill = content.labelColor || content.textColor || '#ffffff'
    ortZeichen.forEach((z) => {
      body +=
        `<g data-ort="${z.k}">` +
        `<circle cx="${z.x.toFixed(1)}" cy="${z.y.toFixed(1)}" r="${z.r.toFixed(1)}" fill="${punktFarbe}" ` +
        `stroke="#ffffff" stroke-opacity="0.9" stroke-width="${Math.max(1, z.r * 0.25).toFixed(1)}"/>` +
        (z.name
          ? `<text x="${z.name.x.toFixed(1)}" y="${z.name.y.toFixed(1)}" fill="${ortFill}" font-size="${ortFs}" ` +
            `font-weight="${namenGewicht}" font-family="${ff}" text-anchor="${z.name.anker}" ` +
            `dominant-baseline="central">${esc(orte[z.k].name)}</text>`
          : '') +
        `</g>`
    })
  }
  if (legend.svg) {
    // data-legende: der Renderer blendet sie beim Aufbau ein (Jan 19.10.2026,
    // „die Legende baut nicht mit auf“) — die Karte selbst ist statisch
    body += `<g data-legende="1" transform="translate(${padX}, ${titleSpace.toFixed(
      1
    )})">${legend.svg}</g>`
  }
  return wrap(content, body, { links: offX + minX * scale, rechts: offX + maxX * scale })
}

function kpiSvg(content: InfographicContent, progress: number): string {
  const { width: w, height: h, value, unit, accentColor } = content
  const { title: titleFs, value: valueFs, label: labelFs } = fonts(content)
  // EINHEIT NEBEN DER ZAHL (Jan 15.10.2026, wählbar; neue Karten stehen
  // darauf): halbe Zahlengröße auf der Grundlinie der Zahl, wie getippt.
  // Fehlt unitPosition, bleibt die Einheit die Unterzeile in Versalien.
  const neben = content.unitPosition === 'beside' && String(unit ?? '').trim() !== ''
  const ff = fontStack(content)
  // title right above and source right under the number (Jan, 12.10.2026) —
  // the generic card-edge title/source in wrap() are suppressed for kpi: with
  // the kpi card's large padding both sat far away from the number. Styling
  // matches wrap().
  // Abstand nach Jans Feinschliff: eine gute Titel-Hoehe Luft zur Zahl
  const titleY = h / 2 - 10 - valueFs / 2 - titleFs * 1.3
  const titleEl = content.title
    ? `<text x="${w / 2}" y="${titleY.toFixed(1)}" fill="${content.titleColor || content.textColor}" ` +
      `font-size="${titleFs}" font-weight="${staerke(content, 700)}" font-family="${ff}" ` +
      `text-anchor="middle" data-titel="1">${esc(content.title)}</text>`
    : ''
  const unitY = h / 2 + valueFs / 2 + labelFs + 6
  // ÜBERZEILE über Titel bzw. Zahl, UNTERZEILE unter der Einheit (Jan
  // 19.10.2026, „Since launch / 120 / countries / where our customers call
  // home“) — ohne beide bleibt der KPI zeichengenau wie bisher.
  const kicker = (content.kicker ?? '').trim()
  const sub = (content.subtitle ?? '').trim()
  const ks = content.kickerFontSize ?? Math.round(titleFs * 0.42)
  const ss = content.subtitleFontSize ?? Math.round(titleFs * 0.5)
  const kickerY = content.title
    ? titleY - titleFs - ks * 0.7
    : h / 2 - 10 - valueFs / 2 - ks * 1.3
  const kickerEl = kicker
    ? `<text x="${w / 2}" y="${kickerY.toFixed(1)}" fill="${content.kickerColor || content.titleColor || content.textColor}" ` +
      `font-size="${ks}" font-weight="${staerke(content, 600)}" letter-spacing="${(ks * 0.12).toFixed(1)}" ` +
      `font-family="${ff}" text-anchor="middle" data-titel="1">${esc(kicker.toUpperCase())}</text>`
    : ''
  const subY = unitY + labelFs * 0.4 + ss * 1.5
  const subEl = sub
    ? `<text x="${w / 2}" y="${subY.toFixed(1)}" fill="${content.subtitleColor || content.labelColor || content.textColor}" ` +
      (content.subtitleColor ? '' : `fill-opacity="0.75" `) +
      `font-size="${ss}" font-family="${ff}" text-anchor="middle" data-titel="1">${esc(sub)}</text>`
    : ''
  const sourceFs = content.sourceFontSize ?? QUELLE_SCHRIFT_STANDARD
  const sourceY = sub ? subY + ss * 0.3 + sourceFs * 1.4 : unitY + labelFs * 0.4 + sourceFs * 1.4
  const sourceEl = content.source
    ? `<text x="${w / 2}" y="${sourceY.toFixed(1)}" ` +
      `fill="${content.sourceColor || content.labelColor || content.textColor}" ` +
      `fill-opacity="${content.sourceColor ? 1 : content.labelColor ? 0.85 : 0.6}" ` +
      `font-size="${sourceFs}" font-family="${ff}" text-anchor="middle" data-quelle="1">${esc(content.source)}</text>`
    : ''
  // Einheit neben der Zahl blendet mit ein (tspan: fill-opacity)
  const einheitO = textAufbauDeckkraft(progress, 0.2)
  const einheitDeck = einheitO >= 0.999 ? '' : ` fill-opacity="${einheitO.toFixed(2)}"`
  return wrap(
    content,
    kickerEl +
    titleEl +
    (neben
      ? `<text x="${w / 2}" y="${(h / 2 - 10 + valueFs * 0.36).toFixed(1)}" fill="${accentColor}" ` +
        `font-size="${valueFs}" font-weight="${staerke(content, 700)}" text-anchor="middle" ` +
        `font-family="${ff}">${esc(scaleKpiValue(value ?? '', progress))}` +
        `<tspan font-size="${(valueFs * 0.5).toFixed(1)}" dx="${(valueFs * 0.08).toFixed(1)}"${einheitDeck}>` +
        `${esc(String(unit))}</tspan></text>`
      : `<text x="${w / 2}" y="${h / 2 - 10}" fill="${accentColor}" font-size="${valueFs}" ` +
        `font-weight="${staerke(content, 700)}" text-anchor="middle" dominant-baseline="middle" ` +
        `font-family="${ff}">${esc(scaleKpiValue(value ?? '', progress))}</text>` +
        `<text${deckAttr(textAufbauDeckkraft(progress, 0.2))} x="${w / 2}" y="${unitY}" ${labelPaint(content, 0.7)} ` +
        `font-size="${labelFs}" font-weight="${staerke(content, 600)}" text-anchor="middle" letter-spacing="2" ` +
        `font-family="${ff}">${esc(String(unit ?? '').toUpperCase())}</text>`) +
      subEl +
      sourceEl
  )
}

export function buildInfographicSvg(
  content: InfographicContent,
  progress = 1
): string {
  const p = Math.min(Math.max(progress, 0), 1)
  let svg: string
  switch (content.chartType) {
    case 'line':
      svg = lineSvg(content, p)
      break
    case 'pie':
      svg = pieSvg(content, p)
      break
    case 'kpi':
      svg = kpiSvg(content, p)
      break
    case 'timeline':
      svg = timelineSvg(content, p)
      break
    case 'circles':
      svg = circlesSvg(content, p)
      break
    case 'wordcloud':
      svg = wordcloudSvg(content, p)
      break
    case 'table':
      svg = tableSvg(content, p)
      break
    case 'steps':
      svg = stepsSvg(content, p)
      break
    case 'map':
    case 'mapEurope':
    case 'mapCountry':
      svg = mapSvg(content, p)
      break
    case 'bar':
    default:
      svg = barSvg(content, p)
      break
  }
  // Titel blendet am Anfang, Quelle am Ende des Aufbaus ein (Jan 18.10.2026:
  // beide standen sofort da). Karten sind vom Fortschritt unabhängig gebaut —
  // dort setzt der Renderer die Deckkraft pro Bild (wie bei den Regionen).
  if (p < 1 && !isMapChart(content.chartType)) {
    svg = svg
      .replace(/ data-titel="1"/g, ` data-titel="1" opacity="${titelDeckkraft(p).toFixed(2)}"`)
      .replace(/ data-quelle="1"/g, ` data-quelle="1" opacity="${quelleDeckkraft(p).toFixed(2)}"`)
  }
  return svg
}
